import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  ApiError, BUILDABLE_FIELD_TYPES, CHOICE_COLORS, FILTER_OPS, FORMATS, PERMISSIONS, ROW_HEIGHTS,
  SCOPE, SLUG_RE, api, attachmentCells, canHaveDefault, choiceColor, linkCells, parseRowLink, rowHeightPx, rowLink,
  ownerSettingProblem, SCOPE_LABELS, settingValue, slugify, typeIcon, typeLabel, viewVisibility,
  type Automation, type Base, type BrainItem, type Delivery,
  type Field, type FieldOption, type FilterRule, type GrantLevel,
  type AttachmentCell, type GrantRow, type HistoryRow, type IdentityRow, type UserRow, type LinkCell, type Me,
  type ClaudeAccess, type ClaudeProposalRow, type ClaudeSwitchRow,
  type PlanConfigRow, type RecordCount, type RecordPage, type RowHeight,
  type IdentityScopes, type SavedView, type ScopeAllowance, type SettingKey, type SettingsPayload,
  type SortRule, type TableMeta,
  type TableSummary, type TrashedField, type TriggerKind, type Workspace,
  uploadOneFile,
  keptLinkIds, sharedLinkable,
  type LinkOption,
} from './api.js';
import { usePointerReorder, reorder } from './drag.js';
import {
  BROWSER_TZ, HOUSE_ZONE, formatDateOnly, formatInstant, isoToZoned, zoneOf, zonedToIso,
  type DateFormat, type DisplayPrefs, type TimeFormat,
} from './tz.js';
import {
  EMPTY_DOC, RichTextEditor, RichTextView, isRichTextDoc,
  type EditorAttachment, type RTDoc,
} from './richtext.js';

/**
 * The width below which the sidebar becomes an overlay — C2-17(2).
 *
 * THE SAME NUMBER IS IN styles.css, and it has to be: the layout is a media
 * query and the dismiss-on-selection is JavaScript. Exported so the guard in
 * server/test/guards.test.ts can read both and fail if they drift.
 */
export const NARROW_SCREEN = '(max-width: 900px)';

/**
 * The grid.
 *
 * WHAT THE UI DOES ABOUT PERMISSIONS, stated plainly because it is easy to
 * mistake for a control: it hides affordances a person's scopes cannot use, and
 * that is a COURTESY. Every one of those actions is refused by the server and
 * again by the database whether or not the button is on screen — the API returns
 * 403 with the scope it needed, and RLS returns zero rows. Nothing here is load
 * bearing. If this file were replaced wholesale by a hostile one, the answer
 * would be the same.
 *
 * WHAT IT DOES ABOUT REFUSALS is the part that matters: an `insufficient_scope`
 * answer is rendered as itself, naming the scope, rather than as "something went
 * wrong". A precise refusal a person can read is the difference between a
 * boundary and a bug report.
 */

/**
 * HOW DATES AND TIMES ARE SHOWN, FOR THIS PERSON — C2-13, C2-16.
 *
 * This was `const TZ = 'America/New_York'`, a module constant, and REQ-1 is
 * exactly the complaint that a preference in source is a preference only the
 * developer has. It is now three of the eight settings in console.setting_keys.
 *
 * IT IS STILL A MODULE-LEVEL VALUE, deliberately, and that needs saying because
 * it looks like the thing this project warns about. `fmtTime()` is called from
 * five components that have no reason to know about settings — the grid's
 * built-in columns, the row history, the field trash, the brain list. Threading
 * a prefs object through all of them would put a parameter on functions whose
 * job has nothing to do with preferences, to carry a value that changes once per
 * session.
 *
 * THE RULE THAT MAKES IT SAFE: this is only ever written by `setDisplayPrefs`,
 * which is called from the SAME effect that puts the settings into React state.
 * The write happens first and the state change follows, so no render can paint
 * from a value the tree has not been told about — the failure this project has
 * already met once, one layer down, in the drag commit (build-craft §16). A
 * write here without an accompanying state change would be a stale screen, which
 * is why there is one caller and it is named.
 */
let displayPrefs: DisplayPrefs = { ...BROWSER_TZ };

function setDisplayPrefs(p: SettingsPayload | null): void {
  displayPrefs = {
    tz: settingValue<string>(p, 'timezone_override', ''),
    dateFormat: settingValue<DateFormat>(p, 'date_format', 'iso'),
    timeFormat: settingValue<TimeFormat>(p, 'time_format', '24h'),
  };
}

/**
 * The settings, for the components that need the VALUES rather than the
 * formatting — row height, page size, drag sensitivity, the two confirmations.
 * A context rather than the module value above, because those do have to repaint
 * when they change.
 */
const SettingsContext = createContext<SettingsPayload | null>(null);
function useSettings(): SettingsPayload | null {
  return useContext(SettingsContext);
}

function fmt(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * "a" or "an".
 *
 * FOUND BY THE FIRST PERSON WHO DID NOT WRITE THE SCREEN. The banner read "You
 * are a admin" from C1 until a staff Admin signed in on 2026-08-28 and read it —
 * which is what a second account is for, and is why it is recorded here rather
 * than quietly fixed. It is one word and it is the only user-facing English on
 * this screen that varies with data.
 *
 * Vowel-initial is the whole rule, which is right for the three roles that exist
 * (owner admin, admin, developer) and for every role name anyone is likely to
 * add. It is not right for "an hour" or "a university"; if a role is ever named
 * one of those, this comment is the warning.
 */
function article(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/**
 * /ws/{workspace}/{base}/{table} — a row link without the row.
 *
 * The grid writes this into the address bar whenever a table is selected, so
 * that the URL a person copies out of the bar is a link that comes back to where
 * they were. parseRowLink() handles the longer form with the row on the end.
 */
function matchTablePath(pathname: string): { workspace: string; base: string; table: string } | null {
  const m = /^\/ws\/([^/]+)\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  return {
    workspace: decodeURIComponent(m[1]!),
    base: decodeURIComponent(m[2]!),
    table: decodeURIComponent(m[3]!),
  };
}

/**
 * Stored UTC, displayed in the zone and formats this person chose — spec v2
 * V2-7, widened by C2-13. The arithmetic lives in tz.ts, with the DST cases.
 */
function fmtTime(iso: unknown): string {
  return formatInstant(iso, displayPrefs);
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [booted, setBooted] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.refusal.kind === 'auth') setMe(null);
        else setFatal(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setBooted(true));
  }, []);

  if (!booted) return null;
  if (fatal) return <div className="signin"><h1>Assett AI Console</h1><p>{fatal}</p></div>;
  if (!me) return <SignIn />;
  return <Console me={me} />;
}

function SignIn() {
  return (
    <div className="signin">
      <h1>Assett AI Console</h1>
      <p className="muted">
        The internal team's grid. Sign in with your Assett Google account — the address
        registered against your identity is what lets you in, and there is no password.
      </p>
      <a className="go" href="/auth/start">Sign in with Google</a>
    </div>
  );
}

/** What the create dialog is currently making, if anything. */
type NewThing =
  | { kind: 'workspace' }
  | { kind: 'base'; parentId: string; parentName: string }
  | { kind: 'table'; parentId: string; parentName: string };

/** What the rename dialog is currently editing, if anything. */
interface Editing {
  kind: 'workspaces' | 'bases' | 'tables' | 'fields';
  id: string;
  name: string;
  description: string;
}

function Console({ me }: { me: Me }) {
  // Sidebar open/shut, remembered across reloads. Reading localStorage can
  // THROW rather than return null (private windows, blocked site data), so the
  // read is wrapped and the default is open.
  /**
   * NARROW SCREENS OVERLAY THE SIDEBAR — C2-17(2), and Matt ruled it on
   * 2026-08-29 (Amendment 1, A1-2): "OVERLAY. Below a breakpoint the sidebar
   * floats OVER the grid and dismisses on selection." Collapse-to-icons was
   * offered and declined.
   *
   * P7's complaint was that an expanded sidebar "crushes the grid to a sliver".
   * On a 606px screen the grid column was left about 340px wide, which is one
   * column and a half of another — the sidebar was not covering the grid, it was
   * SQUEEZING it, and the grid had nowhere to go.
   *
   * THE BREAKPOINT IS 900px AND IT IS WRITTEN TWICE, here and in styles.css,
   * because the layout half is CSS and the dismiss half is JavaScript. A guard
   * in the test suite reads both and fails if they drift — the same shape as the
   * ORDER BY / index guard, and for the same reason: two numbers that must agree
   * and no behaviour that notices when they do not.
   */
  const [narrow, setNarrow] = useState<boolean>(() => {
    try { return window.matchMedia(NARROW_SCREEN).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try { mq = window.matchMedia(NARROW_SCREEN); } catch { return; }
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const [navOpen, setNavOpen] = useState<boolean>(() => {
    /*
      A DEEP LINK ON A NARROW SCREEN OPENS ON THE GRID, not on the nav. Arriving
      from a row link already says which table you want, so an overlay across it
      is the navigation answering a question nobody asked — and the dismissal is
      a small button in the corner. It is not a preference change: what is in
      localStorage is left exactly as it was, and the next wide-screen visit
      honours it.
    */
    try {
      if (window.matchMedia(NARROW_SCREEN).matches && window.location.pathname.startsWith('/ws/')) {
        return false;
      }
    } catch { /* no matchMedia: fall through to the stored preference */ }
    try { return window.localStorage.getItem('console.navOpen') !== '0'; } catch { return true; }
  });

  const has = useCallback((s: string) => me.scopes.includes(s), [me.scopes]);

  /**
   * THIS PERSON'S SETTINGS — C2-13, REQ-1.
   *
   * Loaded ONCE, here, and handed down as a context. The panel writes through
   * `applySettings` so that the module-level display prefs and the React state
   * always move together: the module value is set FIRST and the state change
   * follows, so nothing can paint from a value the tree has not been told about.
   *
   * A FAILURE TO LOAD IS NOT FATAL AND IS NOT SHOWN. Everything below falls back
   * to the catalogue's defaults through `settingValue()`, and a console that
   * refused to open because it could not read a row-height preference would be
   * worse in every way than one that opens with short rows. It is also what
   * happens on the first request after this migration lands and before the
   * deploy that serves the route — which is a real minute, not a hypothetical.
   */
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /**
   * THE CONSOLE'S OWN SETTINGS — D-7, and they are a different thing entirely.
   *
   * `settings` above is one person's preferences and changes nothing for anybody
   * else. This is the console's configuration — today, the largest file anybody
   * may upload — and it is behind console.settings:manage, which sits on the
   * owner_admin ceiling and no other.
   *
   * NOT LOADED AT STARTUP. It is fetched when the panel opens, because every
   * other session would fetch it and be refused: this is the one panel in the
   * grid that exactly one person can open.
   */
  const [ownerSettingsOpen, setOwnerSettingsOpen] = useState(false);
  const [claudeAccessOpen, setClaudeAccessOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);

  const applySettings = useCallback((p: SettingsPayload | null) => {
    setDisplayPrefs(p);
    setSettings(p);
  }, []);

  useEffect(() => {
    api.settings().then(applySettings).catch(() => { /* defaults, see above */ });
  }, [applySettings]);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [bases, setBases] = useState<Record<string, Base[]>>({});
  const [tables, setTables] = useState<Record<string, TableSummary[]>>({});
  const [openWs, setOpenWs] = useState<Set<string>>(new Set());
  const [openBase, setOpenBase] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<TableSummary | null>(null);
  /**
   * The slugs of the workspace and base the selected table sits under.
   *
   * Needed for one thing only: building a row's own URL (C1c-13). A table
   * summary does not carry its parents, and deriving them by searching the
   * loaded tree would go wrong the moment two bases in different workspaces
   * shared a table slug — which is allowed, because slugs are unique within
   * their parent and not globally.
   */
  const [selectedPath, setSelectedPath] = useState<{ ws: string; base: string }>({ ws: '', base: '' });
  /** A row named in the address bar, waiting for its table to finish loading. */
  const [deepRow, setDeepRow] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [creating, setCreating] = useState<NewThing | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [access, setAccess] = useState<{ level: GrantLevel; id: string; name: string } | null>(null);
  /* C2b-12. Opened from a table's row tools, next to the access control, because
     "who can see this" and "where does this get sent" are the same kind of
     question about the same table. */
  const [automations, setAutomations] = useState<{ id: string; name: string } | null>(null);

  const canBuild = has(SCOPE.schemaWrite);

  const loadWorkspaces = useCallback(async () => {
    if (!has(SCOPE.schemaRead)) return;
    try {
      const r = await api.workspaces();
      setWorkspaces(r.workspaces);
    } catch (e) { onError(e); }
  }, [has]);

  useEffect(() => { void loadWorkspaces(); }, [loadWorkspaces]);

  /**
   * ROW LINKS, the receiving end (C1c-13).
   *
   * /ws/{workspace}/{base}/{table}/r/{row} opens that table with that row
   * expanded. The point is not deep linking for its own sake — it is that a Matt
   * Queue row, or an agent, can say "open this row" and mean it.
   *
   * THE LINK IS NOT AN ACCESS TOKEN. Resolving it walks the ordinary API, so
   * every step runs the same grant check as clicking through the sidebar would:
   * a person sent a link to something they may not see gets an empty sidebar and
   * nothing else. Sending someone a URL grants them nothing.
   *
   * Runs once, on boot. A path that names nothing simply leaves the grid on its
   * welcome screen rather than reporting an error about a URL the person may
   * have typed by hand.
   */
  useEffect(() => {
    const row = parseRowLink(window.location.pathname);
    const want = row ?? matchTablePath(window.location.pathname);
    if (!want || !has(SCOPE.schemaRead)) return;
    let cancelled = false;
    void (async () => {
      try {
        const ws = (await api.workspaces()).workspaces.find((w) => w.slug === want.workspace);
        if (!ws || cancelled) return;
        const bs = (await api.bases(ws.id)).bases.find((b) => b.slug === want.base);
        if (!bs || cancelled) return;
        const tb = (await api.tables(bs.id)).tables.find((t) => t.slug === want.table);
        if (!tb || cancelled) return;
        await loadBases(ws.id);
        await loadTables(bs.id);
        if (cancelled) return;
        setOpenWs((o) => new Set(o).add(ws.id));
        setOpenBase((o) => new Set(o).add(bs.id));
        setSelected(tb);
        setSelectedPath({ ws: ws.slug, base: bs.slug });
        if (row) setDeepRow(row.rowId);
      } catch { /* a hand-typed URL that names nothing is not an error worth a banner */ }
    })();
    return () => { cancelled = true; };
    // Boot only. Re-running this on every render would fight the sidebar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onError(err: unknown) {
    if (err instanceof ApiError) setProblem(err);
    else setProblem(new ApiError({ kind: 'error', message: String(err) }));
  }

  const loadBases = useCallback(async (workspaceId: string) => {
    try {
      const r = await api.bases(workspaceId);
      setBases((b) => ({ ...b, [workspaceId]: r.bases }));
    } catch (e) { onError(e); }
  }, []);

  const loadTables = useCallback(async (baseId: string) => {
    try {
      const r = await api.tables(baseId);
      setTables((t) => ({ ...t, [baseId]: r.tables }));
    } catch (e) { onError(e); }
  }, []);

  async function toggleWs(w: Workspace) {
    const next = new Set(openWs);
    if (next.has(w.id)) next.delete(w.id);
    else {
      next.add(w.id);
      if (!bases[w.id]) await loadBases(w.id);
    }
    setOpenWs(next);
  }

  async function toggleBase(b: Base) {
    const next = new Set(openBase);
    if (next.has(b.id)) next.delete(b.id);
    else {
      next.add(b.id);
      if (!tables[b.id]) await loadTables(b.id);
    }
    setOpenBase(next);
  }

  /**
   * Re-read only the branch that changed.
   *
   * Not a blanket reload: the sidebar's open/closed state and the selected table
   * are the person's place in the app, and throwing them away on every rename is
   * how a grid becomes annoying to use.
   */
  async function afterStructureChange(scope: { workspaceId?: string; baseId?: string }) {
    await loadWorkspaces();
    if (scope.workspaceId) await loadBases(scope.workspaceId);
    if (scope.baseId) await loadTables(scope.baseId);
  }

  return (
    <SettingsContext.Provider value={settings}>
    <div className={`shell${navOpen ? '' : ' navshut'}${narrow ? ' narrow' : ''}`}>
      {/*
        THE SCRIM, and it is the dismissal Matt asked for as well as the dimming.
        An overlay with no way out but the same small button you opened it with
        is a trap on a phone; tapping the grid you can see is the gesture
        everybody already has.
      */}
      {narrow && navOpen && (
        <div
          className="navscrim"
          role="button"
          tabIndex={-1}
          aria-label="Close the sidebar"
          onPointerDown={() => setNavOpen(false)}
        />
      )}
      {/*
        THE SIDEBAR TOGGLE (Matt, 2026-08-28).

        It sits ON the seam between the nav and the grid rather than inside
        either, which is what he asked for and is also the only place it can be:
        put it inside the nav and it disappears with the nav.

        The chevrons point the way the panel WILL go, not the way it currently
        is — « while open (click and it leaves, to the left), » while shut
        (click and it comes back, from the left). That is the reading that
        matches what your hand is about to do.

        The choice survives a reload because it is a preference about how you
        work, not state about what you are looking at. A wrapped read: private
        browsing and blocked site data both throw on access rather than
        returning null, and a console that will not start because it could not
        remember a sidebar is a bad trade.
      */}
      <button
        className="navtoggle"
        onClick={() => {
          const next = !navOpen;
          setNavOpen(next);
          try { window.localStorage.setItem('console.navOpen', next ? '1' : '0'); } catch { /* not worth failing over */ }
        }}
        aria-label={navOpen ? 'Collapse the sidebar' : 'Expand the sidebar'}
        aria-expanded={navOpen}
        title={navOpen ? 'Collapse the sidebar' : 'Expand the sidebar'}
      >
        {navOpen ? '\u00AB' : '\u00BB'}
      </button>
      <nav className="side">
        <div className="brand">
          Assett AI Console
          <small>
            {me.displayName || me.email} · {me.role.replace('_', ' ')}
            {me.kind === 'internal_key' ? ' · internal key' : ''}
          </small>
          {/*
            Sign out. The route existed from the first build and nothing ever
            called it, which meant the only way to end a session was to clear a
            cookie by hand — and that is also the only way to pick up a scope
            that was minted after you signed in, because a session keeps what it
            was handed at its door.

            Not offered to the internal header key: that caller has no session
            to end, and a button that clears a cookie it never had would report
            success and change nothing.
          */}
          {me.kind === 'session' && (
            <button
              className="signout"
              title={`Sign out of ${me.email}`}
              onClick={async () => {
                try { await api.signOut(); } catch { /* the cookie is going either way */ }
                // A full reload rather than clearing React state: the session
                // cookie is gone, so every request from this point would 401,
                // and the honest thing to show is the sign-in screen.
                window.location.assign('/');
              }}
            >
              Sign out
            </button>
          )}
          {/*
            SETTINGS — REQ-1. It sits beside the person's own name because that
            is what it is about: these are HIS preferences, not the console's
            configuration, and nothing in here changes what anybody else sees.

            Offered to the internal header key as well. That caller is Matt on
            his own machine, its identity is the same row, and the settings it
            reads are the ones his browser wrote.
          */}
          <button
            className="signout"
            title="Your settings — row height, dates, time zone and more"
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </button>
          {/*
            OWNER SETTINGS — D-7, Decision reccRrHWWA2ZKl3vw.

            Shown only to a session carrying console.settings:manage, which is on
            the owner_admin ceiling and no other. HIDING IT IS A COURTESY AND NOT
            A WALL: the route checks the same scope, and the database refuses the
            write on any row that is not flagged editable. This button being
            absent is what stops a person being confused; those two are what stop
            them writing.

            It is a SEPARATE button rather than a section inside Settings,
            because the two answer different questions — "how do I want to see
            this" against "what is this console set to" — and the second one
            changes the product for everybody.
          */}
          {has(SCOPE.settingsManage) && (
            <button
              className="signout"
              title="Console settings — what this console is set to, for everybody"
              onClick={() => setOwnerSettingsOpen(true)}
            >
              Console
            </button>
          )}
          {/*
            CLAUDE ACCESS — M1-12.

            Behind console.settings:manage, the same scope as Console settings,
            and shown only to a session carrying it. SEPARATE FROM Console
            settings on purpose, and M1-12 says why: the panel is where the
            browser-only boundary is made VISIBLE rather than implied. Folding
            two switches that decide what Claude may do without asking into a
            list of console preferences would say they are the same kind of
            thing, and they are not.

            HIDING THE BUTTON IS A COURTESY AND NOT A WALL, as everywhere else
            here: the route checks the scope, and requireBrowserSession refuses
            any caller that is not a person in a browser — including an MCP
            token that carries the scope.
          */}
          {has(SCOPE.settingsManage) && (
            <button
              className="signout"
              title="Claude Access — what your Claude may do without asking you"
              onClick={() => setClaudeAccessOpen(true)}
            >
              Claude
            </button>
          )}
          {/*
            PEOPLE — M1-11.

            The screen Matt asked for, and the reason handing somebody a
            permission has been a hand-written row in the Supabase table editor
            since C1. Shown only to a session carrying console.users:manage,
            which is on the owner_admin ceiling and no other.

            SEPARATE FROM ACCESS, which sits on each object. That one answers
            "who can see THIS table"; this one answers "what kind of thing may
            this PERSON do at all". They are different questions and a screen
            that mixed them would answer neither well — a person can hold "edit
            rows" and still see nothing, which is the whole deny-by-default
            shape and is confusing exactly once if the two are shown together.
          */}
          {/*
            USERS, NOT PEOPLE — Matt's ruling of 2026-09-14 (M1-E, Decision
            8dcbb633). Everywhere a person reads it; the code keeps `people`.
          */}
          {has(SCOPE.usersManage) && (
            <button
              className="signout"
              title="Users — who can sign in, and what each person may do"
              onClick={() => setPeopleOpen(true)}
            >
              Users
            </button>
          )}
        </div>

        {!has(SCOPE.schemaRead) && (
          <p className="muted" style={{ padding: '0 14px' }}>
            Your session cannot list the structure.
          </p>
        )}

        {workspaces.map((w) => (
          <div className="ws" key={w.id}>
            <div className="row">
              <button onClick={() => void toggleWs(w)} title={w.description}>
                {openWs.has(w.id) ? '▾' : '▸'} {w.name}
              </button>
              {canBuild && (
                <span className="rowtools">
                  <button
                    className="mini"
                    title={`Rename or describe ${w.name}`}
                    onClick={() => setEditing({ kind: 'workspaces', id: w.id, name: w.name, description: w.description })}
                  >✎</button>
                  <button
                    className="mini"
                    title={`Who can see ${w.name}`}
                    onClick={() => setAccess({ level: 'workspace', id: w.id, name: w.name })}
                  >⚿</button>
                  <button
                    className="mini"
                    title={`New base in ${w.name}`}
                    onClick={() => setCreating({ kind: 'base', parentId: w.id, parentName: w.name })}
                  >+</button>
                </span>
              )}
            </div>
            {openWs.has(w.id) &&
              (bases[w.id] ?? []).map((b) => (
                <div className="base" key={b.id}>
                  <div className="row">
                    <button onClick={() => void toggleBase(b)} title={b.description}>
                      {openBase.has(b.id) ? '▾' : '▸'} {b.name}
                      {b.tenant_slug ? <span className="muted"> · {b.tenant_slug}</span> : null}
                    </button>
                    {canBuild && (
                      <span className="rowtools">
                        <button
                          className="mini"
                          title={`Rename or describe ${b.name}`}
                          onClick={() => setEditing({ kind: 'bases', id: b.id, name: b.name, description: b.description })}
                        >✎</button>
                        <button
                          className="mini"
                          title={`Who can see ${b.name}`}
                          onClick={() => setAccess({ level: 'base', id: b.id, name: b.name })}
                        >⚿</button>
                        <button
                          className="mini"
                          title={`New table in ${b.name}`}
                          onClick={() => setCreating({ kind: 'table', parentId: b.id, parentName: b.name })}
                        >+</button>
                      </span>
                    )}
                  </div>
                  {openBase.has(b.id) &&
                    (tables[b.id] ?? []).map((t) => (
                      <div className="row" key={t.id}>
                        <button
                          className={`tbl${selected?.id === t.id ? ' on' : ''}`}
                          onClick={() => {
                            setProblem(null);
                            setSelected(t);
                            setSelectedPath({ ws: w.slug, base: b.slug });
                            setDeepRow(null);
                            // DISMISS ON SELECTION — C2-17(2). Only when the
                            // sidebar is an overlay: on a wide screen it is a
                            // column, closing it after every click would be a
                            // tic, and the preference in localStorage is left
                            // alone either way because this is not a preference.
                            if (narrow) setNavOpen(false);
                            // The address bar follows the selection, so the URL
                            // in it is always a link that comes back here.
                            window.history.replaceState(null, '', `/ws/${w.slug}/${b.slug}/${t.slug}`);
                          }}
                          title={t.description}
                        >
                          {t.name}
                          {t.system_source ? <span className="muted"> · read-only</span> : null}
                        </button>
                        {canBuild && !t.system_source && (
                          <span className="rowtools">
                            <button
                              className="mini"
                              title={`Rename or describe ${t.name}`}
                              onClick={() => setEditing({ kind: 'tables', id: t.id, name: t.name, description: t.description })}
                            >✎</button>
                            <button
                              className="mini"
                              title={`Who can see ${t.name}`}
                              onClick={() => setAccess({ level: 'table', id: t.id, name: t.name })}
                            >⚿</button>
                            <button
                              className="mini"
                              title={`Where changes to ${t.name} get sent`}
                              onClick={() => setAutomations({ id: t.id, name: t.name })}
                            >⇥</button>
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}

        {canBuild && (
          <button className="newws" onClick={() => setCreating({ kind: 'workspace' })}>
            + New workspace
          </button>
        )}
        {has(SCOPE.schemaRead) && !canBuild && (
          <p className="muted" style={{ padding: '10px 14px 0', fontSize: 12 }}>
            Your session can see the structure but not change it. That needs{' '}
            <code>console.schema:write</code>.
          </p>
        )}
      </nav>

      <main className="main">
        {problem && <Problem err={problem} onDismiss={() => setProblem(null)} />}
        {!selected && <Welcome me={me} />}
        {selected?.system_source === 'brain.content_items' && <BrainGrid has={has} onError={onError} />}
        {selected && selected.system_source && selected.system_source !== 'brain.content_items' && (
          <SystemTableNote table={selected} />
        )}
        {selected && !selected.system_source && (
          <TableGrid
            key={selected.id}
            table={selected}
            wsSlug={selectedPath.ws}
            baseSlug={selectedPath.base}
            openRow={deepRow}
            me={me}
            has={has}
            onError={onError}
            onDescribe={(t) => setEditing({ kind: 'tables', id: t.id, name: t.name, description: t.description })}
          />
        )}
      </main>

      {creating && (
        <CreateDialog
          thing={creating}
          onCancel={() => setCreating(null)}
          onError={onError}
          onCreated={async (created) => {
            setCreating(null);
            if (creating.kind === 'workspace') {
              await afterStructureChange({});
            } else if (creating.kind === 'base') {
              await afterStructureChange({ workspaceId: creating.parentId });
              setOpenWs((s) => new Set(s).add(creating.parentId));
            } else {
              await afterStructureChange({ baseId: creating.parentId });
              setOpenBase((s) => new Set(s).add(creating.parentId));
              if (created?.name) {
                setSelected({
                  id: created.id, slug: created.slug, name: created.name,
                  description: created.description, position: 0, system_source: null,
                });
              }
            }
          }}
        />
      )}

      {access && (
        <AccessDialog
          target={access}
          me={me}
          onCancel={() => setAccess(null)}
          onError={onError}
        />
      )}

      {automations && (
        <AutomationsDialog
          table={automations}
          me={me}
          onCancel={() => setAutomations(null)}
          onError={onError}
        />
      )}

      {editing && (
        <DescribeDialog
          editing={editing}
          onCancel={() => setEditing(null)}
          onError={onError}
          onSaved={async () => {
            const wasEditing = editing;
            setEditing(null);
            await afterStructureChange({});
            // A renamed table has to be re-read into the header too, otherwise the
            // sidebar and the title disagree until the next click.
            for (const [baseId, list] of Object.entries(tables)) {
              if (list.some((t) => t.id === wasEditing.id)) await loadTables(baseId);
            }
            for (const [wsId, list] of Object.entries(bases)) {
              if (list.some((b) => b.id === wasEditing.id)) await loadBases(wsId);
            }
            if (selected && selected.id === wasEditing.id) {
              try {
                const r = await api.table(selected.id);
                setSelected({ ...selected, name: r.table.name, description: r.table.description });
              } catch (e) { onError(e); }
            }
          }}
        />
      )}

      {settingsOpen && (
        <SettingsPanel
          payload={settings}
          onCancel={() => setSettingsOpen(false)}
          onSaved={applySettings}
          onError={onError}
        />
      )}

      {ownerSettingsOpen && (
        <OwnerSettingsPanel onCancel={() => setOwnerSettingsOpen(false)} />
      )}

      {claudeAccessOpen && (
        <ClaudeAccessPanel onCancel={() => setClaudeAccessOpen(false)} onError={onError} />
      )}

      {peopleOpen && (
        <PeoplePanel onCancel={() => setPeopleOpen(false)} onError={onError} />
      )}
    </div>
    </SettingsContext.Provider>
  );
}

/**
 * THE SETTINGS PANEL — C2-13, and REQ-1 is the whole reason it exists.
 *
 * Matt: "I won't remember to go to that script to adjust it."
 *
 * EVERY CONTROL IS DRAWN FROM THE CATALOGUE, and this file names no setting key
 * anywhere. `kind` decides the control — an enum is a select, an int is a number
 * box with the catalogue's own bounds on it, a bool is a checkbox, a timezone is
 * a list of the zones the browser knows. So a preference added by a migration
 * appears here without a deploy, which is the property lens A asks for and the
 * reason the catalogue is a table rather than a constant.
 *
 * SAVING SENDS ONLY WHAT CHANGED. The PATCH merges, so a panel that has never
 * heard of a setting cannot erase it — the shape that bit this system once
 * before, where a partial array silently deleted the entries it did not name.
 *
 * RESET SENDS null, which removes the key and puts that preference back to the
 * catalogue's default. There is no delete verb and console_app holds no DELETE
 * on the table: putting a value back is a write, like every other write here.
 */
function SettingsPanel({
  payload, onCancel, onSaved, onError,
}: {
  payload: SettingsPayload | null;
  onCancel: () => void;
  onSaved: (p: SettingsPayload) => void;
  onError: (e: unknown) => void;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({ ...(payload?.settings ?? {}) }));
  const [busy, setBusy] = useState(false);

  const catalogue = payload?.catalogue ?? [];

  /** The value a control should show: the draft, else the catalogue's default. */
  function shown(spec: SettingKey): unknown {
    return spec.key in draft ? draft[spec.key] : spec.default_value;
  }
  function isDefault(spec: SettingKey): boolean {
    return !(spec.key in draft);
  }
  function set(key: string, value: unknown): void {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  function reset(key: string): void {
    setDraft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const before = payload?.settings ?? {};
      const patch: Record<string, unknown> = {};
      // Only the differences, in both directions: a key that was set and is now
      // back to its default travels as null.
      for (const spec of catalogue) {
        const had = spec.key in before;
        const has = spec.key in draft;
        if (has && (!had || JSON.stringify(before[spec.key]) !== JSON.stringify(draft[spec.key]))) {
          patch[spec.key] = draft[spec.key];
        } else if (had && !has) {
          patch[spec.key] = null;
        }
      }
      if (Object.keys(patch).length === 0) { onCancel(); return; }
      const r = await api.saveSettings(patch);
      onSaved({ catalogue, settings: r.settings });
      onCancel();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Your settings" onCancel={onCancel} wide>
      {catalogue.length === 0 && (
        <p className="muted" style={{ marginTop: 0 }}>
          Settings are not available on this console yet.
        </p>
      )}

      {catalogue.map((spec) => (
        <div key={spec.key} className="setting">
          <label htmlFor={`set-${spec.key}`}>
            <strong>{spec.label}</strong>
            {!isDefault(spec) && (
              <button className="linkish" onClick={() => reset(spec.key)} title="Back to the default">
                reset
              </button>
            )}
          </label>

          {spec.kind === 'enum' && (
            <select
              id={`set-${spec.key}`}
              value={String(shown(spec) ?? '')}
              onChange={(e) => set(spec.key, e.target.value)}
            >
              {(spec.allowed ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}

          {spec.kind === 'int' && (
            <input
              id={`set-${spec.key}`}
              type="number"
              min={spec.min_int ?? undefined}
              max={spec.max_int ?? undefined}
              step={1}
              value={Number(shown(spec) ?? 0)}
              onChange={(e) => {
                // The database is the wall — console.validate_settings() refuses
                // anything outside the catalogue's bounds and says so. This only
                // stops an empty box becoming NaN on the way there.
                const n = Number.parseInt(e.target.value, 10);
                if (!Number.isNaN(n)) set(spec.key, n);
              }}
            />
          )}

          {spec.kind === 'bool' && (
            <input
              id={`set-${spec.key}`}
              type="checkbox"
              checked={shown(spec) === true}
              onChange={(e) => set(spec.key, e.target.checked)}
            />
          )}

          {spec.kind === 'timezone' && (
            <TimeZonePicker
              id={`set-${spec.key}`}
              value={String(shown(spec) ?? '')}
              onChange={(v) => set(spec.key, v)}
            />
          )}

          <small className="muted">{spec.help}</small>
        </div>
      ))}

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" disabled={busy || catalogue.length === 0} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * THE CONSOLE'S OWN SETTINGS — Matt's ruling D-7, Decision reccRrHWWA2ZKl3vw.
 *
 * WHAT MAKES THIS DIFFERENT FROM THE PANEL ABOVE, and it is worth being explicit
 * because the two look alike: those are one person's preferences and change
 * nothing for anybody else. These are the console's configuration. Raising the
 * upload cap raises it for every person and every base, and the number is read
 * by the upload route on every intent.
 *
 * THIS FILE NAMES NO SETTING KEY, exactly as the panel above does not. A row is
 * editable if it says it is, its floor and ceiling come from the row, and its
 * words come from the row — so the day a migration flags a second row editable,
 * a control appears here without a deploy. That is the property that makes
 * console.plan_config a table rather than a constant in the server.
 *
 * HIDING A CONTROL IS NOT A WALL AND IS NOT TREATED AS ONE. Every read-only row
 * below is refused by an RLS policy, and every number outside its bounds by a
 * check constraint on the table. This panel exists so that a person does not
 * have to discover either of those by being refused.
 *
 * THE ERROR IS SHOWN HERE RATHER THAN THROUGH onError, deliberately: the refusals
 * this can hit are all written once on the server and say what to type instead,
 * and a sentence about a number belongs next to the box that holds it.
 */
/**
 * THE CLAUDE ACCESS PANEL — M1-12, and the two switches of M1-8.
 *
 * WHY IT IS ITS OWN PANEL and not a section of Console settings: M1-12 says it
 * "keeps the browser-only boundary visible rather than implied". A switch that
 * decides whether Claude may act without asking is a different kind of thing
 * from a row height or an upload cap, and a screen that listed them together
 * would say otherwise.
 *
 * THE EXPIRY IS NEVER COMPUTED HERE. `on` and `expires_at` arrive already
 * decided by console.claude_switch_on(); this file only renders them. A
 * countdown derived in the browser from enabled_at would drift from the wall
 * that actually decides, and would drift in the direction of showing a switch
 * as open after it had shut.
 *
 * PHONE FIRST. Matt reads this standing up on a small screen — every row is a
 * block, nothing is a fixed-layout table, and no cell relies on horizontal
 * room. M1-C's People table passed a 1568px drive and was broken at 390px.
 */
function ClaudeAccessPanel({
  onCancel, onError,
}: { onCancel: () => void; onError: (e: unknown) => void }) {
  const [data, setData] = useState<ClaudeAccess | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    api.claudeAccess()
      .then(setData)
      .catch((e) => { onError(e); setProblem(describeError(e)); });
  }, [onError]);

  useEffect(load, [load]);

  const flip = async (row: ClaudeSwitchRow, on: boolean, durationSeconds?: number) => {
    setBusy(row.switch);
    setProblem(null);
    try {
      const r = await api.setClaudeSwitch(row.switch, on, durationSeconds);
      setData((d) => (d ? { ...d, switches: r.switches } : d));
    } catch (e) {
      setProblem(describeError(e));
      onError(e);
    } finally {
      setBusy(null);
    }
  };

  const settle = async (p: ClaudeProposalRow, outcome: 'applied' | 'rejected') => {
    setBusy(p.id);
    setProblem(null);
    try {
      await api.settleProposal(p.id, outcome);
      load();
    } catch (e) {
      setProblem(describeError(e));
      onError(e);
    } finally {
      setBusy(null);
    }
  };

  const pending = data?.proposals.filter((p) => p.status === 'pending') ?? [];
  const settled = data?.proposals.filter((p) => p.status !== 'pending') ?? [];

  return (
    <Modal title="Claude Access" onCancel={onCancel} wide>
      <p className="muted" style={{ marginTop: 0 }}>
        These two switches decide whether your Claude does a thing straight away, or writes it
        down here and waits for you. They turn themselves off after a while, so one you forget
        does not stay on.
      </p>

      {problem && <p className="muted bad">{problem}</p>}
      {data === null && !problem && <p className="muted">Reading&hellip;</p>}

      {data?.switches.map((row) => (
        <div key={row.switch} className="setting" style={{ display: 'block' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong>{row.label}</strong>
            <span className="muted">{row.on ? 'ON' : 'off'}</span>
          </div>

          <small className="muted" style={{ display: 'block', margin: '0.25rem 0 0.5rem' }}>
            {row.help}
          </small>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="signout"
              disabled={busy === row.switch}
              onClick={() => void flip(row, !row.on)}
            >
              {row.on ? 'Turn it off' : 'Turn it on'}
            </button>

            {/*
              THE DURATION IS WRITTEN BY THIS SAME ROUTE (Amendment 1, M1-R2),
              and it is a fixed list rather than a number box. A control whose
              whole job is to be temporary must not accept a mistyped 100000.
            */}
            <label className="muted" htmlFor={`dur-${row.switch}`}>for</label>
            <select
              id={`dur-${row.switch}`}
              value={row.duration_seconds}
              disabled={busy === row.switch}
              onChange={(e) => void flip(row, row.on, Number(e.target.value))}
            >
              {data.durations.map((d) => (
                <option key={d} value={d}>{durationLabel(d)}</option>
              ))}
            </select>
          </div>

          <small className="muted" style={{ display: 'block', marginTop: '0.4rem' }}>
            {row.on && row.expires_at
              ? `Turns itself off ${whenPlainly(row.expires_at)}` +
                (row.enabled_by_email ? ` — switched on by ${row.enabled_by_email}` : '')
              : 'Off. Your Claude writes what it wants to do below, and waits for you.'}
          </small>
        </div>
      ))}

      <h3 style={{ marginBottom: '0.25rem' }}>Waiting for you</h3>
      {pending.length === 0 && <p className="muted">Nothing is waiting.</p>}
      {pending.map((p) => (
        <div key={p.id} className="setting" style={{ display: 'block' }}>
          <div><strong>{p.summary}</strong></div>
          <small className="muted" style={{ display: 'block', margin: '0.25rem 0 0.5rem' }}>
            {p.kind === 'permission' ? 'A permission change' : 'An automation'}
            {p.proposed_by_email ? ` — asked for by ${p.proposed_by_email}` : ''}
            {`, ${whenPlainly(p.created_at)}`}
          </small>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="signout" disabled={busy === p.id}
              onClick={() => void settle(p, 'applied')}>Mark it done</button>
            <button className="signout" disabled={busy === p.id}
              onClick={() => void settle(p, 'rejected')}>No</button>
          </div>
          {/*
            SAID ON THE SCREEN, not only in a spec: marking it done RECORDS the
            decision. Writing a permission is still Users, which is the only
            screen that touches who holds what.
          */}
          <small className="muted" style={{ display: 'block', marginTop: '0.4rem' }}>
            {p.kind === 'permission'
              ? 'Marking it done records your decision. Changing who holds what is still Users.'
              : 'Marking it done records your decision.'}
          </small>
        </div>
      ))}

      {settled.length > 0 && (
        <>
          <h3 style={{ marginBottom: '0.25rem' }}>Already decided</h3>
          {settled.slice(0, 20).map((p) => (
            <div key={p.id} className="setting" style={{ display: 'block' }}>
              <div>{p.summary}</div>
              <small className="muted">
                {p.status}
                {p.auto_applied ? ' — without asking you, because the switch was on' : ''}
                {p.settled_by_email ? ` — ${p.settled_by_email}` : ''}
                {p.settled_at ? `, ${whenPlainly(p.settled_at)}` : ''}
              </small>
            </div>
          ))}
        </>
      )}

      <p className="muted" style={{ marginTop: '1rem' }}>{data?.note}</p>
    </Modal>
  );
}

/**
 * The server's sentence, or a plain one if it did not send a message.
 *
 * These routes answer in words a person can act on — the duration refusal names
 * the whole list, and the browser-only refusal explains the caller kind — so the
 * server's text is shown rather than replaced with a generic failure.
 */
function describeError(e: unknown): string {
  if (e instanceof ApiError) return e.refusal.message;
  return String(e);
}

/** 3600 -> "1 hour". The list is fixed by Amendment 1, so this is a lookup. */
function durationLabel(seconds: number): string {
  if (seconds === 3600) return '1 hour';
  if (seconds === 14400) return '4 hours';
  if (seconds === 28800) return '8 hours';
  if (seconds === 86400) return '24 hours';
  if (seconds === 604800) return '7 days';
  return `${seconds} seconds`;
}

/**
 * A timestamp a person reads, in their own zone, without a countdown.
 *
 * NOT a live countdown on purpose: a ticking number in the browser is a second
 * copy of the expiry, and the database is the one that decides. This says when,
 * and a reload asks the wall again.
 */
function whenPlainly(iso: string): string {
  const t = new Date(iso);
  const today = new Date();
  const sameDay = t.toDateString() === today.toDateString();
  const time = t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `at ${time} today` : `at ${time} on ${t.toLocaleDateString()}`;
}

function OwnerSettingsPanel({ onCancel }: { onCancel: () => void }) {
  const [rows, setRows] = useState<PlanConfigRow[] | null>(null);
  /**
   * THE DRAFT HOLDS WHAT WAS TYPED, AS A STRING, AND THAT IS THE FIX FOR THE
   * DEFECT MATT FOUND ON 2026-09-05 ("I'm not able to edit any").
   *
   * It used to hold a number, and the onChange refused anything that did not
   * parse — so an EMPTY box could never be represented. React restores a
   * controlled input's DOM value when the state does not change, so select-all
   * and Backspace put `262144000` straight back and the field read as
   * read-only. Reproduced in a real browser before it was changed: cmd+A then
   * Backspace, value unchanged.
   *
   * A half-typed value is a normal state. Whether it is ACCEPTABLE is a
   * different question, asked by ownerSettingProblem() and answered under the
   * box while somebody types.
   */
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await api.ownerSettings();
        if (alive) { setRows(r.config); setProblem(null); }
      } catch (e) {
        if (alive) setProblem(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  /** A bound arrives as a bigint, which pg hands over as a string. */
  const bound = (v: string | number | null): number | null =>
    v === null ? null : Number(v);

  /**
   * WHICH ROWS THIS PANEL CAN OFFER A CONTROL FOR, and it is a narrower question
   * than `is_owner_editable`. A row is editable AND numeric AND bounded, or it
   * is shown read-only with the reason — because a control with no bounds is a
   * box that accepts anything and finds out from the database, which is the
   * shape this whole ruling was about avoiding.
   */
  function editable(r: PlanConfigRow): boolean {
    return r.is_owner_editable
      && typeof r.value === 'number'
      && bound(r.min_int) !== null && bound(r.max_int) !== null;
  }

  /** What the box holds right now: the draft if it has been touched, else the row. */
  function shown(r: PlanConfigRow): string {
    return r.key in draft ? draft[r.key]! : String(r.value);
  }

  /** null when the current text is acceptable; a sentence when it is not. */
  function problemWith(r: PlanConfigRow): string | null {
    const lo = bound(r.min_int);
    const hi = bound(r.max_int);
    if (lo === null || hi === null) return null;
    return ownerSettingProblem(shown(r), lo, hi);
  }

  /** Touched AND different from what the server holds. */
  function changed(r: PlanConfigRow): boolean {
    return r.key in draft && shown(r) !== String(r.value);
  }

  async function save(): Promise<void> {
    if (!rows) return;
    const patch: Record<string, number> = {};
    for (const r of rows) {
      if (!editable(r) || !changed(r)) continue;
      // REFUSED HERE RATHER THAN SENT, so the sentence names the box. The
      // database refuses it too — this is not the wall, it is the manners.
      const bad = problemWith(r);
      if (bad) { setProblem(`${r.label}: ${bad}`); return; }
      patch[r.key] = Number(shown(r).trim());
    }
    if (Object.keys(patch).length === 0) { onCancel(); return; }
    setBusy(true);
    try {
      const saved = await api.saveOwnerSettings(patch);
      setRows(saved.config);
      setDraft({});
      onCancel();
    } catch (e) {
      // VERBATIM. The server's refusal names the floor and the ceiling and says
      // why they are there; rewriting it here would be a second copy of the
      // bounds that drifts the day a migration changes them.
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Console settings" onCancel={onCancel} wide>
      <p className="muted" style={{ marginTop: 0 }}>
        These are the console&rsquo;s own settings, not yours — a change here applies to
        everybody who uses it. Only an Owner Admin can open this.
      </p>

      {problem && <p className="muted bad">{problem}</p>}

      {rows === null && !problem && <p className="muted">Reading the console&rsquo;s settings&hellip;</p>}

      {rows?.map((r) => (
        <div key={r.key} className="setting">
          <label htmlFor={`plan-${r.key}`}><strong>{r.label}</strong></label>

          {editable(r) ? (
            <>
              <input
                id={`plan-${r.key}`}
                type="number"
                min={bound(r.min_int) ?? undefined}
                max={bound(r.max_int) ?? undefined}
                step={1}
                value={shown(r)}
                onChange={(e) => {
                  // EVERY value is accepted into the draft, including the empty
                  // string. Refusing one here is what made the box unclearable
                  // and therefore unusable — see the draft's own comment.
                  const typed = e.target.value;
                  setDraft((d) => ({ ...d, [r.key]: typed }));
                  if (problem) setProblem(null);
                }}
              />
              <small className={problemWith(r) ? 'muted bad' : 'muted'}>
                {problemWith(r) ?? (
                  `${Number(shown(r)).toLocaleString('en-US')} — between ` +
                  `${(bound(r.min_int) ?? 0).toLocaleString('en-US')} and ` +
                  `${(bound(r.max_int) ?? 0).toLocaleString('en-US')}`
                )}
              </small>
            </>
          ) : (
            <>
              <div id={`plan-${r.key}`} className="muted" style={{ wordBreak: 'break-word' }}>
                {typeof r.value === 'number'
                  ? r.value.toLocaleString('en-US')
                  : JSON.stringify(r.value)}
              </div>
              <small className="muted">
                Read-only here. Changing it is a numbered migration — deliberate, because it is
                read by the upload path.
              </small>
            </>
          )}

          <small className="muted">{r.help}</small>
        </div>
      ))}

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button
          className="action on"
          disabled={busy || rows === null || !rows.some(editable)}
          title={rows?.some(changed) ? 'Save the change' : 'Nothing has changed yet'}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * A zone picker that does not ship a list of zones.
 *
 * `Intl.supportedValuesOf('timeZone')` is the browser's own copy of the IANA
 * database — the same database `pg_timezone_names` validates against on the
 * server, so the two agree without either of them holding a list this project
 * maintains. Where it is missing, the box falls back to free text and the server
 * is still the wall.
 */
function TimeZonePicker({
  id, value, onChange,
}: { id: string; value: string; onChange: (v: string) => void }) {
  const zones = useMemo<string[]>(() => {
    try {
      const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
      return typeof f === 'function' ? f('timeZone') : [];
    } catch { return []; }
  }, []);

  /**
   * "AUTO-DETECT" IS NAMED, AND IT SAYS WHAT IT RESOLVES TO IN BOTH PLACES — M1-7.
   *
   * The empty value already had an option, labelled "Follow this browser". That
   * was true and is no longer the whole truth: since M1-B a person's Claude
   * reads this same setting through the MCP door, has no browser to follow, and
   * resolves an empty setting to the house zone. So the blank now means two
   * different things depending on who is asking, and the one place a person
   * could ever find that out is the control itself.
   *
   * The VALUE is unchanged — this still stores the empty string it has stored
   * since 0031. Only the words change, here and in the setting's help row
   * (migration 0043).
   */
  const browserZone = zoneOf({ ...BROWSER_TZ });
  const autoLabel = `Auto-detect — this browser: ${browserZone}; your Claude: ${HOUSE_ZONE}`;

  if (zones.length === 0) {
    return (
      <input
        id={id}
        value={value}
        placeholder={`America/New_York — or empty for ${autoLabel}`}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{autoLabel}</option>
      {zones.map((z) => <option key={z} value={z}>{z}</option>)}
    </select>
  );
}

/**
 * Creating a workspace, a base or a table.
 *
 * The slug is offered, not imposed: it is derived from the name as you type
 * until you edit it yourself, after which it is yours. A slug is a permanent
 * handle in a physical schema name, so guessing one and silently keeping it is
 * worse than asking.
 *
 * WHAT THIS DIALOG DOES NOT DO is decide whether the person may do this. It
 * checks the slug SHAPE, because a round trip to be told "must be lower-case" is
 * a waste of everyone's time. Authorisation happens twice on the far side —
 * `requireScope` on the route and `assert_ddl_allowed` inside the SECURITY
 * DEFINER function — and a refusal from either is rendered as itself.
 */
function CreateDialog({
  thing, onCancel, onCreated, onError,
}: {
  thing: NewThing;
  onCancel: () => void;
  onCreated: (created: { id: string; slug: string; name: string; description: string } | null) => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugOk = SLUG_RE.test(effectiveSlug);
  const noun = thing.kind;
  const where = thing.kind === 'workspace' ? null : thing.parentName;

  async function submit() {
    if (!name.trim() || !slugOk || busy) return;
    setBusy(true);
    try {
      if (thing.kind === 'workspace') {
        const r = await api.createWorkspace({ slug: effectiveSlug, name: name.trim(), description });
        onCreated({ id: r.id, slug: effectiveSlug, name: name.trim(), description });
      } else if (thing.kind === 'base') {
        const r = await api.createBase({ workspaceId: thing.parentId, slug: effectiveSlug, name: name.trim(), description });
        onCreated({ id: r.id, slug: effectiveSlug, name: name.trim(), description });
      } else {
        const r = await api.createTable({ baseId: thing.parentId, slug: effectiveSlug, name: name.trim(), description });
        onCreated({ id: r.id, slug: effectiveSlug, name: name.trim(), description });
      }
    } catch (e) {
      onError(e);
      onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`New ${noun}`} onCancel={onCancel}>
      {where && <p className="muted" style={{ marginTop: 0 }}>Inside <strong>{where}</strong>.</p>}

      <label htmlFor="c-name">Name</label>
      <input
        id="c-name" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
        placeholder={thing.kind === 'table' ? 'Facilities' : 'Operations'}
      />

      <label htmlFor="c-slug">Slug</label>
      <input
        id="c-slug" value={effectiveSlug}
        onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
      />
      <p className={`muted${effectiveSlug && !slugOk ? ' bad' : ''}`} style={{ fontSize: 12, marginTop: 4 }}>
        {effectiveSlug && !slugOk
          ? 'Lower-case, starting with a letter, using only letters, digits and underscores.'
          : 'The permanent handle. It becomes part of a physical schema name and does not change when you rename.'}
      </p>

      <label htmlFor="c-desc">Description</label>
      <textarea
        id="c-desc" value={description} rows={3}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What this is for. Plain markdown."
      />

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" disabled={!name.trim() || !slugOk || busy} onClick={() => void submit()}>
          {busy ? 'Creating…' : `Create ${noun}`}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Renaming, and the description that goes with it.
 *
 * Names and descriptions are editable at every level (spec v2 V2-1); slugs and
 * physical names are not, and that is enforced by a COLUMN-LEVEL grant in
 * migration 0012 rather than by the absence of a text box here.
 */
function DescribeDialog({
  editing, onCancel, onSaved, onError,
}: {
  editing: Editing;
  onCancel: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState(editing.name);
  const [description, setDescription] = useState(editing.description ?? '');
  const [busy, setBusy] = useState(false);
  const singular = { workspaces: 'workspace', bases: 'base', tables: 'table', fields: 'field' }[editing.kind];

  async function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await api.describe(editing.kind, editing.id, { name: name.trim(), description });
      onSaved();
    } catch (e) {
      onError(e);
      onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Edit ${singular}`} onCancel={onCancel}>
      <label htmlFor="e-name">Name</label>
      <input
        id="e-name" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
      />

      <label htmlFor="e-desc">Description</label>
      <textarea id="e-desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />

      <p className="muted" style={{ fontSize: 12 }}>
        The slug does not change. It is a permanent handle, and renaming a schema out from
        under a running query should not be a side effect of fixing a typo.
      </p>

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" disabled={!name.trim() || busy} onClick={() => void submit()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

/** A plain modal. Escape closes it; the backdrop closes it; focus starts inside. */
function Modal({
  title, children, onCancel, wide,
}: {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
  wide?: boolean;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        {/*
          Matt, 2026-08-28, pointing at Airtable's expand panel. Escape already
          closed this and so did a click on the backdrop, but neither is visible
          — a dialog with no X reads as one you might be stuck in. It goes on
          Modal rather than on the expand panel so every dialog gets it at once.
        */}
        <button className="modal-x" onClick={onCancel} aria-label={`Close ${title}`} title="Close">×</button>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Problem({ err, onDismiss }: { err: ApiError; onDismiss: () => void }) {
  const scope = err.refusal.kind === 'scope';
  return (
    <div className={`notice${scope ? ' scope' : ''}`}>
      <h2>{scope ? 'Your session cannot do that' : 'Refused'}</h2>
      <p style={{ margin: '0 0 6px' }}>{err.refusal.message}</p>
      {err.refusal.requiredScope && (
        <p className="muted" style={{ margin: '0 0 8px' }}>
          It needs <code>{err.refusal.requiredScope}</code>. That is a property of how you signed
          in, not of what you have been granted — the two narrow independently, and whichever says
          no, wins.
        </p>
      )}
      <button className="action" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

function Welcome({ me }: { me: Me }) {
  return (
    <div className="notice">
      <h2>Signed in as {me.email}</h2>
      <p style={{ margin: '0 0 8px' }}>
        Pick a table on the left. You are {article(me.role.replace('_', ' '))}{' '}
        <strong>{me.role.replace('_', ' ')}</strong> on <code>{me.tenant}</code>.
      </p>
      <p className="muted" style={{ margin: 0 }}>
        This session carries {me.scopes.length} scope{me.scopes.length === 1 ? '' : 's'}:
      </p>
      {/*
        A FLEX ROW, NOT INLINE <code> ELEMENTS — Matt, 2026-08-29, first browser
        pass: the scope list ran straight out of the panel and off the side of
        the screen. The cause is not the width, it is that adjacent JSX elements
        have NO WHITESPACE TEXT NODE between them, and a line can only break at a
        break opportunity. `marginRight: 8` made the codes LOOK separated while
        leaving the browser with one unbreakable run 118 characters long.
        Wrapping them in a flex container gives every item its own line box, so
        the list wraps at any width, and `gap` replaces the margin that was
        pretending to be spacing.
      */}
      <div className="scopelist">
        {me.scopes.map((s) => <code key={s}>{s}</code>)}
      </div>
    </div>
  );
}

function SystemTableNote({ table }: { table: TableSummary }) {
  return (
    <div className="notice">
      <h2>{table.name}</h2>
      <p style={{ margin: 0 }}>
        A read-only view of <code>{table.system_source}</code>. C1 renders the corpus itself;
        the other brain tables arrive with the admin MCP in C3.
      </p>
    </div>
  );
}

/**
 * Brain content, through the console's own door.
 *
 * What is on screen is exactly what the brain would serve this person — the same
 * predicate decides both, and no console grant can widen it (spec v2 V2-5).
 * There is no edit control anywhere in this component and there is no route
 * behind one: console_app holds SELECT and only SELECT in schema brain.
 */
function BrainGrid({ has, onError }: { has: (s: string) => boolean; onError: (e: unknown) => void }) {
  const [items, setItems] = useState<BrainItem[]>([]);
  const [search, setSearch] = useState('');
  const allowed = has(SCOPE.brainRead);

  useEffect(() => {
    if (!allowed) return;
    const t = setTimeout(() => {
      api.brain(search).then((r) => setItems(r.items)).catch(onError);
    }, 200);
    return () => clearTimeout(t);
  }, [search, allowed, onError]);

  if (!allowed) {
    return (
      <div className="notice scope">
        <h2>Your session cannot read AI Brain content</h2>
        <p style={{ margin: 0 }}>
          It needs <code>console.brain:read</code>. What you would see is only ever what the brain
          itself would serve you — the console cannot widen it.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>Content Items</h1>
          <p>
            Read-only. Filtered by the brain's own rule — status, the tier ladder, audience, and
            whose it is — not by console grants.
          </p>
        </div>
        <div className="spacer" />
        <input
          className="action"
          placeholder="Search title and summary"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: '18rem' }}
        />
      </div>
      <div className="grid-wrap">
        <table className="grid" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Title</th><th>Summary</th><th>Brain</th><th>Tier</th>
              <th>Audience</th><th>Status</th><th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr className="brainrow" key={i.id}>
                <td style={{ fontWeight: 600, maxWidth: '22rem' }}>{i.title}</td>
                <td style={{ maxWidth: '38rem' }}>{i.summary}</td>
                <td className="tier">{i.brain_slug}</td>
                <td className="tier">{i.access_tier}</td>
                <td className="tier">{i.audience}</td>
                <td className="tier">{i.status}</td>
                <td className="tier">{fmtTime(i.updated_at)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="muted" style={{ padding: 16 }}>Nothing to show.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TableGrid({
  table, wsSlug, baseSlug, openRow, me, has, onError, onDescribe,
}: {
  table: TableSummary;
  /** For row links only (C1c-13) — a table summary does not carry its parents. */
  wsSlug: string;
  baseSlug: string;
  /** A row named in the address bar, to expand once the page has loaded. */
  openRow: string | null;
  me: Me;
  has: (s: string) => boolean;
  onError: (e: unknown) => void;
  onDescribe: (t: TableSummary) => void;
}) {
  const [page, setPage] = useState<RecordPage | null>(null);
  const [trash, setTrash] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [addingField, setAddingField] = useState<{ at?: number } | null>(null);

  // C1c: the field editor bundle, the field Trash, the column header menu, the
  // long-text expand modal, and the search box. Each is one piece of state and
  // none of them is a permission — every action behind them is refused by the
  // server and by the database whether or not its control is on screen.
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldTrash, setFieldTrash] = useState(false);
  const [headerMenu, setHeaderMenu] = useState<string | null>(null);
  const [expandCell, setExpandCell] = useState<{ rowId: string; slug: string } | null>(null);
  /* C2b-9. The rich text document modal — the expand panel IS the editor. */
  const [docCell, setDocCell] = useState<{ rowId: string; slug: string } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ rowId: string; slug: string } | null>(null);

  /**
   * THIS PERSON'S SETTINGS — C2-13. Read here rather than threaded in, because
   * five separate things in this component want one of them: the page size, the
   * row height, the drag sensitivity, the trash confirmation, and which view a
   * table opens in.
   */
  const settings = useSettings();
  const confirmBeforeTrash = settingValue<boolean>(settings, 'confirm_before_trash', true);

  /**
   * ROW HEIGHT — C1c-10.
   *
   * TWO SOURCES, IN THIS ORDER, and both exist on purpose. A SAVED VIEW pins a
   * height the same way it pins a sort and a set of widths; a person's
   * `row_height_default` is what a table with no saved view opens at. Airtable
   * has both, for the same reason: the preference belongs to the person and the
   * view belongs to the data.
   */
  const [rowHeight, setRowHeight] = useState<RowHeight | null>(null);
  const effectiveRowHeight: RowHeight =
    rowHeight ?? settingValue<RowHeight>(settings, 'row_height_default', 'short');

  // C2-A. The two things a link cell can open, held here rather than in the
  // cell: a modal owned by a <td> would be unmounted the moment the grid
  // reloaded under it, which is exactly what linking a record does.
  const [picking, setPicking] = useState<{ rowId: string; fieldId: string } | null>(null);
  const [openLink, setOpenLink] = useState<{ tableId: string; recordId: string } | null>(null);
  /* C2b-A. The popover and the expand panel, owned here for the same reason the
   * link modals are: a cell renders, the grid decides what a click opens. */
  const [attaching, setAttaching] = useState<{ rowId: string; fieldId: string } | null>(null);
  const [expanding, setExpanding] = useState<{ rowId: string; fieldId: string } | null>(null);

  /**
   * N-15 — WHAT THE SWAP SAYS AFTER IT HAPPENS, and the undo that makes Matt's
   * ruling (a) safe.
   *
   * With `limit to one` on, picking a second record REPLACES the first. Matt
   * ruled the behaviour stays — forcing remove-then-add is two steps for one
   * intention every time, and a swap is what a one-item control does everywhere
   * else. The defect was never the swapping. It was the silence: a chip
   * disappeared with no prompt and no message, which is build-craft §2 with the
   * loss on the other foot.
   *
   * THE UNDO IS ONE FLAG, NOT A SECOND MECHANISM. The displaced link was SOFT
   * deleted — "never hard delete" paying for itself — and writeLinkCell()'s
   * insert carries `on conflict … do update set deleted_at = null`, so asking
   * for the old id back through the ordinary PATCH clears the flag on the row
   * that is already there. There is no undo path; there is the same path.
   */
  const [swapped, setSwapped] =
    useState<{ rowId: string; fieldId: string; from: LinkCell; toTitle: string } | null>(null);

  // Matt, 2026-08-28: "I would like to be able to click away anywhere on the
  // screen too in order to unselect a cell."
  //
  // A selected cell used to survive until you clicked something the grid itself
  // owned, so it sat there highlighted while you worked elsewhere, looking like
  // the thing your next keystroke would land in. Anything outside a grid cell
  // now clears it. Bound to mousedown rather than click so it lands before a
  // new cell's own handler, and capture so a stopPropagation inside a cell
  // cannot quietly keep the old selection alive.
  useEffect(() => {
    if (!selectedCell) return;
    function away(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (t?.closest('td, th')) return;   // a click on another cell selects it instead
      setSelectedCell(null);
    }
    window.addEventListener('mousedown', away, true);
    return () => window.removeEventListener('mousedown', away, true);
  }, [selectedCell]);
  const [search, setSearch] = useState('');
  const [searchBox, setSearchBox] = useState('');
  const [showBuiltIns, setShowBuiltIns] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  // Where to float the "copied" confirmation. Matt copied a row link and could
  // not tell whether anything had happened — the only feedback lived on a button
  // inside the expand panel, which is not where the grid's link icon is.
  const [copiedAt, setCopiedAt] = useState<{ x: number; y: number } | null>(null);
  const [confirmTrash, setConfirmTrash] = useState<Field | null>(null);
  const canRead = has(SCOPE.recordsRead);
  const canWrite = has(SCOPE.recordsWrite);
  const canBuild = has(SCOPE.schemaWrite);

  // The view: sort, filter, hidden columns, widths. Held here and sent with the
  // request; saving it is a separate act, so experimenting with a sort does not
  // silently rewrite a saved view.
  const [sort, setSort] = useState<SortRule[]>([]);
  const [filter, setFilter] = useState<FilterRule[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [views, setViews] = useState<SavedView[]>([]);
  const [activeView, setActiveView] = useState<string | null>(null);
  const [savingView, setSavingView] = useState(false);
  const [editingFilter, setEditingFilter] = useState(false);
  const [editingSort, setEditingSort] = useState(false);

  // Bulk edit and CSV export (spec v1 S6). The affordances are shown to the
  // Owner Admin, which is what the spec asks for. The SERVER does not care about
  // the role — bulk edit needs console.records:write, the same scope one edit
  // needs, and the RLS policy decides which of the named rows actually change.
  // Restricting the buttons is a product decision; the boundary is elsewhere.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /** Bumped by load(); the total count effect watches it. See load(). */
  const [rowsNonce, setRowsNonce] = useState(0);
  /** True while the ghost row is turning itself into a real one. */
  const [ghostBusy, setGhostBusy] = useState(false);

  /**
   * DUPLICATE A ROW — C1c-14.
   *
   * The copy lands directly below the original, the grid reloads, and the panel
   * moves to the NEW row: making a copy and then editing it is the whole reason
   * anybody duplicates anything, and leaving the panel on the original would
   * mean the next thing typed went into the wrong record.
   *
   * `links_not_copied` is said out loud. A link cell can hold rows this person
   * cannot read; those are not copied, because the write path refuses to link
   * blind. Letting the copy be quietly thinner than the original is the kind of
   * silent difference nobody finds until it matters.
   */
  const [duplicating, setDuplicating] = useState(false);

  async function duplicateRow(rowId: string): Promise<void> {
    if (duplicating) return;
    setDuplicating(true);
    try {
      const r = await api.duplicateRecord(table.id, rowId);
      await load();
      setExpanded(r.record.id);
      if (r.links_not_copied > 0) {
        onError(new Error(
          `The copy was made. ${r.links_not_copied} linked record${r.links_not_copied === 1 ? '' : 's'} `
          + 'could not be copied because your access does not include them.',
        ));
      }
    } catch (e) {
      onError(e);
    } finally {
      setDuplicating(false);
    }
  }
  /**
   * The cell to put the cursor in once a ghost-row create has RENDERED.
   *
   * An effect and not a requestAnimationFrame. The first version used rAF and
   * the cell was focused but EMPTY: React 18 batches, so the render that adds
   * the row had not been committed when the rAF callback ran, the querySelector
   * found nothing, and the focus arrived later from somewhere else. An effect
   * keyed on `rows` runs after the DOM exists, which is the only moment the
   * element can be found. Caught by typing into it in a browser and watching
   * the text go nowhere.
   */
  const [focusAfterCreate, setFocusAfterCreate] =
    useState<{ rowId: string; slug: string } | null>(null);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  /**
   * THE TRASH CONFIRMATION — Matt, 2026-08-29, first browser pass.
   *
   * Trashing used to be a grey icon on every row: one click, no confirmation, no
   * undo in front of you. It is now a red button that only exists once rows are
   * SELECTED, and it asks first. `null` when nothing is being confirmed;
   * otherwise which direction is being confirmed, because the same selection can
   * hold live rows and trashed ones and they are opposite acts.
   */
  const [confirmBin, setConfirmBin] = useState<'trash' | 'restore' | null>(null);
  const [binBusy, setBinBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isOwnerAdmin = me.role === 'owner_admin';

  /**
   * THE PAGER — C2-5, and it is LOAD-MORE because Matt's ruling R5 says so:
   * "PAGER = LOAD-MORE on the keyset cursor, with a total row count. NO numbered
   * page jumps (they need OFFSET)."
   *
   * WHY NOT OFFSET, MEASURED RATHER THAN ASSUMED (PERF-5): an offset walks every
   * row it is told to skip, so page 500 of a large table times out while page 1
   * is instant. The keyset cursor is flat at every depth — on production's
   * 99,976-row table the index scan touched 98 / 107 / 108 buffers at 0 / 50,000
   * / 99,800 rows deep.
   *
   * LOAD-MORE APPENDS. `rows` is what is on screen and grows; `cursor` is where
   * the next page starts; `done` is the server saying there is no next page, by
   * returning a null nextCursor rather than by arithmetic on the total (with a
   * capped total the arithmetic cannot know).
   *
   * EVERY CURSOR IS BOUND TO THE SORT IT WAS ISSUED UNDER (EX-4), so all of this
   * is thrown away whenever the listing changes — sort, filter, search, trash.
   * Not doing so would send a cursor from the old ordering and get a refusal,
   * which is the server correctly telling the browser it made a mistake.
   */
  /**
   * HOW BIG A PAGE IS — `page_size`, C2-13.
   *
   * It was a constant here reading 100, which is REQ-1's complaint exactly: a
   * number only the person who wrote the file could change. It now comes from
   * the catalogue, whose default is 200 — the API's own default, and the number
   * the count cap and the cursor were designed around. THAT IS A DELIBERATE
   * CHANGE OF BEHAVIOUR, from 100 rows a click to 200, and it is written down
   * here rather than left to be noticed: nothing else about the pager moves, and
   * anybody who wants 100 can now say so without editing source.
   *
   * `settings` is null until the first request answers, and the fallback here is
   * the OLD constant rather than a guess at the catalogue's value — a wrong
   * fallback would be a second copy of the default, drifting the first time a
   * migration changes one.
   */
  const PAGE_SIZE = settingValue<number>(settings, 'page_size', 100);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [count, setCount] = useState<RecordCount | null>(null);

  const load = useCallback(() => {
    if (!canRead) return;
    // The selection is cleared on every reload. Keeping ids across a re-read
    // would let a bulk edit act on rows that are no longer on screen — the
    // filter may have changed under it — and "it edited something I could not
    // see" is the worst thing this feature could do.
    setPicked(new Set());
    /*
      AND THE TOTAL IS RE-COUNTED — found by deleting a row in a browser on
      2026-08-29 and reading the footer, which said "14 of 15 — all of them".
      Both halves of that were true and together they were a lie.

      The count lives in its own effect, keyed on the things that change WHICH
      ROWS ARE IN THE LISTING — table, trash, filter, search. Nothing in that
      list changes when a row is created, trashed or restored, so the number
      beside the rows was stale after every mutation. PRE-EXISTING: the old
      per-row trash icon and New row had it too. It survived because no test can
      see a footer and nobody had looked.

      A nonce rather than a dependency on `rows`: the count must be re-asked when
      the row SET changes, not when its contents do, and re-counting on every
      cell edit would pay 105 ms on a big table to get the same answer back.
    */
    setRowsNonce((n) => n + 1);
    api.records(table.id, trash, { sort, filter, search }, null, PAGE_SIZE)
      .then((p) => { setPage(p); setRows(p.records); setCursor(p.nextCursor); })
      .catch(onError);
  }, [table.id, trash, canRead, onError, sort, filter, search]);

  useEffect(load, [load]);

  // See focusAfterCreate. Keyed on `rows` because that is what puts the cell in
  // the DOM; it clears itself so a later reload cannot re-steal the cursor.
  useEffect(() => {
    if (!focusAfterCreate) return;
    /*
      WAIT FOR THE ROW TO ARRIVE. This effect runs the moment focusAfterCreate is
      set — which is BEFORE load()'s response comes back — so the first version
      looked for a cell that did not exist yet, found nothing, and then cleared
      its own target. By the time the rows arrived there was nothing left to
      focus, and the cursor sat on the ghost row while the person typed into
      thin air. Seen in a browser, twice, in two different disguises.

      So it does nothing at all until the row is in `rows`; the render that puts
      it there re-runs this effect, and only then is there an element to find.
    */
    if (!rows.some((r) => String(r['id']) === focusAfterCreate.rowId)) return;
    const sel = `td[data-row="${focusAfterCreate.rowId}"][data-field="${focusAfterCreate.slug}"]`;
    const el = document.querySelector<HTMLElement>(
      `${sel} input, ${sel} select, ${sel} textarea, ${sel} button`);
    // The row is present, so a missing element means a cell type with nothing
    // focusable in it — nothing to do, and not a failure worth reporting.
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
      el.focus();
    }
    setFocusAfterCreate(null);
  }, [focusAfterCreate, rows]);


  /**
   * THE TOTAL, ASKED ONCE PER LISTING. Its own route (C2-5) and its own effect,
   * with the same dependencies as the listing and NOT the cursor: load-more does
   * not change how many rows there are, so counting again on every page would
   * pay 105 ms for an answer we already have.
   *
   * A count that fails is not an error banner. The rows are on screen and
   * useful; the number beside them is a convenience, and turning a slow count
   * into a modal would be the tail wagging the dog.
   */
  useEffect(() => {
    if (!canRead) return;
    let live = true;
    setCount(null);
    api.recordCount(table.id, trash, { filter, search })
      .then((c) => { if (live) setCount(c); })
      .catch(() => { if (live) setCount(null); });
    return () => { live = false; };
  }, [table.id, trash, canRead, filter, search, rowsNonce]);

  /** One more page, appended. The cursor the server issued, echoed back. */
  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    api.records(table.id, trash, { sort, filter, search }, cursor, PAGE_SIZE)
      .then((p) => {
        // APPEND, and de-duplicate by id. The cursor cannot repeat a row on a
        // total ordering — `id` is in every ordering for exactly that reason —
        // but a row edited between two pages can move, and showing it twice
        // would be a grid that disagrees with itself in front of somebody.
        setRows((prev) => {
          const seen = new Set(prev.map((r) => String(r['id'])));
          return [...prev, ...p.records.filter((r) => !seen.has(String(r['id'])))];
        });
        setCursor(p.nextCursor);
        // The METADATA travels too: a field renamed or added between pages
        // should not leave the header describing the previous shape.
        setPage(p);
      })
      .catch(onError)
      .finally(() => setLoadingMore(false));
  }, [table.id, trash, sort, filter, search, cursor, loadingMore, onError]);

  /**
   * A row named in the address bar opens as soon as its page has arrived.
   *
   * Only if it is actually on the page. A row that is trashed, or filtered out
   * by a saved view, or simply beyond the pages loaded so far, is not there to
   * expand —
   * and silently opening an empty panel would be a worse answer than the table
   * itself, which is what the person gets.
   */
  useEffect(() => {
    if (!openRow) return;
    if (rows.some((r) => String(r['id']) === openRow)) setExpanded(openRow);
  }, [openRow, rows]);

  /**
   * The search box, debounced.
   *
   * Every keystroke is a round trip to a Postgres full-text index, which is
   * cheap but is not free, and a request per character arrives out of order.
   * 250ms is the pause that reads as "it is searching" rather than as lag.
   */
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchBox), 250);
    return () => clearTimeout(t);
  }, [searchBox]);

  const loadViews = useCallback(() => {
    if (!canRead) return;
    api.views(table.id).then((r) => setViews(r.views)).catch(onError);
  }, [table.id, canRead, onError]);

  useEffect(loadViews, [loadViews]);

  /**
   * WHICH VIEW A TABLE OPENS IN — `open_last_view` (C2-13).
   *
   * OFF (the default): the table's own default view, if it has one. Nothing has
   * ever applied `is_default` — the column has been loaded and ignored since C1,
   * so a view marked as the default behaved exactly like one that was not.
   * ON: the view you last chose on THIS table, and the default if you have not
   * chosen one.
   *
   * "LAST USED" LIVES IN localStorage AND NOT IN THE DATABASE, deliberately. It
   * is a fact about this browser, not about this person — the same account on a
   * phone and on a desktop wants different answers, and a round trip to write it
   * on every view click would be a write per glance. It is also disposable: a
   * private window, cleared site data, or a different machine simply falls back
   * to the default, which is the same behaviour as never having chosen.
   *
   * ONCE PER TABLE. The ref is what stops this fighting the person — without it,
   * every reload of the view list would re-apply the remembered view over
   * whatever they had just changed by hand.
   */
  const openedView = useRef<string | null>(null);
  useEffect(() => {
    if (views.length === 0 || openedView.current === table.id) return;
    openedView.current = table.id;
    const openLast = settingValue<boolean>(settings, 'open_last_view', false);
    let remembered: string | null = null;
    if (openLast) {
      try { remembered = window.localStorage.getItem(`console.lastView.${table.id}`); } catch { /* see above */ }
    }
    // `is_default` IS SCOPED TO THIS PERSON — 0032. Nothing writes that column
    // today, so this is inert either way; what changed is that the list now
    // contains other people's views, and the moment anything does write it, an
    // unscoped lookup would open a COLLEAGUE'S default view on your screen and
    // there would be nothing to tell you why. A remembered view is already
    // per-person: it comes out of this browser's localStorage.
    const chosen = (remembered && views.find((v) => v.id === remembered))
      ?? views.find((v) => v.is_default
        && (!v.identity_id || v.identity_id === me.identityId));
    if (chosen) applyView(chosen);
    // applyView is a plain function on every render and listing it here would
    // re-run this on each one; the ref is what actually bounds it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, table.id, settings]);

  const allFields = useMemo(() => page?.table.fields ?? [], [page]);
  const fields = useMemo(
    () => allFields.filter((f) => !hidden.includes(f.slug)),
    [allFields, hidden],
  );

  /* =========================================================================
   * MANUAL ROW ORDER AND COLUMN DRAGGING — C2-8, C2-9, C2-10.
   *
   * Both use the ONE pointer-event primitive in ./drag.ts. Rows slide aside;
   * columns show an insertion line — two presentations, one set of numbers, and
   * no HTML5 drag-and-drop anywhere. See that file's header for the four
   * attempts that bought this.
   * ====================================================================== */

  /**
   * MANUAL MODE IS A SORT, and dragging is enabled only in it (Matt's ruling
   * R6, and Airtable's model). The server agrees: `manual` is an allow-listed
   * built-in sort slug resolving to the `position` column migration 0030 added,
   * so the cursor, the export and the count all inherit it with no second
   * implementation.
   *
   * `sort.length === 1` deliberately: a manual order combined with another sort
   * is not a manual order, it is that other sort with a tiebreaker, and letting
   * somebody drag a row inside it would move the row and change nothing on
   * screen — a change that works and looks broken, which is the failure this
   * project keeps writing down.
   */
  const manualOrder = sort.length === 1 && sort[0]!.field === 'manual';

  /**
   * The row order this browser is showing, when it is ahead of the server.
   *
   * THE LIST MOVES IMMEDIATELY. Firing the request and waiting for the reload is
   * what made the choice reorder look broken on 2026-08-28: Matt dragged, saw
   * the row settle back, concluded nothing had happened, and dragged it back —
   * two successful reorders three seconds apart whose net effect was zero. On a
   * refusal this is dropped and the server's order wins again, so a refusal
   * shows the truth rather than a lie that happens to be on screen.
   */
  const [rowOrder, setRowOrder] = useState<string[] | null>(null);
  const shownRows = useMemo(() => {
    if (!rowOrder) return rows;
    const byId = new Map(rows.map((r) => [String(r['id']), r]));
    const out = rowOrder.map((id) => byId.get(id)).filter((r): r is Record<string, unknown> => !!r);
    // Anything the optimistic order does not know about — a row that arrived
    // with the reload — is appended rather than dropped. Losing a row off the
    // screen would be a far worse bug than the one this is fixing.
    for (const r of rows) if (!rowOrder.includes(String(r['id']))) out.push(r);
    return out;
  }, [rows, rowOrder]);

  // Once the server's order matches, the server is the truth again.
  useEffect(() => {
    if (!rowOrder) return;
    if (rows.map((r) => String(r['id'])).join() === rowOrder.join()) setRowOrder(null);
  }, [rows, rowOrder]);

  /**
   * P7(4), SECOND HALF — Matt, 2026-08-29: the grid "opens horizontally offset
   * with the first column cut in half".
   *
   * TableGrid is keyed on the table id, so a fresh table is a fresh mount and
   * the scroll box starts at zero by construction — which is exactly why this
   * looked impossible until you watch what happens next. Something inside the
   * first paint scrolls it: a focus landing in a cell, or the browser restoring
   * a position for a box it thinks it has seen before. Rather than hunt the
   * culprit, the promise is made true where it is made: the grid opens at the
   * left edge, every time, and anything that disagrees is overruled one frame
   * later.
   */
  const gridWrap = useRef<HTMLDivElement | null>(null);
  const wantsLeftEdge = useRef(true);
  useLayoutEffect(() => { wantsLeftEdge.current = true; }, [table.id]);
  useLayoutEffect(() => {
    // ON THE FIRST ROWS, NOT ON MOUNT, and that is the whole fix. Resetting at
    // mount was tried and did not work: the table is EMPTY at mount, so the
    // scroll box has nothing to scroll and the position it is being reset from
    // does not exist yet. Chrome restores the previous scrollLeft when the
    // content arrives — which is after mount, after the effect, and after the
    // frame that followed it. So the reset waits for the content it is about.
    if (!wantsLeftEdge.current || rows.length === 0) return;
    const el = gridWrap.current;
    if (!el) return;
    el.scrollLeft = 0;
    wantsLeftEdge.current = false;
  }, [rows, table.id]);

  /**
   * WHETHER THERE IS MORE TABLE OFF EITHER EDGE — C2-14 (UI-6).
   *
   * Measured, never assumed: the alternative is comparing a column count to a
   * window width, which is wrong the moment a column is resized, hidden, or
   * holds a long value. `scrollLeft` and the two widths are what the box itself
   * knows.
   *
   * The 1px tolerance is not superstition. Scroll positions are fractional on a
   * scaled display, and `scrollLeft === scrollWidth - clientWidth` is false by a
   * fraction of a pixel at the true right-hand end — which would leave a fade
   * saying "there is more" when there is not.
   */
  const [moreLeft, setMoreLeft] = useState(false);
  const [moreRight, setMoreRight] = useState(false);

  const measureEdges = useCallback(() => {
    const el = gridWrap.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setMoreLeft(el.scrollLeft > 1);
    setMoreRight(max > 1 && el.scrollLeft < max - 1);
  }, []);

  useLayoutEffect(measureEdges);

  useEffect(() => {
    const el = gridWrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // The window resizing is one way the answer changes; the TABLE changing size
    // under a still window is the other, and only the observer sees that one.
    const ro = new ResizeObserver(measureEdges);
    ro.observe(el);
    window.addEventListener('resize', measureEdges);
    return () => { ro.disconnect(); window.removeEventListener('resize', measureEdges); };
  }, [measureEdges]);

  /* ===========================================================================
   * KEYBOARD NAVIGATION — C2-11 / C1c-7
   * ===========================================================================
   *
   * C1c shipped ONE binding, Shift+Space, and deferred the rest until there were
   * real rows to navigate. There are now.
   *
   * THE DIFFICULTY, AND WHY THE ARROW RULES ARE NOT "ARROWS MOVE THE CELL". This
   * grid has no separate edit mode: every cell renders its own live control, and
   * clicking one puts the caret in it. So a left arrow is genuinely ambiguous —
   * it means "move the caret" to somebody part-way through a word and "move to
   * the previous column" to somebody who has just arrived.
   *
   * The rule that resolves it is the one every spreadsheet uses, and it is not a
   * guess about intent but a reading of where the caret actually is:
   *
   *   ← / →   move the cell ONLY when the caret is already at that end of the
   *           value, with nothing selected. Otherwise the caret moves, which is
   *           what the person is plainly doing.
   *   ↑ / ↓   always move the cell, EXCEPT in a textarea, where up and down are
   *           how you move between the lines you can see.
   *   Enter   commits, through the control's own blur handler. It does NOT move
   *           down: that was built, driven, found not to work through the
   *           reload a commit causes, and removed rather than half-shipped —
   *           C1c-7 asks for "Enter edits", not for a spreadsheet's Enter.
   *   Escape  puts the value back to what it was and leaves the cell — the one
   *           binding here that undoes rather than navigates. It reads a REF,
   *           not state; see Cell, where the first version saved instead.
   *   Tab     is left entirely alone. The controls are in document order, so the
   *           browser already moves along the row and on to the next one, and
   *           the focus handler selects whatever it lands on.
   *
   * MODIFIERS ARE LEFT ALONE. Cmd+← is "start of line" on a Mac and Alt+↑ is a
   * word jump in some inputs; a grid that swallowed those would be taking
   * bindings that belong to the text box.
   */
  /**
   * WHERE THE CURSOR SHOULD BE ONCE THE GRID HAS FINISHED RE-RENDERING.
   *
   * Enter and Tab both COMMIT before they move, and a commit reloads the
   * listing — so the focus they place is put on a DOM node that the reload
   * replaces a moment later. That is exactly why the first Enter-moves-down
   * committed correctly and did not move: focusCell() ran, did the right thing,
   * and the re-render undid it with nothing on screen to say so.
   *
   * So the move is made TWICE, on purpose. Once immediately, which is what
   * happens when nothing changed and no reload follows; and once from an effect
   * keyed on `rows`, which is the only moment the replacement node exists. The
   * ref is cleared as it is used, so a reload for any other reason does not
   * drag the cursor around.
   *
   * The same shape as the ghost row's focusAfterCreate above, and for the same
   * reason — an effect, never a requestAnimationFrame, because React batches and
   * the node does not exist yet when the frame runs.
   */
  const pendingFocus = useRef<{ rowId: string; slug: string } | null>(null);

  const focusCell = useCallback((rowId: string, slug: string): void => {
    const esc = (v: string): string =>
      (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&');
    const td = gridWrap.current?.querySelector<HTMLElement>(
      `td[data-row="${esc(rowId)}"][data-field="${esc(slug)}"]`,
    );
    if (!td) return;
    // The control if the cell has one — a link cell has chips and no input — and
    // the cell itself otherwise, which is why every data cell carries tabIndex.
    const control = td.querySelector<HTMLElement>('input, select, textarea');
    (control ?? td).focus();
    // Asked for again after the next render, in case a commit is reloading the
    // listing underneath this. See pendingFocus above.
    pendingFocus.current = { rowId, slug };
    // `nearest` on both axes: scrolling a frozen first column into view
    // horizontally when it is already there would jerk the whole grid sideways.
    td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    setSelectedCell({ rowId, slug });
  }, []);

  /**
   * Move the selection by whole cells. Clamped rather than wrapped: running off
   * the last column onto the first cell of the next row is a spreadsheet
   * behaviour people rely on for Tab and find startling on an arrow.
   */
  const moveSelection = useCallback((dRow: number, dCol: number): boolean => {
    const sel = selectedCell;
    if (!sel) return false;
    const rowIds = shownRows.map((r) => String(r['id']));
    const slugs = fields.map((f) => f.slug);
    const ri = rowIds.indexOf(sel.rowId);
    const ci = slugs.indexOf(sel.slug);
    if (ri < 0 || ci < 0) return false;
    const nr = Math.min(rowIds.length - 1, Math.max(0, ri + dRow));
    const nc = Math.min(slugs.length - 1, Math.max(0, ci + dCol));
    if (nr === ri && nc === ci) return false;
    focusCell(rowIds[nr]!, slugs[nc]!);
    return true;
  }, [selectedCell, shownRows, fields, focusCell]);

  /**
   * DOWN A ROW AND BACK TO THE TITLE — Matt's ruling, 2026-09-03.
   *
   * "I want it to move the selector down one row and to the title cell of that
   * row." That is what Enter does, and it is where Tab lands when it runs out of
   * columns. It is the shape of entering records rather than of reading them:
   * you finish a row and start the next one at its beginning.
   *
   * THE TITLE IS THE FIRST VISIBLE COLUMN, not a field named 'title'. The
   * primary field is pinned to position 1 by migration 0021 and IS the title,
   * but it can be hidden — and a cursor sent to a column nobody can see is a
   * cursor that has vanished.
   */
  const moveToNextRowTitle = useCallback((): boolean => {
    const sel = selectedCell;
    const first = fields[0];
    if (!sel || !first) return false;
    const rowIds = shownRows.map((r) => String(r['id']));
    const ri = rowIds.indexOf(sel.rowId);
    if (ri < 0 || ri + 1 >= rowIds.length) return false;   // clamped at the last row
    focusCell(rowIds[ri + 1]!, first.slug);
    return true;
  }, [selectedCell, shownRows, fields, focusCell]);

  /**
   * TAB — Matt's ruling, 2026-09-03: "move the selector to the right across the
   * row the selected cell is on. It just goes to the next editable cell to the
   * right. If it's out of cells, it goes down to the row below and to the title
   * cell of that row."
   *
   * `fields` IS the list of editable columns: Created and Updated are not in it
   * (they are the console's own, rendered read-only outside this loop), and a
   * hidden column is not in it either. So "the next editable cell to the right"
   * is the next entry, and there is no separate skip rule to keep in step with
   * the rendering.
   *
   * SHIFT+TAB IS THE MIRROR, which Matt did not ask for and which would be a
   * surprise by its absence: having taken Tab from the browser, leaving the
   * browser's Shift+Tab would send the cursor somewhere neither of us chose.
   */
  const moveTab = useCallback((back: boolean): boolean => {
    const sel = selectedCell;
    if (!sel) return false;
    const rowIds = shownRows.map((r) => String(r['id']));
    const slugs = fields.map((f) => f.slug);
    const ri = rowIds.indexOf(sel.rowId);
    const ci = slugs.indexOf(sel.slug);
    if (ri < 0 || ci < 0) return false;

    if (!back) {
      if (ci + 1 < slugs.length) { focusCell(rowIds[ri]!, slugs[ci + 1]!); return true; }
      return moveToNextRowTitle();
    }
    if (ci > 0) { focusCell(rowIds[ri]!, slugs[ci - 1]!); return true; }
    // Off the front of a row goes to the END of the one above — the mirror of
    // running out of columns forwards.
    if (ri === 0) return false;
    focusCell(rowIds[ri - 1]!, slugs[slugs.length - 1]!);
    return true;
  }, [selectedCell, shownRows, fields, focusCell, moveToNextRowTitle]);

  /**
   * PUT THE CURSOR BACK AFTER A COMMIT RELOADED THE LISTING — see pendingFocus.
   *
   * Enter and Tab both blur before they move, and a blur that changed something
   * commits, and a commit calls load(). So the node focusCell() just focused is
   * replaced a moment later and the cursor is gone, which is precisely how the
   * first Enter-moves-down looked like it did nothing at all.
   *
   * Cleared as it is used, so a reload for any OTHER reason — a save from the
   * expand panel, a link written from a picker — does not move somebody's
   * cursor. `rows` is the key because that is what the reload replaces.
   */
  useEffect(() => {
    const want = pendingFocus.current;
    if (!want) return;
    if (!rows.some((r) => String(r['id']) === want.rowId)) return;
    pendingFocus.current = null;
    focusCell(want.rowId, want.slug);
    // focusCell arms it again for the same cell; clear that, or the NEXT reload
    // for any reason would drag the cursor back here.
    pendingFocus.current = null;
  }, [rows, focusCell]);

  /** Whether a left/right arrow in this control should move the cell or the caret. */
  function caretAtEdge(el: EventTarget | null, side: 'start' | 'end'): boolean {
    const t = el as HTMLInputElement | HTMLTextAreaElement | null;
    if (!t || typeof t.selectionStart !== 'number' || typeof t.selectionEnd !== 'number') {
      // A select, a checkbox, or the cell itself: there is no caret to protect.
      return true;
    }
    if (t.selectionStart !== t.selectionEnd) return false;   // something is selected
    return side === 'start'
      ? t.selectionStart === 0
      : t.selectionEnd === (t.value ?? '').length;
  }

  const onCellKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLTableCellElement>,
    rowId: string,
    field: Field,
  ): void => {
    if (e.altKey || e.metaKey || e.ctrlKey) return;

    // Shift+Space expands a long-text cell — the binding C1c shipped, kept
    // exactly as it was, and it is the reason this check comes first.
    if (e.shiftKey && e.key === ' ' && field.type === 'long_text') {
      e.preventDefault();
      setExpandCell({ rowId, slug: field.slug });
      return;
    }
    // Shift is otherwise left alone — Shift+Arrow is a text selection — but
    // Shift+Tab is a binding of its own and is handled below.
    if (e.shiftKey && e.key !== 'Tab') return;

    const inTextarea = (e.target as HTMLElement | null)?.tagName === 'TEXTAREA';

    switch (e.key) {
      case 'ArrowUp':
        if (inTextarea) return;
        if (moveSelection(-1, 0)) e.preventDefault();
        return;
      case 'ArrowDown':
        if (inTextarea) return;
        if (moveSelection(1, 0)) e.preventDefault();
        return;
      case 'ArrowLeft':
        if (!caretAtEdge(e.target, 'start')) return;
        if (moveSelection(0, -1)) e.preventDefault();
        return;
      case 'ArrowRight':
        if (!caretAtEdge(e.target, 'end')) return;
        if (moveSelection(0, 1)) e.preventDefault();
        return;
      case 'Enter':
        // DOWN A ROW, TO THE TITLE — Matt's ruling of 2026-09-03. It commits
        // first, through the control's own blur, and then moves.
        if (inTextarea) return;   // a newline in a long text cell is a newline
        e.preventDefault();
        (e.target as HTMLElement | null)?.blur?.();
        moveToNextRowTitle();
        return;
      case 'Tab':
        // TAKEN FROM THE BROWSER, both ways. Its default order is the controls
        // in the DOM, which is nearly this for one row and then leaves the grid
        // entirely at the end of it.
        e.preventDefault();
        (e.target as HTMLElement | null)?.blur?.();
        moveTab(e.shiftKey);
        return;
      case 'Escape':
        // Handled inside Cell, which is the only thing that knows the value this
        // cell started with. Here it only leaves the control, so the next arrow
        // is a move rather than a caret nudge.
        (e.target as HTMLElement | null)?.blur?.();
        return;
      default:
    }
  }, [moveSelection, moveToNextRowTitle, moveTab]);

  const rowEls = useRef<(HTMLTableRowElement | null)[]>([]);
  const headEls = useRef<(HTMLTableCellElement | null)[]>([]);

  // Leaving manual mode throws the optimistic order away: it is an order for a
  // listing that is no longer on screen.
  useEffect(() => { if (!manualOrder) setRowOrder(null); }, [manualOrder]);

  /**
   * HOW FAR YOU DRAG BEFORE A NEIGHBOUR STEPS ASIDE — `drag_sensitivity`, and
   * this is REQ-1's original instance. Matt: "I won't remember to go to that
   * script to adjust it."
   *
   * The catalogue stores it as a PERCENTAGE because a catalogue of whole numbers
   * with bounds validates itself; the hook wants the fraction it has always
   * wanted, so the division happens here and in one place. THE SPEC CALLS THIS
   * "px threshold", which is a slip in the spec rather than in the code:
   * DRAG_SENSITIVITY has always been a fraction of the neighbour's size, and
   * DRAG_THRESHOLD_PX — the four pixels that separate a click from a drag — is a
   * different constant that nobody asked to make settable.
   *
   * Falls back to the hook's own default rather than to a number written here,
   * so there is exactly one place that says what 0.35 means.
   */
  const dragSensitivity = settings
    ? settingValue<number>(settings, 'drag_sensitivity', 35) / 100
    : undefined;

  /**
   * EDGE AUTO-SCROLL for both drags (drag.ts, Matt 2026-10-02). The grid box is
   * the one thing that scrolls. Its start edge is partly covered by something
   * frozen, and the zone has to begin where the person can SEE: for a column,
   * the right edge of the pinned first column; for a row, the bottom of the
   * sticky header row. Measured at the press, like the rest of the geometry.
   */
  const frozenColumnInset = (): number => {
    const wrap = gridWrap.current;
    const pinned = headEls.current[0];
    if (!wrap || !pinned) return 0;
    return pinned.getBoundingClientRect().right - wrap.getBoundingClientRect().left;
  };
  const stickyHeaderInset = (): number => {
    const wrap = gridWrap.current;
    const head = wrap?.querySelector('thead');
    if (!wrap || !head) return 0;
    return head.getBoundingClientRect().bottom - wrap.getBoundingClientRect().top;
  };

  const rowDrag = usePointerReorder({
    axis: 'y',
    sensitivity: dragSensitivity,
    autoScroll: { scroller: () => gridWrap.current, startInset: stickyHeaderInset },
    onDrop: (from, to) => {
      const ids = shownRows.map((r) => String(r['id']));
      const next = reorder(ids, from, to);
      setRowOrder(next);
      /*
        WHICH ROW IT LANDED AFTER, computed from the NEW order rather than from
        the old one. `to` is a slot in the list as it will be, so the anchor is
        whatever ends up immediately above it — and at the top there is no
        anchor at all, which is what to_top exists for.
      */
      const moved = ids[from];
      const anchorId = next[to - 1];
      if (!moved) return;
      const body: { to_top: true } | { after_row_id: string } =
        to === 0 || !anchorId ? { to_top: true } : { after_row_id: anchorId };
      // NOT through the shared busy helper: that opens with `if (busy) return`,
      // so a second drag while one is in flight would vanish with no request, no
      // error and no sign on screen. This owns its own failure.
      api.moveRecord(table.id, moved, body)
        .then(() => { load(); })
        .catch((e) => { setRowOrder(null); onError(e); });
    },
  });

  /**
   * COLUMN DRAG — C2-10.
   *
   * THE FIRST COLUMN IS THE PRIMARY FIELD AND IT IS PINNED (migration 0021, and
   * Matt's ruling R1 of 2026-08-29). The server answers 409 structural_rule for
   * a move onto position 1 and for a move OF the primary field, and this refuses
   * both HERE rather than letting the server refuse: a column that snaps back
   * with no explanation is the worse failure, and a structural rule is not a
   * permission, so saying it early hides nothing.
   *
   * The clamp is the refusal. The column follows the pointer and simply will not
   * go left of the primary, so the affordance says no before the drop does.
   */
  const columnDrag = usePointerReorder({
    axis: 'x',
    sensitivity: dragSensitivity,
    autoScroll: { scroller: () => gridWrap.current, startInset: frozenColumnInset },
    onDrop: (from, to) => {
      const target = Math.max(1, to);
      const moving = fields[from];
      const onto = fields[target];
      if (!moving || !onto || from === target) return;
      /*
        WHICH SIDE OF `onto` IT LANDS ON. console.move_field (0021) sorts the
        moved field at position - 0.5, i.e. just BEFORE whatever holds that
        position now. That is right for a move to the left. For a move to the
        right it landed one slot short: a column dragged to the last slot ended
        up second-to-last, and a one-step drag to the right did nothing at all
        (found 2026-10-02, the first time a drag went all the way to the end).
        Moving right, aim one past `onto`, which sorts it just after.
      */
      const position = target > from ? onto.position + 1 : onto.position;
      api.moveField({ fieldId: moving.id, position })
        .then(() => { load(); })
        .catch(onError);
    },
  });

  /** Whether a header may be picked up at all: not the pinned primary, and not
   *  without console.schema:write, which is what moving a column needs. */
  const columnDraggable = (index: number): boolean => canBuild && index > 0;

  function applyView(v: SavedView) {
    setActiveView(v.id);
    // Remembered for `open_last_view`. Written whether or not the setting is on:
    // turning it on should not require choosing a view again first.
    try { window.localStorage.setItem(`console.lastView.${table.id}`, v.id); } catch { /* not worth failing over */ }
    setSort(v.sort ?? []);
    setFilter(v.filter ?? []);
    setHidden(v.hidden_fields ?? []);
    setWidths(v.widths ?? {});
    // A view saved before migration 0031 has no height. `null` means "fall back
    // to this person's default", which is the honest answer for a view that
    // never pinned one — rather than pinning 'short' on their behalf.
    setRowHeight(v.row_height ?? null);
  }

  function clearView() {
    setActiveView(null);
    try { window.localStorage.removeItem(`console.lastView.${table.id}`); } catch { /* not worth failing over */ }
    setSort([]); setFilter([]); setHidden([]); setWidths({});
    setRowHeight(null);
  }

  async function applyBulk() {
    if (!bulkField || bulkBusy) return;
    setBulkBusy(true);
    try {
      const field = allFields.find((f) => f.slug === bulkField);
      const value = field ? coerce(field, bulkValue) : (bulkValue || null);
      const r = await api.bulkEdit(table.id, [...picked], bulkField, value);
      // The count is REPORTED, not hidden. A row the caller may not edit is
      // simply not updated, and a silent "done" over a partial result is how a
      // person comes to believe something happened that did not.
      if (r.updated < r.asked) {
        onError(new ApiError({
          kind: 'refused',
          message: `${r.updated} of ${r.asked} rows changed. The rest were not yours to edit.`,
        }));
      }
      setBulkField('');
      setBulkValue('');
      load();
    } catch (e) { onError(e); } finally { setBulkBusy(false); }
  }

  /**
   * MOVE THE SELECTED ROWS TO THE TRASH, OR BRING THEM BACK.
   *
   * ONE REQUEST PER ROW, sequentially, and that is a deliberate choice rather
   * than an oversight: there is no bulk trash route, the pool has eight
   * connections, and firing a hundred PATCHes at once would starve the grid to
   * make a delete look fast. Sequential also means a failure stops where it
   * happened instead of leaving an unknowable half-state.
   *
   * THE COUNT IS REPORTED, the same way applyBulk() reports its own. A row the
   * caller may not edit is refused by the write wall, and a silent "done" over a
   * partial result is how somebody comes to believe a row is gone when it is not.
   */
  /**
   * THE GHOST ROW BECOMES A REAL ONE — Matt, 2026-08-29, second browser pass:
   * "make it work like Airtable where there's always a blank row below the
   * bottom row so we can just click into any field and it creates that record".
   *
   * NO TITLE REQUIRED, which he said explicitly and which the console already
   * agreed with: `New row` has always POSTed `{}`. The row is created empty and
   * the caller types into it afterwards, exactly as the button did — what
   * changes is that the row you get is under your cursor instead of at the top
   * of the table.
   *
   * IT RELOADS AND THEN FINDS THE ROW, rather than appending it locally, and
   * the first version did the opposite. Appending put the row under the cursor
   * where it was asked for — and then the first commit called load(), the
   * listing came back in its real order (`created_at desc` by default), and the
   * row TELEPORTED TO THE TOP WHILE THE PERSON WAS TYPING IN IT. Seen in a
   * browser; nothing else would have shown it.
   *
   * So the row moves ONCE, before anybody types, to wherever the CURRENT SORT
   * actually puts it — which is also correct when a sort or a filter is on, and
   * a local append never could be. The cursor follows it there and the cell is
   * scrolled into view.
   *
   * A NOTE ON WHERE IT LANDS. Airtable's new row stays at the bottom because
   * Airtable's default order is insertion order; this grid's default is newest
   * first, so the row correctly appears at the TOP. Making it land at the bottom
   * is a change to the DEFAULT SORT, which is a product decision and not this
   * function's to make.
   */
  async function addRowFromGhost(slug: string) {
    if (!canWrite || trash || ghostBusy) return;
    setGhostBusy(true);
    try {
      const created = await api.createRecord(table.id, {});
      setFocusAfterCreate({ rowId: String(created.record.id), slug });
      load();
    } catch (e) { onError(e); } finally { setGhostBusy(false); }
  }

  /** Of what is selected, how much is live and how much is already in the trash. */
  const liveCount = [...picked].filter((id) => {
    const r = rows.find((x) => String(x['id']) === id);
    return r && !r['deleted_at'];
  }).length;
  const trashedCount = picked.size - liveCount;

  async function binSelected(dir: 'trash' | 'restore') {
    if (binBusy) return;
    setBinBusy(true);
    const ids = [...picked].filter((id) => {
      const r = rows.find((x) => String(x['id']) === id);
      return dir === 'trash' ? r && !r['deleted_at'] : r && r['deleted_at'];
    });
    let done = 0;
    let failure: unknown = null;
    for (const id of ids) {
      try {
        if (dir === 'trash') await api.trashRecord(table.id, id);
        else await api.restoreRecord(table.id, id);
        done += 1;
      } catch (e) { failure = e; break; }
    }
    setBinBusy(false);
    setConfirmBin(null);
    setPicked(new Set());
    load();
    if (failure) onError(failure);
    else if (done < ids.length) {
      onError(new ApiError({
        kind: 'refused',
        message: `${done} of ${ids.length} rows were moved. The rest were not yours to change.`,
      }));
    }
  }

  /**
   * CSV of everything the current view shows — not of the page on screen, and
   * as of REQ-2 not of the first ten thousand rows either.
   *
   * WHAT THIS USED TO BE, because the change is the point. It looped the records
   * API at 200 rows a time, accumulated every row in this tab's memory, built one
   * string and handed it to a Blob — and stopped at 10,000, writing the
   * truncation into the file. That cap was the honest part: at 100,000 rows the
   * loop is 500 requests and a ~50MB string, and with a sort applied it does not
   * merely crawl, it fails on the server's statement timeout. Removing the
   * number alone would have bought a feature that grinds and then dies.
   *
   * WHAT IT IS NOW. One request to a route that streams the rows out of a
   * server-side cursor as it reads them. The browser writes the response
   * straight to the downloads folder, so nothing accumulates HERE either — no
   * Blob, no string, no ceiling. The view still travels: sort, filter, search,
   * trash and the visible columns all go in the query string, so the file is
   * still "what this view shows".
   *
   * WHY IT IS A LINK AND NOT A fetch(). Fetching it into memory to make a Blob
   * would put the whole table back in the tab and undo the reason for the work.
   * A plain navigation lets the browser stream it to disk.
   */
  function exportCsv() {
    const p = new URLSearchParams();
    if (trash) p.set('trash', '1');
    if (sort.length) p.set('sort', JSON.stringify(sort));
    if (filter.length) p.set('filter', JSON.stringify(filter));
    // The SEARCH travels too. A CSV of "everything the view shows" that quietly
    // ignored the search box would be a file the person believes is their
    // filtered set and is not.
    if (search.trim()) p.set('q', search.trim());
    // The COLUMNS travel, in the order they are on screen, and hidden ones are
    // left out — the file matches the view rather than the table.
    p.set('fields', fields.map((f) => f.slug).join(','));

    // ACKNOWLEDGE THE CLICK. The browser owns the download from here, so this
    // tab never learns when it finished — but a large table can take a while to
    // produce its first byte, and a button that does nothing visible invites a
    // second click and a second full export. Times out rather than waiting for
    // a completion signal that does not exist.
    setExporting(true);
    window.setTimeout(() => setExporting(false), 4000);

    const a = document.createElement('a');
    a.href = `/api/tables/${table.id}/export.csv?${p.toString()}`;
    // The server names the file in Content-Disposition; this is the fallback.
    a.download = `${table.slug || 'table'}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  /** Clicking a header cycles asc → desc → off, and sorts by that column alone. */
  function cycleSort(slug: string) {
    setActiveView(null);
    setSort((cur) => {
      const existing = cur.find((s) => s.field === slug);
      if (!existing) return [{ field: slug, dir: 'asc' }];
      if (existing.dir === 'asc') return [{ field: slug, dir: 'desc' }];
      return [];
    });
  }

  async function save(recordId: string, field: Field, raw: string) {
    const value = coerce(field, raw);
    try {
      await api.patchRecord(table.id, recordId, { [field.slug]: value });
      load();
    } catch (e) { onError(e); }
  }

  /**
   * Set a link cell to exactly these ids (C2-A).
   *
   * The same PATCH every other cell uses — a link is a field of the source row,
   * not a resource of its own — and it RELOADS, because the server may have
   * preserved links this list did not mention (any whose target this person
   * cannot read) and the cell has to show what is actually there rather than
   * what was asked for.
   */
  async function saveLink(recordId: string, field: Field, ids: string[]): Promise<boolean> {
    try {
      await api.patchRecord(table.id, recordId, { [field.slug]: ids });
      load();
      return true;
    } catch (e) { onError(e); return false; }
  }

  /**
   * Duplicate a field: same type and settings, no data (C1c-14, the half
   * Amendment 3 ships). The name is the original's plus " copy", which is what
   * Matt's ruling asks for; the slug is derived and de-duplicated here so the
   * common case does not need a dialog.
   */
  async function duplicateField(f: Field) {
    const taken = new Set(allFields.map((x) => x.slug));
    let slug = slugify(`${f.slug} copy`);
    let n = 2;
    while (taken.has(slug)) slug = slugify(`${f.slug} copy ${n++}`);
    try {
      await api.duplicateField({
        fieldId: f.id, slug, name: `${f.name} copy`, position: f.position + 1,
      });
      load();
    } catch (e) { onError(e); }
  }

  /**
   * "Delete field" — which is a move to the field Trash and never a drop.
   *
   * THE CONFIRMATION IS THE APP'S OWN MODAL, not window.confirm(). Every other
   * question the grid asks is a <Modal>, a native dialog cannot be styled or
   * read by a screen reader the same way, and — the reason it was changed rather
   * than left — a native dialog BLOCKS the page, so the browser session that is
   * supposed to be looking at this build cannot get past one.
   *
   * WHAT IT SAYS is what actually happens, rather than "are you sure?". Nothing
   * is lost: the column and every value in it stay exactly where they are, which
   * is why the field goes on holding its name until it is restored or renamed. A
   * person who knows that answers the question quickly.
   */
  async function trashField(f: Field) {
    setConfirmTrash(f);
  }

  async function reallyTrashField(f: Field) {
    setConfirmTrash(null);
    try {
      await api.archive({ kind: 'field', id: f.id });
      load();
    } catch (e) { onError(e); }
  }

  /**
   * Copy a link to one row (C1c-13).
   *
   * The clipboard API needs a secure context and can be refused, so the fallback
   * is not silence: the link goes into a prompt the person can copy by hand.
   * A "copied!" that copied nothing is the failure mode worth spending six lines
   * to avoid.
   */
  async function copyRowLink(rowId: string, at?: { x: number; y: number }) {
    setCopiedAt(at ?? null);
    const url = new URL(
      rowLink({ workspace: wsSlug, base: baseSlug, table: table.slug, rowId }),
      window.location.origin,
    ).toString();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(rowId);
      setTimeout(() => {
        setCopied((c) => (c === rowId ? null : c));
        setCopiedAt(null);
      }, 1500);
    } catch {
      window.prompt('Copy this link to the row:', url);
    }
  }

  if (!canRead) {
    return (
      <div className="notice scope">
        <h2>Your session cannot read rows</h2>
        <p style={{ margin: 0 }}>It needs <code>console.records:read</code>.</p>
      </div>
    );
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>{table.name}</h1>
          <p>{table.description}</p>
        </div>
        <div className="spacer" />
        {canBuild && (
          <>
            <button className="action" onClick={() => onDescribe(table)} title="Rename this table or edit its description">
              Edit table
            </button>
            <button className="action" onClick={() => setAddingField({})} title="Add a field to this table">
              Add field
            </button>
            <button
              className="action"
              onClick={() => setFieldTrash(true)}
              title="Fields that have been deleted. Their columns and data are still there."
            >
              Field Trash
            </button>
          </>
        )}
        <button className={`action${trash ? ' on' : ''}`} onClick={() => setTrash(!trash)}>
          {trash ? 'Showing trash' : 'Trash'}
        </button>
        <button
          className="action"
          disabled={!canWrite || trash}
          title={canWrite ? '' : 'Your session does not carry console.records:write'}
          onClick={async () => {
            try { await api.createRecord(table.id, {}); load(); } catch (e) { onError(e); }
          }}
        >
          New row
        </button>
      </div>

      {/*
        N-15. THE LINE THAT NAMES BOTH, and it is the whole point of Matt's
        ruling: the swap is kept and the silence is not. It says what was there,
        what is there now, and how to put it back.

        It sits here rather than inside the picker because the picker CLOSES on a
        one-record pick — a message in a dialog that is being dismissed is a
        message nobody reads.
      */}
      {swapped && (() => {
        const f = allFields.find((x) => x.id === swapped.fieldId);
        const was = swapped.from.restricted
          ? 'a record you cannot read'
          : (swapped.from.title?.trim() || '(untitled)');
        return (
          <div className="notice" role="status" aria-live="polite">
            Replaced <strong>{was}</strong> with <strong>{swapped.toTitle}</strong>
            {f ? <> in “{f.name}”</> : null} — it holds one record.{' '}
            <button
              className="linkish"
              disabled={swapped.from.restricted || !f}
              title={swapped.from.restricted
                ? 'The link that was replaced points at a row you cannot read, so it cannot be put back from here.'
                : 'Put the previous link back'}
              onClick={() => {
                if (!f) return;
                // The SAME PATCH, with the old id. writeLinkCell() clears the
                // soft-delete flag on the junction row that is still there
                // rather than writing a second one, so this is an undo in the
                // literal sense — nothing new is created and nothing is
                // resurrected from a copy.
                void saveLink(swapped.rowId, f, [swapped.from.id]);
                setSwapped(null);
              }}
            >
              Undo
            </button>
            <button className="linkish" onClick={() => setSwapped(null)} title="Dismiss">Dismiss</button>
          </div>
        );
      })()}

      <div className="views">
        {views.map((v) => {
          const vis = viewVisibility(v);
          const mine = !v.identity_id || v.identity_id === me.identityId;
          return (
            <button
              key={v.id}
              className={`chip${activeView === v.id ? ' on' : ''}`}
              onClick={() => applyView(v)}
              title={
                (mine ? 'Your view' : 'Shared with you')
                + (vis === 'private' ? ' · private' : ' · shared with this base')
              }
            >
              {vis === 'private' ? '🔒 ' : ''}{v.name}
              {!mine && <span className="muted"> · shared</span>}
            </button>
          );
        })}
        {(sort.length > 0 || filter.length > 0 || hidden.length > 0 || activeView) && (
          <button className="chip" onClick={clearView} title="Back to everything, unsorted">
            Reset
          </button>
        )}
        {/*
          SEARCH (C1c-12). It runs on the SERVER, against a Postgres full-text
          index built per table — so it is the whole table, not the 200 rows on
          screen. A browser-side search would be wrong the moment a table outgrew
          one page, and it would look right, which is the part that matters.
        */}
        <input
          className="searchbox"
          value={searchBox}
          placeholder="Search this table…"
          aria-label="Search this table"
          onChange={(e) => setSearchBox(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setSearchBox(''); setSearch(''); } }}
        />
        {searchBox && (
          <button className="chip" onClick={() => { setSearchBox(''); setSearch(''); }} title="Clear the search">
            ✕
          </button>
        )}
        <button className="chip" onClick={() => setEditingFilter(true)}>
          Filter{filter.length ? ` · ${filter.length}` : ''}
        </button>
        {/*
          SORT — C2-12, and the reason it needed a screen at all.

          The server has taken a list of sort rules since C1: parseSort() accepts
          up to eight, resolves each field name against this table's own slugs
          and DROPS anything it cannot resolve. The grid could only ever produce
          ONE of them, because the only way to sort was to click a column header
          and that replaces the whole list. So "sort by owner, then by date"
          was expressible in the API, honoured by the database and unreachable
          from the console.
        */}
        <button className="chip" onClick={() => setEditingSort(true)}>
          Sort{sort.length ? ` · ${sort.length}` : ''}
        </button>
        {/*
          ROW HEIGHT — C1c-10. A three-way toggle rather than a dialog: there are
          three values, they are worth trying against your own data, and a modal
          between you and the answer is one click too many for something you
          judge by looking at it.
        */}
        <span className="chipgroup" role="group" aria-label="Row height">
          {ROW_HEIGHTS.map((h) => (
            <button
              key={h.value}
              className={`chip${effectiveRowHeight === h.value ? ' on' : ''}`}
              title={`${h.label} rows${rowHeight === null ? ' — from your settings until you pick one here' : ''}`}
              onClick={() => setRowHeight(h.value)}
            >
              {h.label}
            </button>
          ))}
        </span>
        {/*
          The built-in columns, toggleable per view (Amendment 1, folded into
          C1c-4). They are read-only and they are the answer to "who touched
          this and when" at a glance, which is not always what you want on
          screen and is sometimes exactly what you want.
        */}
        <button
          className={`chip${showBuiltIns ? ' on' : ''}`}
          onClick={() => setShowBuiltIns((v) => !v)}
          title="Show or hide Created and Updated"
        >
          Built-ins
        </button>
        {/*
          MANUAL ORDER — C2-9, Matt's ruling R6. A MODE rather than a column
          header, because there is no column to click: `position` is not a field
          and never appears in the grid. Turning it on replaces the sort, and
          turning it off returns to the default order (oldest first) rather than
          to whatever was sorted before — a remembered previous sort would be a
          fourth piece of view state nobody asked for.
        */}
        {canWrite && (
          <button
            className={`chip${manualOrder ? ' on' : ''}`}
            onClick={() => { setActiveView(null); setSort(manualOrder ? [] : [{ field: 'manual', dir: 'asc' }]); }}
            title={manualOrder
              ? 'Rows are in the order you dragged them. Click to go back to oldest first.'
              : 'Put the rows in an order you choose, and drag them by the ⠿ handle.'}
          >
            Manual order
          </button>
        )}
        {hidden.length > 0 && (
          <button className="chip" onClick={() => setHidden([])} title="Show every column again">
            {hidden.length} hidden
          </button>
        )}
        {sort.length > 0 && !manualOrder && (
          <span className="muted" style={{ fontSize: 12 }}>
            sorted by {sort.map((s) => `${s.field} ${s.dir}`).join(', ')}
          </span>
        )}
        {manualOrder && (
          <span className="muted" style={{ fontSize: 12 }}>
            drag the ⠿ handle to reorder
          </span>
        )}
        <div className="spacer" />
        {isOwnerAdmin && (
          <button
            className="chip"
            disabled={exporting}
            title="Download every row this view shows, not just the ones on screen — there is no row limit"
            onClick={() => { exportCsv(); }}
          >
            {exporting ? 'Preparing…' : 'Export CSV'}
          </button>
        )}
        <button className="chip" onClick={() => setSavingView(true)}>Save view</button>
        {/*
          DELETE IS OFFERED ONLY ON YOUR OWN VIEW — 0032. The list now contains
          other people's shared views and the database refuses to delete one
          (`views_delete` is owner-only), so a button here would be a control
          that always fails. The route answers 403 with a sentence saying whose
          it is, which is the wall; this is the screen not asking.
        */}
        {activeView && views.some((v) => v.id === activeView
          && (!v.identity_id || v.identity_id === me.identityId)) && (
          <button
            className="chip"
            title="Delete this saved view"
            onClick={async () => {
              try {
                await api.deleteView(table.id, activeView);
                clearView();
                loadViews();
              } catch (e) { onError(e); }
            }}
          >Delete view</button>
        )}
      </div>

      {editingFilter && (
        <FilterDialog
          fields={allFields}
          filter={filter}
          onCancel={() => setEditingFilter(false)}
          onApply={(f) => { setActiveView(null); setFilter(f); setEditingFilter(false); }}
        />
      )}

      {editingSort && (
        <SortDialog
          fields={allFields}
          builtIns={showBuiltIns ? BUILT_IN_SORT_COLUMNS : []}
          sort={sort}
          onCancel={() => setEditingSort(false)}
          onApply={(next) => { setActiveView(null); setSort(next); setEditingSort(false); }}
        />
      )}

      {savingView && (
        <SaveViewDialog
          existing={views}
          mineOnly={views.filter((v) => !v.identity_id || v.identity_id === me.identityId)}
          onCancel={() => setSavingView(false)}
          onError={onError}
          onSave={async (name, visibility) => {
            try {
              await api.saveView(table.id, {
                name, sort, filter, hidden, widths, row_height: effectiveRowHeight, visibility,
              });
              setSavingView(false);
              loadViews();
            } catch (e) { onError(e); setSavingView(false); }
          }}
        />
      )}

      {/*
        C2-A. The picker adds ONE id to whatever the cell already holds — the
        current chips are read out of the freshly loaded page rather than
        remembered, so two adds in a row cannot resurrect a stale list.
      */}
      {picking && (() => {
        const f = allFields.find((x) => x.id === picking.fieldId);
        const row = rows.find((r) => String(r['id']) === picking.rowId);
        if (!f || !row) return null;
        const cells = linkCells(row[f.slug]);
        return (
          <LinkPicker
            field={f}
            already={cells}
            onCancel={() => setPicking(null)}
            onError={onError}
            onPick={(targetId, targetTitle) => {
              const next = keptLinkIds(f, cells);
              // 'Limit to one record' REPLACES rather than refuses — Matt's
              // ruling on N-15, 2026-08-29. The API's 403 is untouched and stays
              // the wall for any caller that is not a browser; this is simply not
              // the path a mouse takes, and the picker sends one id.
              const holdsOne = f.link_allow_multiple === false;
              const displaced = holdsOne ? (cells[0] ?? null) : null;
              const rowId = picking.rowId;
              void saveLink(rowId, f, holdsOne ? [targetId] : [...next, targetId]).then((ok) => {
                // Only on a write that actually landed. A refusal already has
                // its own banner, and "Replaced X with Y" over a failed PATCH
                // would be the console lying about what it did.
                if (ok && displaced && displaced.id !== targetId) {
                  setSwapped({ rowId, fieldId: f.id, from: displaced, toTitle: targetTitle });
                }
              });
              if (holdsOne) setPicking(null);
            }}
          />
        );
      })()}

      {/* C2b-A. The popover is anchored to the grid rather than to the cell:
          a cell can be one row from the bottom of a scrolled table, and a
          popover that renders inside it is a popover half of which is off the
          screen — which is exactly the shape of MAN-3's phone finding. */}
      {attaching && (() => {
        const f = allFields.find((x) => x.id === attaching.fieldId);
        if (!f) return null;
        return (
          <div className="attpop-layer" onMouseDown={(e) => { if (e.target === e.currentTarget) setAttaching(null); }}>
            <AttachPopover
              tableId={table.id}
              recordId={attaching.rowId}
              field={f}
              onError={onError}
              onDone={(added) => { setAttaching(null); if (added) load(); }}
            />
          </div>
        );
      })()}

      {expanding && (() => {
        const f = allFields.find((x) => x.id === expanding.fieldId);
        const row = rows.find((r) => String(r['id']) === expanding.rowId);
        if (!f) return null;
        return (
          <AttachmentPanel
            tableId={table.id}
            recordId={expanding.rowId}
            field={f}
            editable={canWrite && !row?.['deleted_at']}
            onClose={() => setExpanding(null)}
            onChanged={load}
            onError={onError}
          />
        );
      })()}

      {openLink && (
        <LinkedRow
          tableId={openLink.tableId}
          recordId={openLink.recordId}
          onClose={() => setOpenLink(null)}
          onError={onError}
        />
      )}

      {addingField && (
        <AddFieldDialog
          tableId={table.id}
          tableName={table.name}
          baseId={page?.table.base_id ?? ''}
          existing={allFields}
          at={addingField.at}
          onCancel={() => setAddingField(null)}
          onError={onError}
          onCreated={() => { setAddingField(null); load(); }}
        />
      )}

      {/*
        THE FIELD EDITOR. Reads its field out of the freshly loaded page rather
        than holding a copy: every control in it commits on its own and reloads,
        so a stale copy would show the previous answer next to the new one.
      */}
      {editingField && allFields.some((f) => f.id === editingField) && (
        <FieldEditor
          tableId={table.id}
          field={allFields.find((f) => f.id === editingField)!}
          onClose={() => setEditingField(null)}
          onChanged={load}
          onError={onError}
        />
      )}

      {confirmTrash && (
        <Modal title={`Delete ${confirmTrash.name}?`} onCancel={() => setConfirmTrash(null)}>
          <p style={{ marginTop: 0 }}>
            It moves to this table's <strong>field Trash</strong>: it disappears from the
            grid and from the API, and <strong>nothing is deleted</strong>. Its column and
            every value in it stay exactly where they are, and you can restore it at any
            time.
          </p>
          <p className="muted" style={{ fontSize: 12 }}>
            Until you restore or rename it, no new field can use the name{' '}
            <code>{confirmTrash.slug}</code> — because that column is still there, holding
            the data that makes restoring it possible.
          </p>
          <div className="modal-actions">
            <button className="action" onClick={() => setConfirmTrash(null)}>Cancel</button>
            <button className="action on" onClick={() => void reallyTrashField(confirmTrash)}>
              Move to field Trash
            </button>
          </div>
        </Modal>
      )}

      {fieldTrash && (
        <FieldTrash
          tableId={table.id}
          onClose={() => setFieldTrash(false)}
          onRestored={load}
          onError={onError}
        />
      )}

      {docCell && (() => {
        const f = allFields.find((x) => x.slug === docCell.slug);
        const r = rows.find((x) => String(x['id']) === docCell.rowId);
        if (!f || !r) return null;
        return (
          <RichTextModal
            tableId={table.id}
            recordId={docCell.rowId}
            field={f}
            editable={canWrite && !r['deleted_at']}
            onClose={() => setDocCell(null)}
            onSaved={load}
            onError={onError}
          />
        );
      })()}

      {expandCell && (() => {
        const f = allFields.find((x) => x.slug === expandCell.slug);
        const r = rows.find((x) => String(x['id']) === expandCell.rowId);
        if (!f || !r) return null;
        return (
          <ExpandText
            field={f}
            value={r[f.slug]}
            editable={canWrite && !r['deleted_at']}
            onClose={() => setExpandCell(null)}
            onCommit={(next) => void save(expandCell.rowId, f, next)}
          />
        );
      })()}

      {isOwnerAdmin && canWrite && picked.size > 0 && (
        <div className="bulkbar">
          <strong>{picked.size} selected</strong>
          <span className="muted">set</span>
          {/* allFields, not the visible ones. Hiding a column is a display choice,
              and a person who hides Body and then cannot bulk-set Body has hit a
              rule nobody wrote and nothing explains. */}
          <select value={bulkField} onChange={(e) => setBulkField(e.target.value)}>
            <option value="">choose a field…</option>
            {allFields.map((f) => (
              <option key={f.id} value={f.slug}>
                {f.name}{hidden.includes(f.slug) ? ' (hidden)' : ''}
              </option>
            ))}
          </select>
          <span className="muted">to</span>
          <input
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            placeholder="value (blank clears it)"
            style={{ minWidth: '10rem' }}
          />
          <button
            className="action on"
            disabled={!bulkField || bulkBusy}
            onClick={() => void applyBulk()}
          >
            {bulkBusy ? 'Applying…' : 'Apply'}
          </button>
          <div className="spacer" style={{ flex: 1 }} />
          {/*
            TRASHING LIVES HERE NOW — Matt, 2026-08-29 (UI-16). It used to be a
            grey icon on every row, one click away at all times. Putting it in
            the selection bar means it cannot be reached without first choosing
            what it applies to, which is most of the protection; the
            confirmation below is the rest.

            TWO BUTTONS, SHOWN ONLY WHEN THEY APPLY. A selection can hold live
            rows and trashed ones, and "delete" and "restore" are opposite acts —
            one button that guessed from the first row would eventually guess
            wrong on somebody's mixed selection.
          */}
          {/*
            `confirm_before_trash` (C2-13). Off means the click acts, and the
            undo is that nothing is deleted: Trash is restorable and the Restore
            button is right beside this one. The setting is only worth having
            because of that — a confirmation in front of an irreversible act
            would not be a preference.
          */}
          {liveCount > 0 && (
            <button className="action danger" onClick={() => {
              if (confirmBeforeTrash) setConfirmBin('trash'); else void binSelected('trash');
            }}>
              Delete{liveCount === picked.size ? '' : ` ${liveCount}`}
            </button>
          )}
          {trashedCount > 0 && (
            <button className="action" onClick={() => {
              if (confirmBeforeTrash) setConfirmBin('restore'); else void binSelected('restore');
            }}>
              Restore{trashedCount === picked.size ? '' : ` ${trashedCount}`}
            </button>
          )}
          <button className="action" onClick={() => setPicked(new Set())}>Clear selection</button>
        </div>
      )}

      {confirmBin && (
        <Modal
          title={confirmBin === 'trash' ? 'Delete these records?' : 'Restore these records?'}
          onCancel={() => { if (!binBusy) setConfirmBin(null); }}
        >
          <p style={{ margin: '0 0 6px' }}>
            {confirmBin === 'trash'
              ? `Are you sure you want to delete ${liveCount === 1 ? 'this record' : `these ${liveCount} records`}?`
              : `Restore ${trashedCount === 1 ? 'this record' : `these ${trashedCount} records`}?`}
          </p>
          {/*
            SAYS WHERE THEY GO. The console never hard deletes — the row is
            stamped `deleted_at` and console_app holds no DELETE grant on a
            generated table at all — so "deleted" here means something recoverable
            and a dialog that let a person believe otherwise would be frightening
            them about the wrong thing.
          */}
          {confirmBin === 'trash' && (
            <p className="muted" style={{ margin: '0 0 14px' }}>
              They move to the Trash and can be brought back from there. Nothing is erased.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={confirmBin === 'trash' ? 'action danger solid' : 'action on'}
              disabled={binBusy}
              onClick={() => void binSelected(confirmBin)}
            >
              {binBusy ? 'Working…' : confirmBin === 'trash' ? 'Delete' : 'Restore'}
            </button>
            <button className="action" disabled={binBusy} onClick={() => setConfirmBin(null)}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/*
        C2-14 (UI-6) — "THERE IS MORE THIS WAY". Matt's pick out of two.

        Notes has nine fields and a maximised window on the mini shows six. The
        box scrolls, but macOS hides the scrollbar until you move, so there was
        no cue at all — he went looking for a Stage field that was column seven
        and had no reason to believe it existed.

        A FADE ON THE EDGE THAT STILL HAS SOMETHING BEHIND IT, on both sides, so
        it also answers "how did I get here" after you have scrolled right. It is
        a sibling of the scroll box rather than a child, because a child would
        scroll away with the content it is describing; `pointer-events: none` in
        the stylesheet keeps it out of the way of the cells underneath.

        Recomputed on scroll, on resize, and whenever the rows or columns change
        — a fade that was right when the table loaded and wrong after a column
        was hidden would be worse than none.
      */}
      <div className="gridbox">
      {moreLeft && <div className="edgefade left" aria-hidden="true" />}
      {moreRight && <div className="edgefade right" aria-hidden="true" />}
      <div className="grid-wrap" ref={gridWrap} onScroll={measureEdges}>
        {/*
          THE FROZEN FIRST COLUMN NEEDS TO KNOW WHAT IS TO ITS LEFT — C2-10.
          `position: sticky` takes a distance, and the leading columns (the drag
          handle in manual mode, the select-all checkbox for the Owner Admin)
          come and go. Rather than three CSS rules that each assume a layout,
          the offsets are computed here, where the answer is known, and the
          stylesheet reads them.
        */}
        <table
          className="grid"
          style={{
            ['--gripw' as string]: manualOrder && canWrite ? '30px' : '0px',
            ['--pickw' as string]: isOwnerAdmin && canWrite ? '30px' : '0px',
            // C1c-10. One number, from one definition in api.ts, handed to the
            // stylesheet — rather than three CSS classes that would each have to
            // be kept in step with the three heights.
            ['--rowh' as string]: `${rowHeightPx(effectiveRowHeight)}px`,
          }}
        >
          <thead>
            <tr>
              {/*
                THE DRAG HANDLE COLUMN — C2-9, and it exists ONLY in manual mode.
                Airtable's model, and Matt's ruling R6: a handle on every row in
                every view would invite a drag that the ordering would then
                ignore, which is a gesture that works and looks broken.

                FIRST, matching the body. A head and a body that disagree about
                how many leading columns there are is a table that renders one
                column out of step and looks like a CSS bug.
              */}
              {manualOrder && canWrite && <th className="rowgrip" aria-label="Reorder" />}
              {isOwnerAdmin && canWrite && (
                <th className="pick">
                  <input
                    type="checkbox"
                    aria-label="Select every row on this page"
                    checked={picked.size > 0 && picked.size === rows.length}
                    onChange={(e) => {
                      setPicked(e.target.checked
                        ? new Set(rows.map((r) => String(r['id'])))
                        : new Set());
                    }}
                  />
                </th>
              )}
              {/*
                THE ROW-TOOLS COLUMN IS GONE — Matt, 2026-08-29 (UI-16).
                It cost every table a fixed 5rem to carry three icons. Expand and
                copy-link moved onto the first cell's two corners; trashing moved
                into the selection bar, behind a confirmation. Nothing was
                removed, only rehoused.
              */}
              {fields.map((f, ci) => {
                const s = sort.find((x) => x.field === f.slug);
                const shift = columnDrag.shiftFor(ci);
                return (
                  <th
                    key={f.id}
                    ref={(el) => { headEls.current[ci] = el; }}
                    className={[
                      s ? 'sorted' : '',
                      // THE FIRST COLUMN IS FROZEN — C2-10. It is the primary
                      // field, pinned at position 1 by migration 0021, so it is
                      // the one column that is always the same one; freezing
                      // anything else would need a per-view choice nobody has
                      // asked for.
                      ci === 0 ? 'frozen' : '',
                      columnDrag.state?.from === ci ? 'dragging' : '',
                      shift === -1 ? 'shift-start' : shift === 1 ? 'shift-end' : '',
                      columnDraggable(ci) ? 'draggable' : '',
                    ].filter(Boolean).join(' ') || undefined}
                    style={widths[f.slug] ? { width: widths[f.slug], minWidth: widths[f.slug] } : undefined}
                    onPointerDown={(e) => {
                      // NOT FROM THE RESIZER OR THE MENU. Both are inside this
                      // header and both own their own pointer gesture; starting
                      // a column drag from the resizer would make the column
                      // both wider and somewhere else.
                      if ((e.target as HTMLElement).closest('.resizer, .colmenu')) return;
                      if (!columnDraggable(ci)) return;
                      columnDrag.begin(e, ci, headEls.current.slice(0, fields.length));
                    }}
                  >
                    {/*
                      TYPE ICON (Amendment 1, folded into C1c-1). A grid of
                      identical headers makes you read every cell to learn what a
                      column is; the icon says it before you look down.
                    */}
                    <span className="ticon" title={typeLabel(f.type)}>{typeIcon(f.type)}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title="Sort by this column"
                      onClick={() => cycleSort(f.slug)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') cycleSort(f.slug); }}
                    >
                      {f.name}{s ? (s.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </span>
                    <button
                      className="colmenu"
                      title={`What can be done with ${f.name}`}
                      aria-label={`Menu for ${f.name}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => setHeaderMenu(headerMenu === f.id ? null : f.id)}
                    >▾</button>
                    {headerMenu === f.id && (
                      <HeaderMenu
                        field={f}
                        canBuild={canBuild}
                        onClose={() => setHeaderMenu(null)}
                        onEdit={() => setEditingField(f.id)}
                        onInsert={(side) =>
                          setAddingField({ at: f.position + (side === 'left' ? 0 : 1) })}
                        onDuplicate={() => void duplicateField(f)}
                        onSort={(dir) => { setActiveView(null); setSort([{ field: f.slug, dir }]); }}
                        onHide={() => { setActiveView(null); setHidden((h) => [...h, f.slug]); }}
                        onTrash={() => void trashField(f)}
                      />
                    )}
                    <small>
                      {typeLabel(f.type).toLowerCase()}
                      {f.format ? ` · ${f.format}` : ''}
                      {f.default_value !== null && f.default_value !== undefined ? ' · has a default' : ''}
                    </small>
                    <ColumnResizer
                      width={widths[f.slug]}
                      onResize={(px) => { setActiveView(null); setWidths((w) => ({ ...w, [f.slug]: px })); }}
                    />
                  </th>
                );
              })}
              {/*
                THE '+' AT THE END OF THE COLUMNS (Amendment 3 item 1). It was
                not in C1b — the toolbar's "Add field" was the only way in — and
                Matt asked for it verbatim because it is where the hand already
                is when you run out of columns.
              */}
              {canBuild && (
                <th className="addcol">
                  <button
                    className="colmenu big"
                    title="Add a field to the end of this table"
                    aria-label="Add a field"
                    onClick={() => setAddingField({})}
                  >+</button>
                </th>
              )}
              {/*
                THE TWO BUILT-IN COLUMNS ARE SORTABLE — Matt, 2026-08-31:
                "I would like to be able to sort on created date and updated
                date but those options aren't showing."

                They are not fields, so they get a DELIBERATELY SMALLER menu:
                the two sort items and nothing else. Edit, Insert, Duplicate,
                Hide and Delete are all meaningless for a column the console
                owns, and offering them greyed out would be worse than not
                offering them. Clicking the name still cycles the sort, exactly
                like a field header.
              */}
              {showBuiltIns && BUILT_IN_SORT_COLUMNS.map((b) => {
                const s = sort.find((x) => x.field === b.slug);
                return (
                  <th key={b.slug} className={s ? 'sorted' : undefined}>
                    <span
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title={`Sort by ${b.label}`}
                      onClick={() => cycleSort(b.slug)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') cycleSort(b.slug); }}
                    >
                      {b.label}{s ? (s.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </span>
                    <button
                      className="colmenu"
                      title={`What can be done with ${b.label}`}
                      aria-label={`Menu for ${b.label}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => setHeaderMenu(headerMenu === b.slug ? null : b.slug)}
                    >▾</button>
                    {headerMenu === b.slug && (
                      <MenuLayer onClose={() => setHeaderMenu(null)}>
                        {(item) => (<>
                          {item('Sort oldest first', () => {
                            setActiveView(null); setSort([{ field: b.slug, dir: 'asc' }]);
                          })}
                          {item('Sort newest first', () => {
                            setActiveView(null); setSort([{ field: b.slug, dir: 'desc' }]);
                          })}
                        </>)}
                      </MenuLayer>
                    )}
                    {/*
                      The zone these two columns are shown in. It used to be a
                      module constant reading 'America/New_York' for everybody;
                      it is now whatever this person chose, or their browser's
                      when they have chosen nothing (C2-13, C2-16).
                    */}
                    <small title="Change this in Settings">{zoneOf(displayPrefs)}</small>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shownRows.map((r, ri) => {
              const id = String(r['id']);
              const shift = rowDrag.shiftFor(ri);
              return (
                <tr
                  key={id}
                  ref={(el) => { rowEls.current[ri] = el; }}
                  className={[
                    r['deleted_at'] ? 'trashed' : '',
                    rowDrag.state?.from === ri ? 'dragging' : '',
                    shift === -1 ? 'shift-start' : shift === 1 ? 'shift-end' : '',
                  ].filter(Boolean).join(' ') || undefined}
                >
                  {manualOrder && canWrite && (
                    <td className="rowgrip">
                      <span
                        className="grip"
                        role="button"
                        tabIndex={-1}
                        aria-label="Drag to reorder this row"
                        title="Drag to reorder"
                        onPointerDown={(e) => rowDrag.begin(e, ri, rowEls.current.slice(0, shownRows.length))}
                      >⠿</span>
                    </td>
                  )}
                  {isOwnerAdmin && canWrite && (
                    <td className="pick">
                      <input
                        type="checkbox"
                        aria-label="Select this row"
                        checked={picked.has(id)}
                        onChange={(e) => {
                          const next = new Set(picked);
                          if (e.target.checked) next.add(id); else next.delete(id);
                          setPicked(next);
                        }}
                      />
                    </td>
                  )}
                  {fields.map((f, fi) => {
                    const isSel = selectedCell?.rowId === id && selectedCell.slug === f.slug;
                    // THE FIRST VISIBLE COLUMN CARRIES THE ROW'S OWN CONTROLS.
                    // First VISIBLE rather than the primary field by name: the
                    // primary is pinned to position 1 by migration 0021, but it
                    // can still be hidden, and a control attached to a column
                    // nobody can see is a control that does not exist.
                    const corners = fi === 0;
                    return (
                      <td
                        key={f.id}
                        data-row={id}
                        data-field={f.slug}
                        className={[isSel ? 'sel' : '', corners ? 'hascorner' : '',
                                    fi === 0 ? 'frozen' : ''].filter(Boolean).join(' ') || undefined}
                        // Focusable so that a cell with no control of its own —
                        // a link cell is chips and a button — can still be
                        // arrowed on to. -1 keeps it out of the Tab order, which
                        // belongs to the controls.
                        tabIndex={-1}
                        onFocusCapture={() => setSelectedCell({ rowId: id, slug: f.slug })}
                        onMouseDown={() => setSelectedCell({ rowId: id, slug: f.slug })}
                        onKeyDown={(e) => onCellKeyDown(e, id, f)}
                      >
                        <Cell
                              field={f}
                              /*
                                A RICH TEXT CELL READS THE SHADOW, NOT THE FIELD'S
                                OWN KEY — and this line is here because the first
                                version did not, and every row with a document
                                read "Empty".

                                FOUND BY OPENING THE GRID, 2026-09-04, with 311
                                green tests behind it. The listing DELIBERATELY
                                does not carry the document (a page is 100 rows
                                and a document may be most of a megabyte), so
                                `r[f.slug]` is undefined for a rich text field and
                                always will be. The e2e case asserted the
                                LISTING's payload — `row['doc_md']` is the words —
                                and was right; nothing asserted what the cell then
                                did with it, and the cell asked for the one key
                                the server does not send.
                              */
                              value={f.type === 'rich_text' ? r[`${f.slug}_md`] : r[f.slug]}
                              editable={canWrite && !r['deleted_at']}
                              onCommit={(v) => void save(id, f, v)}
                              onLinkOpen={(c) => f.link_target_table_id && setOpenLink({ tableId: f.link_target_table_id, recordId: c.id })}
                              onLinkAdd={() => setPicking({ rowId: id, fieldId: f.id })}
                              onLinkSet={(ids) => void saveLink(id, f, ids)}
                              attach={f.type === 'attachment' ? {
                                add: () => setAttaching({ rowId: id, fieldId: f.id }),
                                expand: () => setExpanding({ rowId: id, fieldId: f.id }),
                                // A chip opens where the thing actually lives,
                                // in a new tab, under the person's own login.
                                open: (a) => { if (a.url) window.open(a.url, '_blank', 'noopener'); },
                                remove: (a) => {
                                  void api.trashAttachment(table.id, id, a.id)
                                    .then(() => load()).catch(onError);
                                },
                                drop: (d) => {
                                  // A DROPPED LINK IS A PASTED LINK. A dropped
                                  // FILE is an upload since C2b-D — the same
                                  // three steps the popover takes, with no
                                  // progress bar, because a drop onto a cell is
                                  // a gesture for a small file and the popover
                                  // is where a big one belongs.
                                  const text = (d.getData('text/uri-list') || d.getData('text/plain') || '').trim();
                                  const isLink = /^https:\/\//i.test(text) && d.files.length === 0;
                                  const work = isLink
                                    ? api.attach(table.id, id, { fieldId: f.id, url: text }).then(() => undefined)
                                    : d.files.length
                                      ? uploadOneFile(table.id, id, f.id, d.files[0]!).then(() => undefined)
                                      // Neither a link nor a file: dropped text,
                                      // an image from another page, a folder.
                                      // Answered rather than silently ignored.
                                      : Promise.reject(new Error(
                                        'That is not a file or an https link, so there is nothing to attach.',
                                      ));
                                  void work.then(() => load()).catch(onError);
                                },
                              } : undefined}
                              rich={f.type === 'rich_text'
                                ? { open: () => setDocCell({ rowId: id, slug: f.slug }) }
                                : undefined} />
                        {corners && (
                          <>
                            {/*
                              EXPAND, top-right. Same glyph and same shape as the
                              long-text expand below, because they do the same
                              kind of thing — open the thing you are looking at in
                              a bigger space — and two icons for one idea is one
                              icon too many.
                            */}
                            <button
                              className="cellexpand"
                              title="Open this record"
                              aria-label="Open this record"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setExpanded(id)}
                            >⤢</button>
                            {/*
                              COPY LINK, bottom-right (C1c-13). The row id IS the
                              link. It is not an access token — opening it runs the
                              same grant check as any other read — which is what
                              makes it safe to paste into a Matt Queue row or hand
                              to an agent.
                            */}
                            <button
                              className="celllink"
                              title="Copy a link straight to this row"
                              aria-label="Copy a link to this row"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => void copyRowLink(id, { x: e.clientX, y: e.clientY })}
                            >🔗</button>
                          </>
                        )}
                        {/*
                          THE EXPAND ICON, in the cell's top-right corner and only
                          on the selected cell — Airtable's behaviour exactly
                          (ruling R1). Always-on would put an icon over the text of
                          every long-text cell in the table.
                        */}
                        {f.type === 'long_text' && isSel && (
                          <button
                            className="cellexpand"
                            title="Expand this cell (Shift+Space)"
                            aria-label={`Expand ${f.name}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setExpandCell({ rowId: id, slug: f.slug })}
                          >⤢</button>
                        )}
                      </td>
                    );
                  })}
                  {canBuild && <td className="addcol" />}
                  {showBuiltIns && <td className="readonly">{fmtTime(r['created_at'])}</td>}
                  {showBuiltIns && <td className="readonly">{fmtTime(r['updated_at'])}</td>}
                </tr>
              );
            })}
            {/*
              THE GHOST ROW — Matt, 2026-08-29. One blank row under the last
              real one; clicking any field of it creates the record and puts the
              cursor in the field you clicked.

              IT IS NOT AN EDITABLE ROW PRETENDING TO BE A RECORD. Every cell is
              a button, so there is nothing to type into until a real row exists
              — which means there is no state to lose, no half-typed value that
              belongs to nothing, and no question about what happens if the
              create fails. Airtable's is the same trick.

              SHOWN ONLY WHEN IT WOULD WORK: not in the trash view, not without
              console.records:write, and not while a search or filter is on —
              there, a new empty row would be created and then immediately
              vanish from the listing because it matches nothing, which is the
              worst possible answer to a click.
            */}
            {canWrite && !trash && !search.trim() && filter.length === 0 && (
              <tr className="ghost">
                {isOwnerAdmin && canWrite && <td className="pick" />}
                {fields.map((f) => (
                  <td key={f.id}>
                    <button
                      type="button"
                      disabled={ghostBusy}
                      title={`Add a row and start typing in ${f.name}`}
                      aria-label={`Add a row and start typing in ${f.name}`}
                      onClick={() => void addRowFromGhost(f.slug)}
                    >{ghostBusy ? '…' : '+'}</button>
                  </td>
                ))}
                {canBuild && <td className="addcol" />}
                {showBuiltIns && <td className="readonly" />}
                {showBuiltIns && <td className="readonly" />}
              </tr>
            )}

            {rows.length === 0 && (
              <tr>
                <td
                  className="muted"
                  style={{ padding: 16 }}
                  colSpan={
                    fields.length
                    + (isOwnerAdmin && canWrite ? 1 : 0)       // the select checkbox
                    + (canBuild ? 1 : 0)                       // the '+' column
                    + (showBuiltIns ? 2 : 0)                   // created, updated
                  }
                >
                  {search.trim()
                    ? `Nothing matches "${search.trim()}".`
                    : trash ? 'Nothing in the trash.' : 'No rows yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/*
        THE PAGER — C2-5, LOAD-MORE on the keyset cursor (Matt's ruling R5).

        WHAT IT SAYS IS AS MUCH OF THE WORK AS WHAT IT DOES. "100 of 2,340" is an
        ordinary total; "100 of 10,000+" is the server saying it STOPPED
        counting, and the `+` is the whole difference between an honest answer
        and an invented one. An exact count of production's 100,009-row table
        through the read wall measured 9,035 ms against an 8-second statement
        timeout, so the alternative to the `+` was a count that 503s.

        "LOAD MORE" IS ENABLED BY THE SERVER'S nextCursor, NOT BY ARITHMETIC ON
        THE TOTAL. With a capped total the arithmetic cannot know whether there
        is another page; the cursor is null exactly when there is not.
      */}
      {page && (
        <div className="pager">
          <button
            className="action"
            disabled={!cursor || loadingMore}
            onClick={loadMore}
            title={cursor ? 'Load the next page' : 'Everything in this view is loaded'}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
          <span className="muted">
            {rows.length === 0
              ? 'No rows'
              : <>
                  {rows.length.toLocaleString()}
                  {count && <> of {count.total.toLocaleString()}{count.totalIsExact ? '' : '+'}</>}
                  {!cursor && rows.length > 0 && <> — all of them</>}
                </>}
            {count && !count.totalIsExact && (
              <span
                title={`This console stops counting at ${count.countCap.toLocaleString()} rows. An exact total of a table this size takes longer than it allows one query to take, so it says "at least" rather than guessing.`}
              > ⓘ</span>
            )}
          </span>
          {/*
            C2-6 / ruling R7: "serve the sort slow-with-warning while it builds."
            The person seeing this is the one waiting on the slow sort, and what
            they need told is that the wait is temporary.

            THE PROMISE IS ONLY MADE WHEN IT CAN BE KEPT — PERF-10. Until
            2026-08-31 this sentence was rendered for EVERY sort of an unindexed
            column, the ten-row fixture included, where the index it promises is
            refused for being pointless and the sort is already instant. The
            server now separates the temporary case from the permanent ones; each
            of those has its own words below, or none at all.
          */}
          {page.sortIndex === 'building' && (
            <span className="muted bad">
              This is the first time anything has sorted by that column, so the database is
              reading the whole table. An index for it is being built in the background —
              the next time you sort this way it will be quick.
            </span>
          )}
          {/*
            AND THE STATE THAT NEEDED ITS OWN WORD. A long text column is never
            indexed for sorting — a single value can be bigger than a btree entry
            may be, so migration 0023 refuses it. Rendering 'building' here would
            promise an index that is not coming, and somebody would keep waiting
            for a sort to get faster.
          */}
          {page.sortIndex === 'unindexable' && (
            <span className="muted bad">
              Sorting by a long text column reads the whole table every time, and always will —
              a single value can be bigger than an index entry is allowed to be. The search box
              covers this column instead.
            </span>
          )}
          {/*
            OVER THE CEILING — PERF-10. The same shape as 'unindexable' (permanent,
            said plainly) for a different reason: the index is possible, and it is
            the WRITE LOCK it would hold on a table this size that the console
            will not take. Naming that cost is what stops this reading as a defect.
          */}
          {page.sortIndex === 'too_large' && (
            <span className="muted bad">
              Sorting by that column reads the whole table, and will keep doing so. This table
              is large enough that building an index for it would lock out everything writing
              to it while it built, so the console does not. Filtering or searching first is
              the faster way in.
            </span>
          )}
          {/*
            ASKED, AND IT DID NOT HAPPEN — PERF-10. Refused, busy, or a genuine
            failure: three causes, one consequence, and the consequence is the
            part a person can act on. WHICH of the three is in the server log,
            where the person who can do something about it will look — putting
            "this caller may not build structure" on the screen would tell it to
            the one person who cannot act on it.
          */}
          {page.sortIndex === 'unindexed' && (
            <span className="muted bad">
              Sorting by that column reads the whole table each time. The console tried to
              build an index for it and could not, so this is as fast as this sort gets for
              now — the server log says why.
            </span>
          )}
          {/*
            A BUILT-IN COLUMN THE CONSOLE NEVER INDEXES — today only Updated,
            added with built-in sorting on 2026-08-31. Permanent like
            'unindexable' and 'too_large', but for a third reason: nothing is
            wrong and nothing was refused. It says so plainly instead of
            borrowing one of the other two sentences, which is the mistake
            PERF-10 was.
          */}
          {page.sortIndex === 'builtin_unindexed' && (
            <span className="muted bad">
              Sorting by Updated reads the whole table each time. The console builds indexes for
              the columns you make, not for the two it keeps itself — Created is already indexed
              and costs nothing, but Updated will always be a full read on a large table.
            </span>
          )}
          {/*
            'not_needed' RENDERS NOTHING, AND THAT IS THE FIX — PERF-10. Below the
            25,000-row floor the sort is already fast, no index is coming, and
            there is nothing a person needs told. Silence is the correct answer
            when nothing is wrong; the old code's mistake was having ONE word for
            "no index" and therefore always having something to say.
          */}
        </div>
      )}

      {/*
        The confirmation, at the pointer. It appears where the click happened
        rather than in a corner, because a toast in a corner of a wide grid is
        a thing you find out about afterwards. aria-live so it is announced
        rather than only seen; pointer-events none so it can never swallow the
        next click.
      */}
      {copied && copiedAt && (
        <div className="copied-toast" style={{ left: copiedAt.x, top: copiedAt.y }} role="status" aria-live="polite">
          Row URL copied
        </div>
      )}

      {expanded && (
        <RecordPanel
          tableId={table.id}
          fields={fields}
          record={rows.find((r) => String(r['id']) === expanded) ?? {}}
          editable={canWrite}
          me={me}
          onClose={() => setExpanded(null)}
          onSaved={load}
          onError={onError}
          onCopyLink={() => void copyRowLink(expanded)}
          copied={copied === expanded}
          onOpenLink={(tid, rid) => setOpenLink({ tableId: tid, recordId: rid })}
          onDuplicate={canWrite ? () => void duplicateRow(expanded) : undefined}
          duplicating={duplicating}
        />
      )}
    </>
  );
}

/**
 * ==========================================================================
 * AUTOMATIONS — C2b-12, and the delivery log is the DONE criterion.
 * ==========================================================================
 *
 * A webhook subscription is the ONE place rows leave this console on a schedule,
 * to a URL somebody typed. So this screen is built around what went wrong rather
 * than around what was configured: every subscription shows how many events are
 * pending, delivered and PARKED, and opening one shows every attempt — the
 * status code, the error, and whether the row gave up.
 *
 * A queue whose failures are invisible is a queue that lies about having
 * delivered. Eleven defects on this project have been found only by a person
 * looking at a screen, and this is the screen for this feature.
 *
 * THE SECRET IS SHOWN ONCE, HERE, AND NEVER AGAIN. The server returns it in the
 * create response and in no listing; there is nowhere to go and look it up. The
 * panel says so plainly at the moment it matters instead of leaving somebody to
 * discover it by refreshing.
 */
function AutomationsDialog({
  table, me, onCancel, onError,
}: {
  table: { id: string; name: string };
  me: Me;
  onCancel: () => void;
  onError: (e: unknown) => void;
}) {
  const canManage = me.scopes.includes(SCOPE.automationsManage);
  const [rows, setRows] = useState<Automation[] | null>(null);
  const [kinds, setKinds] = useState<TriggerKind[]>([]);
  /**
   * THE TABLE'S OWN FIELDS — M2 (e). The sidebar hands this dialog an id and a
   * name, so the pickers below fetch the schema themselves rather than making
   * every caller pass it down.
   */
  const [fields, setFields] = useState<Field[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [log, setLog] = useState<Delivery[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [freshSecret, setFreshSecret] = useState<{ name: string; secret: string } | null>(null);
  const [form, setForm] = useState({
    name: '', triggerKind: 'record.update', targetUrl: '', includeValues: false,
    fieldId: '', optionIds: [] as string[],
  });

  const load = useCallback(() => {
    api.automations()
      .then((r) => {
        setRows(r.automations.filter((a) => a.table_id === table.id));
        setKinds(r.kinds ?? []);
      })
      .catch((e) => { setRows([]); onError(e); });
  }, [table.id, onError]);

  useEffect(() => {
    if (!canManage) return;
    api.table(table.id).then((r) => setFields(r.table.fields)).catch(onError);
  }, [canManage, table.id, onError]);

  /** The kind currently chosen, and therefore what else has to be answered. */
  const kind = kinds.find((k) => k.key === form.triggerKind) ?? null;
  /** The fields this kind may name — status for a status change, checkbox for a tick. */
  const pickable = kind && kind.field_types.length
    ? fields.filter((f) => kind.field_types.includes(f.type))
    : [];
  const pickedField = pickable.find((f) => f.id === form.fieldId) ?? null;

  useEffect(() => { if (canManage) load(); else setRows([]); }, [canManage, load]);

  useEffect(() => {
    if (!open) { setLog(null); return; }
    api.deliveries(open).then((r) => setLog(r.deliveries)).catch(onError);
  }, [open, onError]);

  async function create() {
    setBusy(true);
    try {
      const r = await api.createAutomation({
        tableId: table.id, name: form.name, triggerKind: form.triggerKind,
        targetUrl: form.targetUrl, includeValues: form.includeValues,
        // Sent only when the chosen kind asks for them. The database refuses a
        // field or an option on a kind that names none, so sending them anyway
        // would turn a tidy form into a refusal nobody could act on.
        ...(kind?.needs_options ? { optionIds: form.optionIds } : {}),
        ...(kind?.needs_field ? { fieldId: form.fieldId } : {}),
      });
      setFreshSecret({ name: r.automation.name, secret: r.secret });
      setForm({
        name: '', triggerKind: 'record.update', targetUrl: '', includeValues: false,
        fieldId: '', optionIds: [],
      });
      load();
    } catch (e) { onError(e); } finally { setBusy(false); }
  }

  /**
   * WHAT IS STILL MISSING BEFORE THIS CAN BE REGISTERED, as a sentence rather
   * than a disabled button with no explanation. D-7's lesson, one screen along:
   * a control that will not act and does not say why reads as broken.
   */
  function missing(): string | null {
    if (!form.name.trim()) return 'Give it a name.';
    if (!form.targetUrl.trim()) return 'Say where to send it.';
    if (kind?.needs_field && !form.fieldId) {
      return pickable.length
        ? 'Choose which field this watches.'
        : `This table has no ${kind.field_types.join(' or ')} field, so nothing can fire on one.`;
    }
    if (kind?.needs_options) {
      if (!form.fieldId) {
        return pickable.length
          ? 'Choose which field this watches.'
          : `This table has no ${kind.field_types.join(' or ')} field, so nothing can fire on one.`;
      }
      if (form.optionIds.length === 0) return 'Tick at least one status that should fire it.';
    }
    return null;
  }
  const notYet = missing();

  return (
    <Modal title={`Automations · ${table.name}`} onCancel={onCancel} wide>
      {!canManage && (
        <p className="note">
          Sending rows to a URL needs <code>console.automations:manage</code>, which your session
          does not carry. This is a property of how you signed in, not of what you have been
          granted — the scope sits on the Owner Admin ceiling and on no other role. Nothing on
          this table is listed here because the console cannot show you triggers it will not
          let you see; an empty list would have been a different and untrue answer.
        </p>
      )}

      {canManage && freshSecret && (
        <div className="warnbox">
          <strong>Copy this signing secret now — it is not shown again.</strong>
          <p>
            Your receiver verifies it as an HMAC-SHA256 of the exact request body, sent in the
            <code> x-assett-signature</code> header as <code>sha256=…</code>. The console keeps the
            key so it can sign; it returns it exactly once, here, for the same reason a password is
            shown once. If you lose it, make a new trigger.
          </p>
          <code className="secret">{freshSecret.secret}</code>
        </div>
      )}

      {canManage && (
        <fieldset className="autonew">
          <legend>Send this table’s changes somewhere</legend>
          <label>
            Name
            <input value={form.name} maxLength={200}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Notify ops of new jobs" />
          </label>
          <label>
            Fires on
            {/* RENDERED FROM THE SERVER'S CATALOGUE — M2 (e). These used to be
                ten hard-coded <option> tags, and "a status change" was missing
                from them, which is why choice.set could be registered by an API
                call and by nothing a person could do. A kind added by a future
                migration appears here without touching this file. */}
            <select value={form.triggerKind}
              onChange={(e) => setForm({
                ...form, triggerKind: e.target.value, fieldId: '', optionIds: [],
              })}>
              {kinds.map((k) => (
                <option key={k.key} value={k.key}>{k.label}</option>
              ))}
            </select>
          </label>
          {kind?.description && <p className="note">{kind.description}</p>}

          {/* THE CONDITIONS. Which field, and — for a status change — which of
              its statuses count. Matt, 2026-09-06: one trigger with the
              statuses ticked, not one subscription per status. */}
          {kind && kind.field_types.length > 0 && (
            <label>
              {kind.needs_options ? 'Which status field' : 'Which field'}
              <select value={form.fieldId}
                onChange={(e) => setForm({ ...form, fieldId: e.target.value, optionIds: [] })}>
                <option value="">Choose…</option>
                {pickable.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>
          )}

          {kind?.needs_options && pickedField && (
            <fieldset className="autoconds">
              <legend>Fires when it becomes…</legend>
              {pickedField.options.filter((o) => !o.retired).map((o) => (
                <label className="check" key={o.id}>
                  <input
                    type="checkbox"
                    checked={form.optionIds.includes(o.id)}
                    onChange={(e) => setForm({
                      ...form,
                      optionIds: e.target.checked
                        ? [...form.optionIds, o.id]
                        : form.optionIds.filter((x) => x !== o.id),
                    })}
                  />
                  <span className={`badge c-${choiceColor(o.color)}`}>{o.label}</span>
                </label>
              ))}
              <p className="note">
                Tick every status that should fire this. A row landing on any one of them sends
                the change; a row that was already on one and stayed there does not. Each status
                you tick shows a ⚡ in the field editor for as long as this automation is running.
              </p>
            </fieldset>
          )}
          <label>
            Send to
            <input value={form.targetUrl} inputMode="url"
              onChange={(e) => setForm({ ...form, targetUrl: e.target.value })}
              placeholder="https://hooks.example.com/assett" />
          </label>
          <label className="check">
            <input type="checkbox" checked={form.includeValues}
              onChange={(e) => setForm({ ...form, includeValues: e.target.checked })} />
            Include the changed values, not just the field names
          </label>
          <p className="note">
            By default a delivery carries ids, the action, who did it and the NAMES of the fields
            that changed — never what was in them. Ticking the box sends the old and new values as
            well, and the receiver then holds your data: only tick it for a receiver you would be
            comfortable giving a copy to. Values are never stored in the queue either way; they are
            read at the moment of sending, as you, so a delivery can never carry more than you can
            see. The target must be <code>https</code> and must not resolve to a private address —
            that is checked now and again every time something is sent.
          </p>
          {notYet && <p className="note">{notYet}</p>}
          <button className="primary" disabled={busy || notYet !== null}
            onClick={() => void create()}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </fieldset>
      )}

      {canManage && rows && rows.length === 0 && (
        <p className="note">Nothing on this table sends anywhere yet.</p>
      )}

      {canManage && rows?.map((a) => (
        <div className="autorow" key={a.id}>
          <div className="autohead">
            <button className="linky" onClick={() => setOpen(open === a.id ? null : a.id)}>
              {open === a.id ? '▾' : '▸'} {a.name}
            </button>
            <span className="pill">{a.trigger_kind}</span>
            <span className="autourl" title={a.target_url}>{a.target_url}</span>
            <span className={a.is_active ? 'ok' : 'muted'}>{a.is_active ? 'on' : 'paused'}</span>
          </div>
          <div className="autostats">
            <span>{a.delivered} delivered</span>
            <span>{a.pending} pending</span>
            {/* PARKED IS SHOUTED, not tucked away. It means the console gave up
                after the capped attempts and the rows are sitting there. */}
            <span className={Number(a.parked) > 0 ? 'bad' : 'muted'}>{a.parked} parked</span>
            {a.include_values && <span className="pill warn">sends values</span>}
            <button className="mini" onClick={() => void api.updateAutomation(a.id, { isActive: !a.is_active }).then(load).catch(onError)}>
              {a.is_active ? 'Pause' : 'Resume'}
            </button>
            <button className="mini" onClick={() => void api.archiveAutomation(a.id).then(load).catch(onError)}>
              Archive
            </button>
          </div>

          {open === a.id && (
            <table className="deliverylog">
              <thead>
                <tr><th>When</th><th>Event</th><th>Try</th><th>Result</th><th>Detail</th></tr>
              </thead>
              <tbody>
                {log === null && <tr><td colSpan={5}>Loading…</td></tr>}
                {log?.length === 0 && (
                  <tr><td colSpan={5}>
                    Nothing has been sent yet. Change a row in this table and it will appear here.
                  </td></tr>
                )}
                {log?.map((d) => (
                  <tr key={d.id} className={d.ok ? '' : 'bad'}>
                    <td>{new Date(d.at).toLocaleString()}</td>
                    <td>
                      {d.action}
                      {d.changed_fields?.length > 0 && (
                        <span className="muted"> · {d.changed_fields.join(', ')}</span>
                      )}
                    </td>
                    <td>{d.attempt}</td>
                    <td>{d.ok ? `${d.status_code} ok` : (d.status_code ?? 'no reply')}</td>
                    {/* THE ERROR IS SHOWN, not summarised into a red dot. What
                        somebody needs at 7am is the sentence the far end said. */}
                    <td className="err">
                      {d.parked_at ? <strong>parked · </strong> : null}
                      {d.error || d.response || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </Modal>
  );
}

/**
 * M1-E part 2. What the Add user form may offer, by role — the production
 * console.role_scope_ceiling for admin and developer, as measured 2026-09-14 and
 * pinned by migration 0051 section C5. A COPY FOR THE SCREEN ONLY: the ceiling
 * trigger decides, and a box sent above it rolls the whole add back.
 */
const USER_FORM_SCOPES = [
  'console.records:read', 'console.records:write', 'console.schema:read', 'console.readlog:view',
] as const;
const USER_ROLE_CEILING: Record<'admin' | 'developer', readonly string[]> = {
  admin: ['console.records:read', 'console.records:write', 'console.schema:read', 'console.readlog:view'],
  developer: ['console.records:read', 'console.schema:read', 'console.readlog:view'],
};
interface UserDraft { email: string; displayName: string; role: 'admin' | 'developer'; scopes: string[] }
const BLANK_USER_DRAFT: UserDraft = {
  email: '', displayName: '', role: 'admin', scopes: ['console.records:read', 'console.schema:read'],
};

/** A role as a person reads it. `admin` has been "Staff" on screen since M1-R6(c). */
function roleLabel(role: string): string {
  if (role === 'admin') return 'Staff';
  if (role === 'owner_admin') return 'Owner Admin';
  if (role === 'developer') return 'Developer';
  return role.replace('_', ' ');
}

/**
 * WHAT EACH PERSON IS ALLOWED TO DO — M1-11, the permission toggle screen.
 * Shown to a person as USERS since M1-E (2026-09-14), with the list of everyone
 * who can sign in above the toggles.
 *
 * THE PROBLEM IT SOLVES. Every other wall in this console has a screen. The
 * innermost one — which KIND of thing a person may do at all — had a SQL
 * client: handing somebody `console.records:write` meant typing a row into
 * console.identity_scopes in the Supabase table editor. That is not a thing to
 * do from a phone, and it is not a thing to do at all when the person doing it
 * is the one whose time this system exists to save.
 *
 * THREE FACTS A READER OF THIS SCREEN NEEDS, and it says all three on the page
 * rather than assuming them:
 *
 *   1. A SCOPE IS NOT A GRANT. A scope says what kind of thing may be done; a
 *      grant says on which objects. Somebody can hold "Edit rows" and see
 *      nothing at all, because deny-by-default means the grants decide the
 *      objects. Both are needed and neither implies the other.
 *   2. A ROLE CEILING IS THE OUTER LIMIT. What is not on this person's role
 *      ceiling is shown, greyed, with the reason — rather than hidden. A screen
 *      that hides what it cannot do teaches nobody why it cannot.
 *   3. TAKING ONE AWAY IS IMMEDIATE at the next sign-in and not before. The
 *      scope set is minted at the door, so somebody signed in right now keeps
 *      what they had until their session ends. That is worth saying out loud on
 *      a screen whose entire purpose is to take permissions away.
 *
 * HIDING THIS BUTTON IS A COURTESY AND NOT A WALL. The route asks for
 * users:manage again, refuses any caller that is not a live browser session,
 * and the RLS policies and the ceiling trigger ask again underneath that.
 */
function PeoplePanel({ onCancel, onError }: { onCancel: () => void; onError: (e: unknown) => void }) {
  const [people, setPeople] = useState<IdentityRow[] | null>(null);
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [who, setWho] = useState<string>('');
  const [detail, setDetail] = useState<IdentityScopes | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api.identities()
      .then((r) => {
        setPeople(r.identities);
        setWho((w) => w || (r.identities[0]?.id ?? ''));
      })
      .catch((e) => { onError(e); setPeople([]); });
  }, [onError]);

  // M1-E. The list of who EXISTS. A separate read from `people` on purpose:
  // that one is who may be EDITED, and never holds the Owner Admin or anyone
  // turned off. This one shows both, marked.
  useEffect(() => {
    api.users().then((r) => setUsers(r.users)).catch((e) => { onError(e); setUsers([]); });
  }, [onError]);

  // M1-E part 2 (migration 0051): adding a person, and turning one off or on.
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<UserDraft>(BLANK_USER_DRAFT);
  const [offAsk, setOffAsk] = useState<string | null>(null);
  const [userBusy, setUserBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const reloadUsers = useCallback(() => {
    api.users().then((r) => setUsers(r.users)).catch(onError);
    api.identities().then((r) => setPeople(r.identities)).catch(onError);
  }, [onError]);

  async function addUser() {
    if (userBusy) return;
    setUserBusy('add');
    setSaid(null);
    try {
      const ceiling = USER_ROLE_CEILING[draft.role];
      const r = await api.createUser({
        email: draft.email,
        displayName: draft.displayName,
        role: draft.role,
        scopes: draft.scopes.filter((s) => ceiling.includes(s)),
      });
      setSaid(`Added ${r.user.google_email}. They can sign in with that Google account now.`);
      setDraft(BLANK_USER_DRAFT);
      setAdding(false);
      reloadUsers();
    } catch (e) { onError(e); } finally { setUserBusy(null); }
  }

  // TURNING SOMEBODY OFF TAKES TWO TAPS, and not a confirm() box. A native
  // dialog freezes the page for the browser drive that proves this screen, and
  // on a phone it is easy to dismiss without reading. The first tap changes the
  // button's words; the second does it. Turning back on is one tap.
  async function setTurnedOff(u: UserRow, off: boolean) {
    if (userBusy) return;
    if (off && offAsk !== u.id) { setOffAsk(u.id); return; }
    setOffAsk(null);
    setUserBusy(u.id);
    setSaid(null);
    try {
      if (off) await api.turnUserOff(u.id); else await api.turnUserOn(u.id);
      const name = u.display_name || u.google_email;
      setSaid(off
        ? `${name} is turned off. Their next click in the console signs them out.`
        : `${name} is on again, with the permissions they had before.`);
      reloadUsers();
    } catch (e) { onError(e); } finally { setUserBusy(null); }
  }

  const editable = new Set((people ?? []).map((p) => p.id));

  const load = useCallback(() => {
    if (!who) { setDetail(null); return; }
    api.identityScopes(who).then(setDetail).catch((e) => { onError(e); setDetail(null); });
  }, [who, onError]);

  useEffect(load, [load]);

  async function toggle(row: ScopeAllowance) {
    if (busy) return;
    setBusy(row.scope);
    try {
      if (row.granted) await api.revokeScope(who, row.scope);
      else await api.grantScope(who, row.scope);
      load();
    } catch (e) { onError(e); } finally { setBusy(null); }
  }

  const ceiling = (detail?.scopes ?? []).filter((s) => s.on_ceiling);
  const above = (detail?.scopes ?? []).filter((s) => !s.on_ceiling);

  return (
    <Modal title="Users · who can sign in, and what each may do" onCancel={onCancel} wide>
      <p className="muted" style={{ marginTop: 0 }}>
        Everyone who can sign in to this console. Which tables each person can actually see
        is set with the <strong>⚿ key</strong> beside each workspace, base or table — somebody
        can hold “Edit rows” and still see nothing until they are given something to edit.
      </p>

      {/*
        STACKED, NOT A TABLE, AND THAT IS THE PHONE AGAIN. An email address is the
        longest unbreakable thing on this screen, and M1-C's fixed-layout table
        painted labels over each other at 390px. Each person is a block that
        wraps; nothing here has a width to overflow.
      */}
      <div style={{ margin: '6px 0 14px' }}>
        {users === null && <p className="muted">Loading…</p>}
        {(users ?? []).map((u) => (
          <div
            key={u.id}
            style={{
              padding: '8px 0', borderBottom: '1px solid var(--line)',
              opacity: u.revoked_at ? 0.55 : 1, minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', alignItems: 'baseline' }}>
              <strong style={{ overflowWrap: 'anywhere' }}>{u.display_name || u.google_email}</strong>
              <span className="muted">{roleLabel(u.role)}</span>
              <span className="muted">{u.revoked_at ? 'Turned off' : 'On'}</span>
            </div>
            <div className="muted" style={{ overflowWrap: 'anywhere' }}>{u.google_email}</div>
            <div className="muted" style={{ overflowWrap: 'anywhere', fontSize: 12 }}>
              {u.role === 'owner_admin'
                ? 'Everything — the Owner Admin'
                : u.scopes.length > 0
                  ? u.scopes.map((s) => SCOPE_LABELS[s]?.label ?? s).join(' · ')
                  : 'Nothing yet'}
            </div>
            {editable.has(u.id) && (
              <button
                className="signout"
                style={{ marginTop: 4 }}
                onClick={() => setWho(u.id)}
                aria-pressed={who === u.id}
              >
                {who === u.id ? 'Shown below' : 'Change what they can do'}
              </button>
            )}
            {/* Never offered for the Owner Admin — the database refuses it too. */}
            {u.role !== 'owner_admin' && (
              <button
                className="signout"
                style={{ marginTop: 4, marginLeft: editable.has(u.id) ? 8 : 0 }}
                onClick={() => void setTurnedOff(u, !u.revoked_at)}
              >
                {userBusy === u.id
                  ? 'Saving…'
                  : u.revoked_at
                    ? 'Turn on'
                    : offAsk === u.id ? 'Tap again to turn off' : 'Turn off'}
              </button>
            )}
          </div>
        ))}
      </div>

      {said && <p className="note" role="status" style={{ margin: '0 0 12px' }}>{said}</p>}

      {!adding && (
        <button className="signout" style={{ margin: '0 0 14px' }}
          onClick={() => { setAdding(true); setSaid(null); setOffAsk(null); }}>
          Add a user
        </button>
      )}
      {adding && (
        <fieldset style={{ margin: '0 0 14px', minWidth: 0 }}>
          <legend>Add a user</legend>
          <label>
            Google email
            <input value={draft.email} inputMode="email" autoCapitalize="none" autoCorrect="off"
              spellCheck={false} placeholder="name@assettservices.com"
              onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </label>
          <label>
            Display name
            <input value={draft.displayName} placeholder="Their name"
              onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} />
          </label>
          <label>
            Role
            <select value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value as UserDraft['role'] })}>
              <option value="admin">Staff</option>
              <option value="developer">Developer</option>
            </select>
          </label>
          {/*
            THE FOUR BOXES, AT THE ROLE'S CEILING. "Hand out access" is not one of
            them and never will be on this form: it is above both ceilings
            (Decision 8dcbb633 ruling 3). A box the role cannot hold is shown
            greyed with the reason rather than hidden, and the ceiling trigger in
            the database refuses it regardless of what this screen sends.
          */}
          {USER_FORM_SCOPES.map((scope) => {
            const allowed = USER_ROLE_CEILING[draft.role].includes(scope);
            return (
              <label key={scope} className="check" style={{ opacity: allowed ? 1 : 0.5 }}>
                <input type="checkbox" style={{ width: 'auto' }} disabled={!allowed}
                  checked={allowed && draft.scopes.includes(scope)}
                  onChange={(e) => setDraft({
                    ...draft,
                    scopes: e.target.checked
                      ? [...draft.scopes, scope]
                      : draft.scopes.filter((s) => s !== scope),
                  })} />
                {SCOPE_LABELS[scope]?.label ?? scope}
                {!allowed && <span className="muted"> — not available to Developers</span>}
              </label>
            );
          })}
          <p className="note">
            Use the exact Google account they sign in with. A typo is not an error — it makes a
            login nobody can use. Adding someone gives them no tables yet: give them some with
            the <strong>⚿ key</strong> beside a workspace, base or table.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="primary" disabled={userBusy !== null || draft.email.trim() === ''}
              onClick={() => void addUser()}>
              {userBusy === 'add' ? 'Adding…' : 'Add user'}
            </button>
            <button className="action" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </fieldset>
      )}

      <label htmlFor="p-who">Change what a person may do</label>
      <select id="p-who" value={who} onChange={(e) => setWho(e.target.value)}>
        {(people ?? []).map((i) => (
          <option key={i.id} value={i.id}>
            {i.display_name || i.google_email} · {i.role === 'admin' ? 'Staff' : i.role.replace('_', ' ')}
          </option>
        ))}
      </select>
      {people && people.length === 0 && (
        <p className="muted">Nobody but you has an identity on this console yet.</p>
      )}

      {detail && (
        <>
          {/*
            THE COLUMN WIDTHS ARE EXPLICIT, AND THEY ARE HERE BECAUSE OF A PHONE.

            `.grants` is `table-layout: fixed`, so without a colgroup the three
            columns split evenly — and on a phone that gives the first one about
            110px, which is narrower than "See the read log" renders in the mono
            face. Matt opened this on his phone and the labels were painted on
            top of the descriptions.
          */}
          <table className="grants" style={{ marginTop: 14 }}>
            <colgroup>
              <col style={{ width: '36%' }} />
              <col style={{ width: '40%' }} />
              <col style={{ width: '24%' }} />
            </colgroup>
            <thead>
              <tr><th>Allowed</th><th>What it lets them do</th><th>Since</th></tr>
            </thead>
            <tbody>
              {ceiling.map((row) => {
                const gloss = SCOPE_LABELS[row.scope];
                return (
                  <tr key={row.scope}>
                    {/*
                      NO `white-space: nowrap` HERE. It was, and that is exactly
                      the defect: a fixed-layout cell cannot grow, so a label
                      forbidden to wrap overflows and paints over the column
                      beside it. The label wraps; the checkbox stays at the top
                      of it rather than drifting to the middle of two lines.
                    */}
                    <td className="perm" style={{ verticalAlign: 'top' }}>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', margin: 0 }}>
                        {/*
                          NOT `disabled` WHILE IN FLIGHT, and that is deliberate.
                          Driving this screen in a browser showed the toggle
                          landing and the focus vanishing: a disabled input is
                          dropped by the document, so a keyboard user tabbing
                          down this list loses their place on every tick. The
                          double-press it was guarding against is already
                          refused by `busy` in the handler, which costs nothing
                          and takes no focus away.
                        */}
                        <input
                          type="checkbox"
                          style={{
                            width: 'auto', flex: 'none', marginTop: 2,
                            opacity: busy === row.scope ? 0.5 : 1,
                          }}
                          checked={row.granted}
                          onChange={() => void toggle(row)}
                        />
                        {gloss?.label ?? row.scope}
                      </label>
                    </td>
                    <td className="muted" style={{ verticalAlign: 'top' }}>
                      {gloss?.plain ?? row.description}
                    </td>
                    <td className="muted" style={{ verticalAlign: 'top' }}>
                      {row.granted ? (row.granted_by || fmtTime(row.granted_at) || '—') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <p className="muted" style={{ marginTop: 10 }}>
            A change here lands at their <strong>next sign-in</strong>. Somebody signed in right
            now keeps what they had until their session ends.
          </p>

          {above.length > 0 && (
            <div className="notice scope" style={{ margin: '12px 0 0' }}>
              <h2>Not available to {detail.identity.role === 'admin' ? 'Staff' : 'this role'}</h2>
              <p className="muted" style={{ margin: '0 0 6px' }}>
                These are above the role’s ceiling, so there is no toggle to offer — raising a
                person past it means raising the ROLE, which is a decision about everybody with
                that role rather than about one person.
              </p>
              <p className="muted" style={{ margin: 0 }}>
                {above.map((s) => SCOPE_LABELS[s.scope]?.label ?? s.scope).join(' · ')}
              </p>
            </div>
          )}
        </>
      )}

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Close</button>
      </div>
    </Modal>
  );
}

/**
 * Who can see this workspace, base or table — and, for a table, the "own rows
 * only" field.
 *
 * TWO THINGS THIS SCREEN IS HONEST ABOUT.
 *
 * The first is that `console.users:manage` is not minted to everybody. RULING 3
 * (migration 0015) made it mintable for the OWNER ADMIN and for no other role,
 * so for anybody else this panel is a READER: it shows the grants and says, in
 * a sentence, why the controls are not there. Hiding the panel entirely would
 * have been easier and worse — "who can see this" is worth being able to answer
 * even when you cannot change the answer.
 *
 * The second is that specificity beats union. A grant at table level does not
 * add to one at base level, it REPLACES it — most specific wins, and the first
 * match wins outright. A table grant that omits `can_edit` therefore takes edit
 * away, which is the whole reason to write one, and is the single most
 * surprising thing about this model if nobody tells you.
 */
function AccessDialog({
  target, me, onCancel, onError,
}: {
  target: { level: GrantLevel; id: string; name: string };
  me: Me;
  onCancel: () => void;
  onError: (e: unknown) => void;
}) {
  const canManage = me.scopes.includes('console.users:manage');
  const [grants, setGrants] = useState<GrantRow[] | null>(null);
  const [identities, setIdentities] = useState<IdentityRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [subject, setSubject] = useState<{ kind: 'identity' | 'role'; value: string }>({ kind: 'identity', value: '' });
  const [perms, setPerms] = useState<Record<string, boolean>>({ canView: true });
  const [ownRowsOnly, setOwnRowsOnly] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!canManage) { setGrants([]); return; }
    api.grants(target.level, target.id).then((r) => setGrants(r.grants)).catch((e) => { onError(e); setGrants([]); });
  }, [canManage, target.level, target.id, onError]);

  useEffect(load, [load]);
  useEffect(() => {
    if (!canManage) return;
    api.identities().then((r) => {
      setIdentities(r.identities);
      setSubject((s) => (s.value ? s : { kind: 'identity', value: r.identities[0]?.id ?? '' }));
    }).catch(onError);
  }, [canManage, onError]);

  async function submit() {
    if (busy || !subject.value) return;
    setBusy(true);
    try {
      await api.createGrant({
        ...(subject.kind === 'identity' ? { identityId: subject.value } : { role: subject.value }),
        level: target.level,
        objectId: target.id,
        canView: !!perms['canView'], canEdit: !!perms['canEdit'], canCreate: !!perms['canCreate'],
        canDelete: !!perms['canDelete'], canManage: !!perms['canManage'],
        ownRowsOnly, note,
      });
      setAdding(false);
      setPerms({ canView: true });
      setOwnRowsOnly(false);
      setNote('');
      load();
    } catch (e) { onError(e); } finally { setBusy(false); }
  }

  return (
    <Modal title={`Access · ${target.name}`} onCancel={onCancel} wide>
      <p className="muted" style={{ marginTop: 0 }}>
        Grants on this {target.level}. Deny by default: what is not listed here is not allowed.
        A grant at a deeper level <strong>replaces</strong> this one rather than adding to it —
        most specific wins, and it wins outright.
      </p>

      {!canManage && (
        <div className="notice scope" style={{ margin: '10px 0' }}>
          <h2>Your session cannot read or change grants</h2>
          <p style={{ margin: '0 0 6px' }}>
            This needs <code>console.users:manage</code>, which sits on the Owner Admin role
            ceiling and on no other. It is not that you have not been given it — it is that
            your role may never hold it.
          </p>
          <p className="muted" style={{ margin: 0 }}>
            Handing out access is the Owner Admin&rsquo;s, from the browser, on this screen and
            on Users.
          </p>
        </div>
      )}

      {canManage && (
        <>
          <table className="grants">
            <thead>
              <tr>
                <th>Who</th>
                {PERMISSIONS.map((p) => <th key={p.key} title={p.hint}>{p.label}</th>)}
                <th>Own rows</th><th>Note</th><th></th>
              </tr>
            </thead>
            <tbody>
              {(grants ?? []).map((g) => (
                <tr key={g.id} style={g.revoked_at ? { opacity: 0.45 } : undefined}>
                  <td>
                    {g.role
                      ? <>every <strong>{g.role.replace('_', ' ')}</strong></>
                      : (g.display_name || g.google_email || 'unknown')}
                    {g.revoked_at ? <span className="muted"> · revoked</span> : null}
                  </td>
                  {PERMISSIONS.map((p) => (
                    <td key={p.key} className="perm">
                      {(g as unknown as Record<string, boolean>)[p.column] ? '●' : '·'}
                    </td>
                  ))}
                  <td className="perm">{g.own_rows_only ? '●' : '·'}</td>
                  <td className="muted">{g.note}</td>
                  <td>
                    {!g.revoked_at && (
                      <button
                        className="action"
                        onClick={async () => {
                          try { await api.revokeGrant(g.id); load(); } catch (e) { onError(e); }
                        }}
                      >Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
              {grants && grants.length === 0 && (
                <tr><td colSpan={9} className="muted" style={{ padding: 12 }}>
                  No grants on this {target.level}. Only an Owner Admin can see it.
                </td></tr>
              )}
            </tbody>
          </table>

          {!adding && (
            <button className="action" style={{ marginTop: 12 }} onClick={() => setAdding(true)}>
              Add a grant
            </button>
          )}

          {adding && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <label htmlFor="g-who">Who</label>
              <select
                id="g-who"
                value={`${subject.kind}:${subject.value}`}
                onChange={(e) => {
                  const [kind, ...rest] = e.target.value.split(':');
                  setSubject({ kind: kind as 'identity' | 'role', value: rest.join(':') });
                }}
              >
                <optgroup label="A person">
                  {identities.map((i) => (
                    <option key={i.id} value={`identity:${i.id}`}>
                      {i.display_name || i.google_email} · {i.role.replace('_', ' ')}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Everyone with a role">
                  <option value="role:admin">every admin</option>
                  <option value="role:developer">every developer</option>
                </optgroup>
              </select>

              <label>Permissions</label>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {PERMISSIONS.map((p) => (
                  <label key={p.key} title={p.hint} style={{ display: 'flex', gap: 5, alignItems: 'center', margin: 0 }}>
                    <input
                      type="checkbox"
                      style={{ width: 'auto' }}
                      checked={!!perms[p.key]}
                      onChange={(e) => setPerms({ ...perms, [p.key]: e.target.checked })}
                    />
                    {p.label}
                  </label>
                ))}
              </div>

              {target.level === 'table' && (
                <label style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 10 }}>
                  <input
                    type="checkbox" style={{ width: 'auto' }}
                    checked={ownRowsOnly}
                    onChange={(e) => setOwnRowsOnly(e.target.checked)}
                  />
                  Own rows only
                </label>
              )}

              <label htmlFor="g-note">Note</label>
              <input id="g-note" value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Why this person has this" />

              <div className="modal-actions">
                <button className="action" onClick={() => setAdding(false)}>Cancel</button>
                <button className="action on" disabled={busy || !subject.value} onClick={() => void submit()}>
                  {busy ? 'Adding…' : 'Add grant'}
                </button>
              </div>
            </div>
          )}

          {target.level === 'table' && <OwnRowsField tableId={target.id} onError={onError} />}
          {target.level === 'table' && <ShareTitlesSwitch tableId={target.id} onError={onError} />}
        </>
      )}

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Close</button>
      </div>
    </Modal>
  );
}

/**
 * The "own rows only" field picker.
 *
 * MIGRATION 0050 (Ruling a9da2fb8 item 8) MADE THIS CHOICE MEAN SOMETHING. It was
 * stored and read by nothing; now console.own_row_ids() reads it for every
 * record, history and attachment question an own-rows grant asks. Two kinds of
 * field can drive it, and the list offers only those two, because the database
 * refuses the rest (console.guard_table_update) and a refusal after the choice
 * is a worse screen than a list that never offered it:
 *
 *   Text  a row is someone's when this column holds their sign-in email
 *   Link  a row is someone's when it links to a row that is theirs on the other
 *         table, which therefore needs a Text rule of its own first
 *
 * IT SHOWS WHAT IS SAVED. It used to open on "none" whatever the table held, so
 * the one screen that sets a permission rule could not say what the rule was.
 * A refusal puts the old choice back and shows the database's own sentence.
 */
function OwnRowsField({ tableId, onError }: { tableId: string; onError: (e: unknown) => void }) {
  const [fields, setFields] = useState<Field[]>([]);
  const [current, setCurrent] = useState<string>('');
  const [saved, setSaved] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    api.table(tableId).then((r) => {
      setFields(r.table.fields);
      setCurrent(r.table.own_rows_field_id ?? '');
    }).catch(onError);
  }, [tableId, onError]);

  const choices = fields.filter((f) => f.type === 'text' || f.type === 'link');
  const chosen = fields.find((f) => f.id === current);
  const rule = chosen?.type === 'link'
    ? `A row is someone’s when its “${chosen.name}” links to a row that is theirs on that table.`
    : chosen?.type === 'text'
      ? `A row is someone’s when “${chosen.name}” holds their sign-in email.`
      : 'A row is someone’s when they created it.';

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <label htmlFor="own-rows">The “own rows only” field</label>
      <select
        id="own-rows"
        value={current}
        onChange={async (e) => {
          const v = e.target.value;
          const before = current;
          setCurrent(v);
          setSaved(false);
          setRefusal(null);
          try {
            await api.setOwnRowsField(tableId, v || null);
            setSaved(true);
          } catch (err) {
            setCurrent(before);
            setRefusal(err instanceof Error ? err.message : String(err));
          }
        }}
      >
        <option value="">— none: whoever created the row —</option>
        {choices.map((f) => (
          <option key={f.id} value={f.id}>{f.name} · {f.type === 'link' ? 'Link' : 'Text'}</option>
        ))}
      </select>
      <p className="muted" style={{ fontSize: 12 }}>
        {rule} Only used by grants that have “own rows only” ticked. {saved ? 'Saved.' : ''}
      </p>
      {refusal && <p className="muted bad" role="alert" style={{ fontSize: 12 }}>{refusal}</p>}
    </div>
  );
}

/**
 * "SHARE TITLES CONSOLE-WIDE" — CROSS-LINKS spec v1.0, migration 0052.
 *
 * Matt's ruling R-XL-1: a link chip shows the target row's TITLE and nothing
 * else, and whether someone who cannot read this table sees that title is this
 * switch, per table, default OFF. It is an ACCESS decision (ruling Q1), so it
 * sits here beside the grants and needs console.users:manage; the server refuses
 * any caller but a browser session, and the database refuses anyone but the
 * Owner Admin, each in its own words.
 *
 * IT SHOWS WHAT IS SAVED, and a refusal puts the old state back with the
 * server's sentence — the lesson OwnRowsField above learned the hard way.
 */
function ShareTitlesSwitch({ tableId, onError }: { tableId: string; onError: (e: unknown) => void }) {
  const [on, setOn] = useState<boolean | null>(null);
  const [saved, setSaved] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    api.table(tableId).then((r) => setOn(r.table.share_titles_console_wide === true)).catch(onError);
  }, [tableId, onError]);

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <label htmlFor="share-titles" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          id="share-titles"
          type="checkbox"
          checked={on === true}
          disabled={on === null}
          onChange={async (e) => {
            const next = e.target.checked;
            const before = on;
            setOn(next);
            setSaved(false);
            setRefusal(null);
            try {
              await api.setShareTitles(tableId, next);
              setSaved(true);
            } catch (err) {
              setOn(before);
              setRefusal(err instanceof Error ? err.message : String(err));
            }
          }}
        />
        Share titles console-wide
      </label>
      <p className="muted" style={{ fontSize: 12 }}>
        {on
          ? 'On: anyone who can read a table that links here sees each linked row’s title — this table’s first column, nothing else — and can pick these rows when linking from another base. They still cannot open a row, see its other columns or read its history.'
          : 'Off: a linked row’s title is shown only to people who can read this table themselves. Everyone else sees “restricted”.'}
        {saved ? ' Saved.' : ''}
      </p>
      {refusal && <p className="muted bad" role="alert" style={{ fontSize: 12 }}>{refusal}</p>}
    </div>
  );
}

/**
 * The drag handle on a column edge.
 *
 * Pointer events rather than mouse events, so a trackpad, a mouse and a touch
 * screen all behave the same; pointer capture, so the drag survives the cursor
 * leaving the 5px handle, which it always does.
 */
function ColumnResizer({ width, onResize }: { width?: number; onResize: (px: number) => void }) {
  return (
    <span
      className="resizer"
      title="Drag to resize"
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const th = (e.currentTarget as HTMLElement).closest('th');
        const startX = e.clientX;
        const startW = width ?? th?.getBoundingClientRect().width ?? 160;
        const el = e.currentTarget as HTMLElement;
        el.setPointerCapture(e.pointerId);
        const move = (ev: PointerEvent) => {
          onResize(Math.max(60, Math.round(startW + (ev.clientX - startX))));
        };
        const up = () => {
          el.removeEventListener('pointermove', move);
          el.removeEventListener('pointerup', up);
        };
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', up);
      }}
    />
  );
}

/** Building a filter. Every rule is ANDed — spec v1 S6 asks for filtering, not for a query language. */
function FilterDialog({
  fields, filter, onCancel, onApply,
}: {
  fields: Field[];
  filter: FilterRule[];
  onCancel: () => void;
  onApply: (f: FilterRule[]) => void;
}) {
  const [rules, setRules] = useState<FilterRule[]>(filter.length ? filter : []);

  function set(i: number, patch: Partial<FilterRule>) {
    setRules((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  }

  return (
    <Modal title="Filter" onCancel={onCancel} wide>
      {rules.length === 0 && (
        <p className="muted" style={{ marginTop: 0 }}>No filter. Every row is shown.</p>
      )}
      {rules.map((r, i) => {
        const op = FILTER_OPS.find((o) => o.op === r.op);
        return (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span className="muted" style={{ width: '2.4rem', fontSize: 12 }}>{i === 0 ? 'where' : 'and'}</span>
            <select value={r.field} onChange={(e) => set(i, { field: e.target.value })} style={{ flex: 1 }}>
              {fields.map((f) => <option key={f.id} value={f.slug}>{f.name}</option>)}
            </select>
            <select value={r.op} onChange={(e) => set(i, { op: e.target.value as FilterRule['op'] })} style={{ flex: 1 }}>
              {FILTER_OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
            </select>
            <input
              value={r.value}
              disabled={!op?.needsValue}
              placeholder={op?.needsValue ? 'value' : '—'}
              onChange={(e) => set(i, { value: e.target.value })}
              style={{ flex: 1 }}
            />
            <button className="action" onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}>Remove</button>
          </div>
        );
      })}

      <button
        className="action"
        style={{ marginTop: 8 }}
        disabled={fields.length === 0}
        onClick={() => setRules((r) => [...r, { field: fields[0]!.slug, op: 'contains', value: '' }])}
      >
        Add a rule
      </button>

      <p className="muted" style={{ fontSize: 12 }}>
        Filtering happens in the database, not in this page — the grid pages at 200 rows, and a
        filter applied to the loaded page would quietly be a filter over the wrong set.
      </p>

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" onClick={() => onApply(rules.filter((r) => r.field))}>Apply</button>
      </div>
    </Modal>
  );
}

/**
 * SORT — C2-12's other half.
 *
 * WHY IT IS A LIST AND NOT A DROPDOWN. The server has always taken an ordered
 * list of rules; the header click could only ever produce one. "Owner, then
 * newest first" is the ordinary case the console could not express — and the
 * ORDER of the rules is the part a dropdown cannot say at all, which is why the
 * rows can be moved up and down rather than only added and removed.
 *
 * THE BUILT-IN COLUMNS ARE OFFERED HERE TOO. Matt asked for them by name on
 * 2026-08-29 — "I would like to be able to sort on created date and updated date
 * but those options aren't showing" — and the header menu now has them. They are
 * not fields, so they arrive separately and are labelled as what they are.
 *
 * A FIELD CAN ONLY APPEAR ONCE. Two rules on one column is not an error the
 * server would refuse — the second is simply dead, because the first has already
 * decided every comparison — so it is prevented here, where it can be explained,
 * rather than accepted and quietly ignored.
 */
function SortDialog({
  fields, builtIns, sort, onCancel, onApply,
}: {
  fields: Field[];
  builtIns: readonly { slug: string; label: string }[];
  sort: SortRule[];
  onCancel: () => void;
  onApply: (s: SortRule[]) => void;
}) {
  const [rules, setRules] = useState<SortRule[]>(sort);

  const columns = useMemo(
    () => [
      ...fields.map((f) => ({ slug: f.slug, label: f.name })),
      ...builtIns.map((b) => ({ slug: b.slug, label: `${b.label} (built in)` })),
    ],
    [fields, builtIns],
  );

  const labelFor = (slug: string): string =>
    columns.find((c) => c.slug === slug)?.label ?? slug;

  const unused = columns.filter((c) => !rules.some((r) => r.field === c.slug));

  function set(i: number, patch: Partial<SortRule>): void {
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }
  function move(i: number, by: number): void {
    setRules((rs) => {
      const j = i + by;
      if (j < 0 || j >= rs.length) return rs;
      const out = rs.slice();
      [out[i], out[j]] = [out[j]!, out[i]!];
      return out;
    });
  }

  return (
    <Modal title="Sort" onCancel={onCancel} wide>
      {rules.length === 0 && (
        <p className="muted" style={{ marginTop: 0 }}>
          No sort. Rows come back oldest first, which is the order the table has when nobody
          has asked for another one.
        </p>
      )}

      {rules.map((r, i) => (
        <div key={r.field} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <span className="muted" style={{ width: '3.6rem', fontSize: 12 }}>
            {i === 0 ? 'sort by' : 'then by'}
          </span>
          <select
            value={r.field}
            onChange={(e) => set(i, { field: e.target.value })}
            style={{ flex: 1 }}
          >
            {/* The rule's own column, plus the ones no other rule has taken. */}
            <option value={r.field}>{labelFor(r.field)}</option>
            {unused.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <select
            value={r.dir}
            onChange={(e) => set(i, { dir: e.target.value as SortRule['dir'] })}
            style={{ flex: 1 }}
          >
            <option value="asc">first to last (A→Z, oldest first)</option>
            <option value="desc">last to first (Z→A, newest first)</option>
          </select>
          <button className="action" disabled={i === 0} onClick={() => move(i, -1)} title="Sort by this one earlier">↑</button>
          <button className="action" disabled={i === rules.length - 1} onClick={() => move(i, 1)} title="Sort by this one later">↓</button>
          <button className="action" onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}

      <button
        className="action"
        style={{ marginTop: 8 }}
        disabled={unused.length === 0}
        onClick={() => setRules((rs) => [...rs, { field: unused[0]!.slug, dir: 'asc' }])}
      >
        Add a level
      </button>

      <p className="muted" style={{ fontSize: 12 }}>
        Sorting happens in the database over the whole table, not over the rows already loaded.
        Changing it starts the pager again from the first page, because a cursor belongs to the
        order it was issued under.
      </p>

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" onClick={() => onApply(rules)}>Apply</button>
      </div>
    </Modal>
  );
}

/**
 * Saving the current sort, filter, hidden columns and widths under a name.
 *
 * C2-E: AND DECIDING WHO ELSE SEES IT. Matt ruled the share on 2026-09-03
 * (Decision recUvf7AKmW5b75eG, option 1) and migration 0032 made it real. The
 * checkbox is the only place `visibility` is ever set, and it is sent only when
 * this dialog is used — an absent value means "leave it alone" on the route, so
 * nothing else in the grid can un-share a view by re-saving it.
 *
 * THE CLASH CHECK IS SCOPED TO THIS PERSON'S OWN VIEWS, and it has to be. The
 * list now contains other people's shared views, the unique key in the database
 * is (table, OWNER, name), and a colleague's "Open items" does not collide with
 * yours. Left unscoped, this dialog would have offered to "Overwrite" a view it
 * cannot touch and then quietly created a second one beside it — a wrong word on
 * the button and a wrong result behind it.
 */
function SaveViewDialog({
  existing, mineOnly, onCancel, onSave,
}: {
  existing: SavedView[];
  /** Only these are mine; the rest of `existing` is shared with me. */
  mineOnly: SavedView[];
  onCancel: () => void;
  onSave: (name: string, visibility: 'private' | 'base') => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const trimmed = name.trim();
  const clash = mineOnly.some((v) => v.name === trimmed);
  const theirs = !clash && existing.some((v) => v.name === trimmed);
  const save = () => onSave(trimmed, shared ? 'base' : 'private');

  return (
    <Modal title="Save view" onCancel={onCancel}>
      <label htmlFor="v-name">Name</label>
      <input
        id="v-name" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && trimmed) save(); }}
        placeholder="Open items, newest first"
      />
      {clash && (
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          You already have a view with that name. Saving will overwrite it.
        </p>
      )}
      {theirs && (
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Somebody else has a shared view with that name. Yours will be saved beside theirs,
          not over it.
        </p>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <input
          type="checkbox" checked={shared}
          onChange={(e) => setShared(e.target.checked)}
        />
        Share with everyone who can see this table
      </label>
      <p className="muted" style={{ fontSize: 12 }}>
        A view is a saved sort, filter, hidden columns and widths — <strong>not rows</strong>.
        Sharing it puts it in the view list of everybody who can already read this table;
        each of them still sees only the records they are allowed to see, so the same view can
        show them a different number of rows. Only you can rename, re-save or delete it.
      </p>
      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" disabled={!trimmed} onClick={save}>
          {clash ? 'Overwrite' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Adding a field, and its options if it needs them.
 *
 * A `single_select`, `multi_select` or `status` field with no options is a
 * column you cannot put a value in — the row trigger validates against the
 * option list, so every write would be refused. So the options are collected
 * here, in the same dialog, and added straight after the field. That is two
 * round trips rather than one, and it is worth it: `add_field` and
 * `add_field_option` are separate SECURITY DEFINER functions with separate
 * audit lines, and collapsing them into one call in the browser would hide
 * which half failed.
 *
 * Only the types C1 can build are offered. `link` and `attachment` (C2) and `ai`
 * (C3) exist in the enum and are refused by the database with a message naming
 * the phase that owns them.
 */
function AddFieldDialog({
  tableId, tableName, baseId, existing, at, onCancel, onCreated, onError,
}: {
  tableId: string;
  tableName: string;
  /** The base this table is in — the default place a link points. Since 0052 a link may point at a table in any base. */
  baseId: string;
  existing: Field[];
  /** Where to put it — this is what "insert left" and "insert right" are. */
  at?: number;
  onCancel: () => void;
  onCreated: () => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [type, setType] = useState<string>('text');
  const [description, setDescription] = useState('');
  const [optionText, setOptionText] = useState('');
  const [busy, setBusy] = useState(false);

  // The tables a link may point at: the CHOSEN base's, and never a system table.
  // This base by default; since 0052 (CROSS-LINKS spec) any base this person can
  // see. The list is fetched rather than passed in because a base can gain a
  // table while this dialog is open, and a stale list would silently make the
  // new one unlinkable.
  const [targetBase, setTargetBase] = useState(baseId);
  const [bases, setBases] = useState<{ id: string; name: string; workspace: string }[]>([]);
  const [siblings, setSiblings] = useState<TableSummary[] | null>(null);
  const [target, setTarget] = useState('');
  const [allowMultiple, setAllowMultiple] = useState(true);

  useEffect(() => {
    let live = true;
    api.workspaces()
      .then(async (w) => {
        const all: { id: string; name: string; workspace: string }[] = [];
        for (const ws of w.workspaces) {
          const b = await api.bases(ws.id);
          for (const x of b.bases) all.push({ id: x.id, name: x.name, workspace: ws.name });
        }
        if (live) setBases(all);
      })
      .catch(onError);
    return () => { live = false; };
  }, [onError]);

  useEffect(() => {
    let live = true;
    setSiblings(null);
    api.tables(targetBase)
      .then((r) => { if (live) setSiblings(r.tables.filter((t) => !t.system_source)); })
      .catch(onError);
    return () => { live = false; };
  }, [targetBase, onError]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugOk = SLUG_RE.test(effectiveSlug);
  const clashes = existing.some((f) => f.slug === effectiveSlug);
  const needsOptions = type === 'single_select' || type === 'multi_select' || type === 'status';
  const isLink = type === 'link';
  const options = optionText.split('\n').map((s) => s.trim()).filter(Boolean);
  const ready = !!name.trim() && slugOk && !clashes
    && (!needsOptions || options.length > 0)
    && (!isLink || !!target);

  async function submit() {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const r = await api.createField({
        tableId, slug: effectiveSlug, name: name.trim(), type, description,
        ...(at === undefined ? {} : { position: at }),
        ...(isLink ? { targetTableId: target, allowMultiple } : {}),
      });
      // Colours walk the palette rather than all landing on grey: a status field
      // whose choices are nine identical badges is the thing the badge picker
      // exists to stop being.
      let i = 0;
      for (const label of options) {
        await api.addOption({
          fieldId: r.id, value: slugify(label) || label, label,
          color: CHOICE_COLORS[(i++ % (CHOICE_COLORS.length - 1)) + 1]!,
        });
      }
      onCreated();
    } catch (e) {
      onError(e);
      onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Add field" onCancel={onCancel}>
      <p className="muted" style={{ marginTop: 0 }}>
        To <strong>{tableName}</strong>
        {at === undefined ? ', at the end.' : `, in position ${at}.`}
      </p>

      <label htmlFor="f-name">Name</label>
      <input id="f-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Due on" />

      <label htmlFor="f-slug">Slug</label>
      <input
        id="f-slug" value={effectiveSlug}
        onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
      />
      {effectiveSlug && !slugOk && (
        <p className="muted bad" style={{ fontSize: 12, marginTop: 4 }}>
          Lower-case, starting with a letter, using only letters, digits and underscores.
        </p>
      )}
      {clashes && (
        <p className="muted bad" style={{ fontSize: 12, marginTop: 4 }}>
          This table already has a field with that slug.
        </p>
      )}

      <label htmlFor="f-type">Type</label>
      <select id="f-type" value={type} onChange={(e) => setType(e.target.value)}>
        {BUILDABLE_FIELD_TYPES.map((t) => (
          <option key={t.type} value={t.type}>{t.icon}  {t.label}</option>
        ))}
      </select>
      {/*
        FOUND BY LOOKING, 2026-09-04. This said "Attachments and rich text
        arrive in C2b" while an Attachment option sat in the list above it, so
        the one sentence on the screen about the new field type told a person not
        to expect it. A stale sentence beside a working control is worse than no
        sentence: it is the console arguing with itself.

        THE ATTACHMENT LINE IS ABOUT WHAT IT HOLDS, not about a phase, because
        that is the question a person has at this moment — they are choosing a
        column type and want to know what will go in it.
      */}
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        An attachment field holds pasted links — a YouTube, Vimeo or Loom link becomes a player,
        anything else becomes a chip you can open. Uploading your own files arrives in a later
        phase. A rich text field holds a formatted document: headings, bold, lists, checklists,
        highlights, colours, links and this record’s own attachments, edited in the expand panel.
        The AI field type is not built yet; the database refuses it by name and says which phase
        owns it.
      </p>

      {isLink && (
        <>
          <label htmlFor="f-target-base">In which base</label>
          <select id="f-target-base" value={targetBase}
                  onChange={(e) => { setTargetBase(e.target.value); setTarget(''); }}>
            {bases.length === 0 && <option value={baseId}>This base</option>}
            {bases.map((b) => (
              <option key={b.id} value={b.id}>
                {b.id === baseId ? `${b.name} (this base)` : `${b.workspace} › ${b.name}`}
              </option>
            ))}
          </select>
          <label htmlFor="f-target">Link to which table</label>
          <select id="f-target" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose a table…</option>
            {(siblings ?? []).filter((t) => t.id !== tableId).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
            {/* A table CAN link to itself and the database allows it, so it is
                offered separately rather than hidden — a parent/child column on
                one table is the ordinary reason to want one. */}
            {(siblings ?? []).some((t) => t.id === tableId) && (
              <option value={tableId}>{tableName} (itself)</option>
            )}
          </select>
          {targetBase === baseId && siblings && siblings.length <= 1 && (
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              There is nothing else in this base to link to yet. Pick another base above to link across bases.
            </p>
          )}
          {targetBase !== baseId && (
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              A link to another base shows each linked row’s title only to people who can read that
              table — unless its owner turns on <strong>Share titles console-wide</strong> in that
              table’s access panel. Nobody gains any other permission from the link.
            </p>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <input type="checkbox" checked={!allowMultiple}
                   onChange={(e) => setAllowMultiple(!e.target.checked)} />
            Limit to one linked record
          </label>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Both tables get a column: this one, and a matching column on the table you pick
            showing the same relation from its side. They are one thing stored once, so they
            can never disagree — and deleting either puts both in the field Trash, without
            losing a single link.
          </p>
        </>
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        A field's type is fixed once it exists. Formats, colours, choices and a default
        value are all editable afterwards — the type is not.
      </p>

      {needsOptions && (
        <>
          <label htmlFor="f-opts">Options, one per line</label>
          <textarea
            id="f-opts" rows={4} value={optionText}
            onChange={(e) => setOptionText(e.target.value)}
            placeholder={'Not started\nIn progress\nDone'}
          />
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Required. A select field with no options is a column nothing can be written to —
            the row trigger checks every value against this list.
          </p>
        </>
      )}

      <label htmlFor="f-desc">Description</label>
      <textarea id="f-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
        <button className="action on" disabled={!ready || busy} onClick={() => void submit()}>
          {busy ? 'Adding…' : 'Add field'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * WHO, AND WHICH DOOR (C1c-9's UI half; the columns ship in migration 0016).
 *
 * The mini's internal header key resolves to Matt's identity and a browser
 * session is Matt too, so before the actor columns existed every write Claude
 * Code made read as Matt having made it — audit #2 finding 7A. The kind is what
 * separates them, and the label is what makes the separation readable.
 *
 * A null kind is a THIRD answer, not a missing one: the write did not come
 * through the console service at all — a migration, the seed, or an operator
 * connected as the owner — and saying "system" is exactly right for that.
 */
function actorOf(h: HistoryRow): string {
  const who = h.by_name || h.by_email || 'system';
  switch (h.actor_kind) {
    case 'internal_key': return h.actor_label || 'Claude Code (mini)';
    case 'mcp_client':   return h.actor_label || 'an MCP client';
    case 'person':       return who;
    default:             return 'system';
  }
}

function coerce(field: Field, raw: string): unknown {
  if (raw === '') return null;
  switch (field.type) {
    case 'number': return Number(raw);
    case 'checkbox': return raw === 'true';
    case 'multi_select': return raw.split(',').map((s) => s.trim()).filter(Boolean);
    default: return raw;
  }
}

/* ---------------------------------------------------------------------------
 * C2-3: LINKED RECORDS ON THE SCREEN.
 *
 * Three pieces, and each one is a decision from C2-2 made visible:
 *
 *   LinkChips      the cell. A chip per linked row, and a RESTRICTED chip where
 *                  the title was withheld. It is drawn, not dropped — Matt's
 *                  ruling R4 is that the count stays honest, and a cell that
 *                  quietly showed two of three links would be a cell somebody
 *                  reads a conclusion off.
 *   LinkPicker     the '+'. It searches the TARGET table through
 *                  GET /api/tables/{target}/records?q= — the existing route,
 *                  the existing walls — so a person can only offer to link
 *                  rows they can already read. The server re-checks anyway
 *                  (writeLinkCell), because a picker is not a wall.
 *   LinkedRow      what a chip opens. One row of another table, read-only,
 *                  through the same listing that produced its title.
 * ------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
 * ATTACHMENTS (C2b-A, item C2b-5) — MATT'S SHAPE, TAKEN LITERALLY.
 *
 * His words, 2026-09-04, recorded verbatim as Amendment 1 A1-2:
 *
 *   "with Airtable there's a little plus symbol inside a lighter grey little box
 *    (darker grey if cell backgrounds are white) and when that box is clicked a
 *    popup happens that gives an upload box with a link to go on the computer to
 *    click a file to download, and a box to drag and drop to that will do it.
 *    Also, I think the attachments cell lets us drag and drop straight into it.
 *    The cell also needs an expansion arrow we can use to view all the docs and
 *    retrieve and download them."
 *
 * Every clause of that is built here. What is NOT built is the far end of the
 * two file paths: choosing a file and dropping one both reach the server and are
 * REFUSED with "file uploads arrive in a later phase" — because C2b-A stores no
 * bytes, there is no bucket, and the door needs Decision recEVLNTQlyjj391h
 * applied by a session other than the one that captured it.
 *
 * THE AFFORDANCE IS BUILT ONCE, AND THAT IS THE POINT OF BUILDING IT NOW. When
 * C2b-D opens the door, the drop zone, the picker, the popover and the expand
 * panel are already here and already where Matt expects them; what changes is
 * one refusal turning into an upload. The alternative — ship links now, add the
 * file affordances later — is two designs of the same cell, and the second one
 * is always drawn by somebody who did not read this quote.
 *
 * WHY THE REFUSAL COMES FROM THE SERVER. See api.attach(): one sentence, in one
 * place, that stops being a refusal the day the phase ships.
 * ------------------------------------------------------------------------- */

/** The '+' in its tinted box. Matt's "lighter grey little box". */
function AttachPlus({ onOpen }: { onOpen: () => void }) {
  return (
    <button className="attplus" title="Add an attachment" aria-label="Add an attachment" onClick={onOpen}>
      +
    </button>
  );
}

/**
 * One attachment, as it appears in a cell: a thumbnail or an icon, the title,
 * and — when the cell is editable — an ×.
 *
 * THE WHOLE CHIP IS A LINK, and it opens in a new tab under the person's OWN
 * login at the provider. Nothing is proxied through the console and nothing is
 * copied into it (mounts not syncs, V2-6) — which is also why a Google Doc a
 * person cannot open shows them Google's own refusal rather than ours. That is
 * the honest answer: the console does not know who may read somebody's Drive.
 */
function AttachmentChip({
  a, editable, onRemove, onOpen,
}: {
  a: AttachmentCell;
  editable: boolean;
  onRemove?: () => void;
  onOpen: () => void;
}) {
  return (
    <span className="attchip" title={a.url ?? a.title}>
      {a.thumbnail ? (
        <img className="attthumb" src={a.thumbnail} alt="" loading="lazy" />
      ) : (
        <span className={`atticon i-${a.icon}`} aria-hidden="true" />
      )}
      <button className="chiplink" onClick={onOpen}>
        {a.title.trim() ? a.title : <span className="muted">(untitled)</span>}
      </button>
      {editable && onRemove && (
        <button className="chipx" title="Remove" aria-label="Remove" onClick={onRemove}>×</button>
      )}
    </span>
  );
}

/**
 * The popover Matt described: a paste-link box, a "choose file" link, and a
 * drag-and-drop zone.
 *
 * IT IS A POPOVER AND NOT A MODAL, deliberately. A modal is what LinkPicker
 * needs, because searching another table is a task; attaching a link is one
 * paste and a return key, and a dialog that takes the whole screen for that is
 * the kind of friction a person works around by not using the feature.
 *
 * THE FILE PATHS ARE REAL PATHS. The file input opens a real picker and the drop
 * zone accepts a real drop; both then call the same route the paste box does,
 * with `kind: 'file'`, and render the server's refusal in place. So the wiring
 * that C2b-D needs is exercised now — by a test and by anybody who tries it —
 * rather than being written for the first time on the day it has to work.
 */
function AttachPopover({
  tableId, recordId, field, onDone, onError,
}: {
  tableId: string; recordId: string; field: Field;
  onDone: (added: boolean) => void;
  onError: (e: unknown) => void;
}) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  /** null when nothing is uploading; 0-1 while bytes are moving. */
  const [progress, setProgress] = useState<number | null>(null);
  const file = useRef<HTMLInputElement | null>(null);

  const paste = async (): Promise<void> => {
    const value = url.trim();
    if (!value || busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      await api.attach(tableId, recordId, { fieldId: field.id, url: value });
      setUrl('');
      onDone(true);
    } catch (e) {
      // ANSWERED WHERE IT HAPPENED, not in the global banner — the N-14 rule.
      // A bad link is a fact about the box you just typed in.
      if (e instanceof ApiError && (e.refusal.kind === 'auth' || e.refusal.kind === 'scope')) onError(e);
      setRefusal(e instanceof ApiError ? e.refusal.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * C2b-D. THE FILE PATH IS NOW A REAL UPLOAD.
   *
   * Phase A built both file paths and had them ask the server and be told no,
   * so that "the wiring C2b-D needs is exercised now rather than being written
   * for the first time on the day it has to work" (the header above). This is
   * that day, and the change is this function: one call, three server steps and
   * a direct PUT, all of it inside uploadOneFile().
   *
   * ONE FILE, NOT MANY, and it is a decision rather than a limit of the API. A
   * multi-file upload needs a per-file row in the popover with its own progress
   * and its own refusal — a picker takes several, a drop takes several, and
   * showing one bar for five files is showing a person the wrong thing. So the
   * first file is taken and the rest are named, which is honest, and the queue
   * is a later build rather than a silent drop.
   *
   * THE REFUSAL IS RENDERED WHERE IT HAPPENED (the N-14 rule), not in the global
   * banner: "that file is too large" is a fact about the file you just chose.
   */
  const tryFile = async (picked: FileList | File[] | null): Promise<void> => {
    const files = picked ? Array.from(picked) : [];
    const f = files[0];
    if (!f || busy) return;
    setBusy(true);
    setRefusal(files.length > 1
      ? `Uploading “${f.name}”. ${files.length - 1} other file(s) were not taken — attach them one at a time for now.`
      : null);
    setProgress(0);
    try {
      await uploadOneFile(tableId, recordId, field.id, f, setProgress);
      onDone(true);
    } catch (e) {
      if (e instanceof ApiError && (e.refusal.kind === 'auth' || e.refusal.kind === 'scope')) onError(e);
      setRefusal(e instanceof ApiError ? e.refusal.message : (e as Error).message ?? String(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <div className="attpop" role="dialog" aria-label={`Add to ${field.name}`}>
      <label htmlFor="att-url">Paste a link</label>
      <input
        id="att-url"
        autoFocus
        value={url}
        placeholder="https://…"
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void paste();
          if (e.key === 'Escape') onDone(false);
        }}
      />
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>
        A YouTube, Vimeo or Loom link becomes a player. Anything else becomes a chip you can open.
      </p>

      <div
        className={over ? 'attdrop over' : 'attdrop'}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); void tryFile(e.dataTransfer.files); }}
      >
        {progress === null ? (
          <>
            Drop a file here, or{' '}
            <button className="linkish" onClick={() => file.current?.click()} disabled={busy}>choose a file</button>
          </>
        ) : (
          // WHAT A PERSON SEES WHILE 250 MB MOVES. A motionless spinner for two
          // minutes is how somebody concludes it has hung and presses the button
          // again — C1c-4's shape, a thing that looks broken and is not.
          <span aria-live="polite">
            Uploading… {Math.round(progress * 100)}%
          </span>
        )}
        <input
          ref={file}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => { void tryFile(e.target.files); e.target.value = ''; }}
        />
      </div>

      {refusal && <p className="notice scope" role="alert" style={{ marginBottom: 0 }}>{refusal}</p>}

      <div className="modal-actions">
        <button className="action" onClick={() => void paste()} disabled={busy || !url.trim()}>Add</button>
        <button className="action" onClick={() => onDone(false)}>Close</button>
      </div>
    </div>
  );
}

/**
 * The expand panel: every attachment on this record, with view / retrieve /
 * download — Matt's "expansion arrow we can use to view all the docs and
 * retrieve and download them".
 *
 * WHAT THE THREE WORDS MEAN IN A BUILD WITH NO BYTES:
 *   view      an embed plays here, in the panel, inside an iframe whose origin
 *             the Content-Security-Policy allows because the providers table
 *             says so.
 *   retrieve  open it at the provider, in a new tab, under your own login.
 *   download  the same thing for a link — a link IS its own address. For an
 *             uploaded file it is refused, because there is no file yet; the
 *             button is present and says so rather than being absent and
 *             leaving the panel to be redesigned in C2b-D.
 */
function AttachmentPanel({
  tableId, recordId, field, editable, onClose, onChanged, onError,
}: {
  tableId: string; recordId: string; field: Field; editable: boolean;
  onClose: () => void; onChanged: () => void; onError: (e: unknown) => void;
}) {
  const [rows, setRows] = useState<(AttachmentCell & { field_id: string })[] | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  /** The attachment whose download link is being minted, if any. */
  const [minting, setMinting] = useState<string | null>(null);

  const load = (): void => {
    api.attachments(tableId, recordId)
      .then((r) => setRows(r.attachments.filter((a) => a.field_id === field.id)))
      .catch(onError);
  };
  useEffect(load, [tableId, recordId, field.id]);

  return (
    <Modal title={field.name} onCancel={onClose}>
      {rows === null && <p className="muted">Loading…</p>}
      {rows?.length === 0 && <p className="muted">Nothing is attached to this record yet.</p>}
      <div className="attlist">
        {rows?.map((a) => (
          <div className="attrow" key={a.id}>
            <div className="attrow-head">
              {a.thumbnail
                ? <img className="attthumb big" src={a.thumbnail} alt="" loading="lazy" />
                : <span className={`atticon i-${a.icon}`} aria-hidden="true" />}
              <div className="attrow-name">
                <strong>{a.title.trim() || '(untitled)'}</strong>
                <small className="muted">{a.providerName ?? (a.url ? new URL(a.url).host : 'file')}</small>
              </div>
            </div>
            <div className="attrow-actions">
              {a.embed && (
                <button className="action" onClick={() => setPlaying(playing === a.id ? null : a.id)}>
                  {playing === a.id ? 'Hide' : 'View'}
                </button>
              )}
              {a.url && (
                <a className="action" href={a.url} target="_blank" rel="noopener noreferrer">Retrieve</a>
              )}
              {a.url
                ? <a className="action" href={a.url} target="_blank" rel="noopener noreferrer">Download</a>
                : (
                  /*
                   * C2b-D, A2-2: THE LINK IS MINTED HERE AND NOWHERE ELSE.
                   *
                   * Not on panel open, not in a listing — the server leaves
                   * `url` null for an upload in every payload, so there is no
                   * other place one could come from. Pressing this writes a read
                   * line naming a MINT, and the link it returns is good for
                   * sixty seconds for anybody holding it. A2-3: the console
                   * cannot tell whether it was used, which is why the word on
                   * the button after a successful mint is "issued".
                   */
                  <button
                    className="action"
                    disabled={minting === a.id}
                    title="Get a download link for this file. It works for sixty seconds."
                    onClick={() => {
                      setMinting(a.id);
                      void api.mintDownload(tableId, recordId, a.id)
                        .then((r) => {
                          // OPENED, NOT NAVIGATED TO. The panel stays where it
                          // is; the browser fetches the object from the store.
                          window.open(r.url, '_blank', 'noopener');
                        })
                        .catch(onError)
                        .finally(() => setMinting(null));
                    }}
                  >
                    {minting === a.id ? 'Issuing…' : 'Download'}
                  </button>
                )}
              {editable && (
                <button
                  className="action danger"
                  onClick={() => {
                    void api.trashAttachment(tableId, recordId, a.id)
                      .then(() => { load(); onChanged(); })
                      .catch(onError);
                  }}
                >
                  Remove
                </button>
              )}
            </div>
            {playing === a.id && a.embed && (
              <iframe
                className="attplayer"
                src={a.embed}
                title={a.title}
                /*
                  FOUND BY LOOKING, 2026-09-04, AND NOTHING ELSE WOULD HAVE
                  FOUND IT. The Content-Security-Policy allowed the frame, the
                  iframe loaded, the deploy was green and the e2e suite was
                  green — and YouTube rendered "Video player configuration
                  error, Error 153" instead of the video.

                  Error 153 is YouTube's embedded player refusing a request with
                  no Referer. app.ts sets `referrer-policy: no-referrer` on every
                  response, which is right for the console and fatal for a player
                  that decides whether it may be embedded by looking at who is
                  embedding it.

                  THE FIX IS PER-FRAME, NOT PER-SITE. This attribute overrides
                  the document policy for THIS frame's requests only; every other
                  request the console makes still sends no referrer. And
                  `strict-origin-when-cross-origin` sends the ORIGIN alone —
                  https://console.assettservices.com — never the path, which is
                  what `no-referrer` was protecting here: the URL of a grid page
                  carries the workspace, base and table slugs, and those still do
                  not leave.
                */
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        ))}
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        Removing an attachment here takes it off the record and keeps its history. Nothing is ever
        deleted outright.
      </p>
      <div className="modal-actions">
        <button className="action" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

function LinkChips({
  cells, editable, onOpen, onRemove, sharedRemovable = false,
}: {
  cells: LinkCell[];
  editable: boolean;
  onOpen: (c: LinkCell) => void;
  onRemove?: (c: LinkCell) => void;
  /** 0052: a shared chip can be unlinked only where a shared row can be linked — see keptLinkIds(). */
  sharedRemovable?: boolean;
}) {
  return (
    <>
      {cells.map((c) => (
        <span key={c.id} className={c.restricted ? 'chip restricted' : c.shared ? 'chip shared' : 'chip'}>
          {c.restricted ? (
            // NOT CLICKABLE, and not a button that refuses when pressed. There
            // is nothing behind it to open, and a control that exists to say no
            // is a worse answer than a label that says why.
            <span title="You do not have permission to read this row. It is shown so the count is honest.">
              restricted
            </span>
          ) : c.shared ? (
            // 0052: A SHARED TITLE IS A LABEL, NOT A KEY. The row's table shares
            // its titles console-wide; this person still cannot open the row, see
            // its other columns or read its history, so there is nothing to click.
            <span className="chipshared" title="This table shares its row titles console-wide. You can see the title, not the row.">
              {c.title?.trim() ? c.title : <span className="muted">(untitled)</span>}
            </span>
          ) : (
            <button className="chiplink" onClick={() => onOpen(c)} title="Open this row">
              {c.title?.trim() ? c.title : <span className="muted">(untitled)</span>}
            </button>
          )}
          {editable && !c.restricted && (!c.shared || sharedRemovable) && onRemove && (
            <button className="chipx" title="Unlink" aria-label="Unlink" onClick={() => onRemove(c)}>×</button>
          )}
        </span>
      ))}
    </>
  );
}

/**
 * The '+' picker: search the target table, click a row to link it.
 *
 * The search is the SERVER's, which means it is the whole target table rather
 * than a page of it — the same full-text index the target's own grid searches.
 * With an empty box it lists the first rows, because a picker that shows nothing
 * until you type is a picker that cannot be browsed.
 */
function LinkPicker({
  field, already, onCancel, onPick, onError,
}: {
  field: Field;
  already: LinkCell[];
  onCancel: () => void;
  /** The title travels with the id: only the picker knows what the row is CALLED, and N-15's line has to name it. */
  onPick: (id: string, title: string) => void;
  onError: (e: unknown) => void;
}) {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<LinkOption[]>([]);
  // 0052: the target's NAME comes back only when this person can read it — a
  // table name is not a title and does not cross the wall either.
  const [targetName, setTargetName] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  /**
   * N-14. WHAT THE LAST SEARCH DID, not just what it returned.
   *
   * "Nothing matched." used to be the answer to three different questions:
   * there is no such row / the query was STOPPED / you may not see it. On
   * 2026-08-29 the first search a person ever ran in this picker hit the
   * statement timeout: the global banner said the query had been refused AND
   * this list said nothing matched, at the same time, on the same screen. The
   * note under the list ("A row you cannot see is not here") then actively
   * taught the third reading — so a database being slow was rendered as a
   * statement about that person's PERMISSIONS.
   *
   * The status was here all along; it was being thrown away. A failed request
   * now says so, in place of the empty result, and offers the retry — which is
   * the honest instruction for a 57014, since the identical query returned 200
   * seconds later.
   */
  const [failed, setFailed] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const taken = new Set(already.map((c) => c.id));

  /**
   * N-15, and it is the whole of the warning half. `limit to one` makes the
   * next pick a REPLACEMENT rather than an addition, and until now nothing said
   * so before or after. Matt's ruling (a): the swap stays, the silence goes.
   */
  const holdsOne = field.link_allow_multiple === false;
  const current = already.find((c) => !c.restricted) ?? already[0] ?? null;

  useEffect(() => {
    const target = field.link_target_table_id;
    if (!target) return;
    let live = true;
    setBusy(true);
    const t = setTimeout(() => {
      api.linkOptions(field.id, search)
        .then((p) => { if (live) { setRows(p.options); setTargetName(p.table?.name ?? null); setFailed(null); } })
        .catch((e) => {
          if (!live) return;
          // The list is emptied so a stale result cannot be read as the answer
          // to the query that failed — build-craft §2, the change that works and
          // looks broken, in its other direction.
          setRows([]);
          setFailed(e instanceof ApiError ? e : new ApiError({ kind: 'error', message: String(e) }));
          // Signing in again and a missing scope are not this dialog's to
          // explain: they are true of the whole session and belong in the
          // banner. Everything else is answered where it happened, once.
          if (e instanceof ApiError && (e.refusal.kind === 'auth' || e.refusal.kind === 'scope')) onError(e);
        })
        .finally(() => { if (live) setBusy(false); });
    }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [field.id, field.link_target_table_id, search, attempt, onError]);

  // THE TITLES ARE THE SERVER'S (0052). GET /api/fields/{id}/link-options titles
  // every option with the server's own titleField(), so the picker and the cell
  // cannot disagree about what a row is called — the copy of that rule that used
  // to live here is gone rather than kept in step.

  return (
    <Modal title={`Link to ${targetName ?? 'a record'}`} onCancel={onCancel}>
      {holdsOne && (
        <p className="notice scope" style={{ marginTop: 0 }} role="status">
          This holds one record — picking another replaces
          {current ? <> the current one, <strong>{current.restricted ? 'a record you cannot read' : (current.title?.trim() || '(untitled)')}</strong>.</> : <> the current one.</>}
          {' '}You can undo it straight afterwards.
        </p>
      )}
      <label htmlFor="lp-q">Search</label>
      <input id="lp-q" autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
             placeholder="Type to search the whole table" />
      <div className="picklist">
        {busy && <p className="muted">Searching…</p>}
        {/*
          N-14: THREE STATES, THREE SENTENCES. A failed request is not an empty
          result, and neither of them is a statement about permissions.
        */}
        {!busy && failed && (
          <p className="muted bad" role="alert">
            That search did not finish, so this list is not an answer:{' '}
            {failed.refusal.message}
            {' '}
            <button className="linkish" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          </p>
        )}
        {!busy && !failed && rows.length === 0 && (
          <p className="muted">
            {search.trim()
              ? <>Nothing in {targetName ?? 'this table'} matches “{search.trim()}”.</>
              : <>{targetName ?? 'This table'} has no rows you can link to.</>}
          </p>
        )}
        {rows.map((r) => {
          const id = r.id;
          const title = r.title ?? '';
          return (
            <button
              key={id}
              className="pickrow"
              disabled={taken.has(id)}
              onClick={() => onPick(id, title.trim() || '(untitled)')}
              title={taken.has(id) ? 'Already linked' : 'Link this row'}
            >
              {title.trim() ? title : <span className="muted">(untitled)</span>}
              {taken.has(id) && <span className="muted"> — already linked</span>}
              {r.shared && !taken.has(id) && <span className="muted"> — shared title</span>}
            </button>
          );
        })}
      </div>
      {/*
        N-14. THIS NOTE IS WHY THE THREE STATES ABOVE ARE SEPARATE. It is true
        of a list that arrived, and it teaches exactly the wrong reading of one
        that did not — "you may not see it" is the most alarming of the three
        answers and was the one a timeout produced. So it is shown only when
        there IS a list.
      */}
      {!failed && (
        <p className="muted" style={{ fontSize: 12 }}>
          This list is the target table read with your own permissions. A row you cannot see
          is not here, and the server refuses a link to one even if it is asked for directly
          {rows.some((r) => r.shared)
            ? ' — except rows marked “shared title”: their table shares its titles console-wide, so you can link to them without opening them.'
            : '.'}
        </p>
      )}
      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Done</button>
      </div>
    </Modal>
  );
}

/** One row of another table, read-only, opened by clicking a chip (C2-3). */
function LinkedRow({
  tableId, recordId, onClose, onError,
}: {
  tableId: string; recordId: string; onClose: () => void; onError: (e: unknown) => void;
}) {
  const [data, setData] = useState<{ table: TableMeta; record: Record<string, unknown> } | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    let live = true;
    api.record(tableId, recordId)
      .then((d) => { if (live) setData(d); })
      .catch((e) => {
        // A 404 here is the ordinary case, not a fault: the row was trashed
        // after this cell was drawn, or this person may not read it. Saying so
        // beats an error banner about a request nobody made on purpose.
        if (e instanceof ApiError && e.refusal.status === 404) { if (live) setGone(true); }
        else onError(e);
      });
    return () => { live = false; };
  }, [tableId, recordId, onError]);

  return (
    <Modal title={data ? data.table.name : 'Linked row'} onCancel={onClose}>
      {gone && (
        <p className="muted">
          That row is no longer readable here — it may have been moved to the trash, or it may
          not be yours to open. The link itself is untouched.
        </p>
      )}
      {!gone && !data && <p className="muted">Loading…</p>}
      {data && data.table.fields.map((f) => (
        <div key={f.id}>
          <label>{f.name}</label>
          {f.type === 'attachment' ? (
            /* One level out, like the chips below: shown, not opened. */
            <div className="attcell readonly">
              {attachmentCells(data.record[f.slug]).map((a) => (
                <AttachmentChip key={a.id} a={a} editable={false} onOpen={() => {}} />
              ))}
              {attachmentCells(data.record[f.slug]).length === 0 && <span className="muted">—</span>}
            </div>
          ) : f.type === 'link' ? (
            <div className="readonly">
              {/* Chips one level out, and they do not open: a modal stack over a
                  relation graph is a maze. The row itself is one click away in
                  its own table. */}
              <LinkChips cells={linkCells(data.record[f.slug])} editable={false} onOpen={() => {}} />
              {linkCells(data.record[f.slug]).length === 0 && <span className="muted">—</span>}
            </div>
          ) : f.type === 'long_text' ? (
            <textarea value={fmt(data.record[f.slug])} readOnly rows={4} />
          ) : (
            <input value={fmt(data.record[f.slug])} readOnly />
          )}
        </div>
      ))}
      <div className="modal-actions">
        <button className="action" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function Cell({
  field, value, editable, onCommit, onLinkOpen, onLinkAdd, onLinkSet, attach, rich,
}: {
  field: Field; value: unknown; editable: boolean; onCommit: (raw: string) => void;
  /* C2-A. Supplied only by the grid, which owns the modals a link cell opens. */
  onLinkOpen?: (c: LinkCell) => void;
  onLinkAdd?: () => void;
  onLinkSet?: (ids: string[]) => void;
  /* C2b-B. Same arrangement again: the grid owns the document modal. */
  rich?: { open: () => void };
  /* C2b-A. Same arrangement: the grid owns the popover and the expand panel. */
  attach?: {
    add: () => void;
    expand: () => void;
    open: (a: AttachmentCell) => void;
    remove: (a: AttachmentCell) => void;
    drop: (d: DataTransfer) => void;
  };
}) {
  const initial = field.type === 'checkbox' ? String(value === true) : fmt(value);
  const [draft, setDraft] = useState(initial);

  /**
   * ENTER COMMITS, ESCAPE PUTS IT BACK — C2-11.
   *
   * Escape is the binding that needs this component rather than the grid: the
   * cell is the only thing that knows what the value was before it was typed
   * into. Restoring the draft first and blurring second matters — the blur
   * handler commits when `draft !== initial`, so blurring first would SAVE the
   * edit that Escape exists to abandon.
   *
   * The grid's own key handler sees these too, because the event bubbles, and it
   * moves the selection afterwards. It does not need to know what happened here.
   */
  /**
   * ABANDONED BY ESCAPE, IN A REF — and this is build-craft §16 all over again,
   * caught by pressing the key in a real browser rather than by reading it.
   *
   * The first version did `setDraft(initial)` and then blurred. It SAVED. The
   * blur handler tests `draft !== initial`, and `draft` is React state: the blur
   * runs before any re-render, so the closure still held the typed value and
   * committed it. Escape did the exact opposite of what it says.
   *
   * A ref is current the instant it is written, so the blur — whenever it fires
   * — sees the abandon. `setDraft(initial)` is still there, but only to put the
   * old value back on SCREEN; nothing decides anything from it.
   */
  const abandoned = useRef(false);

  const onEditBlur = (): void => {
    if (abandoned.current) { abandoned.current = false; return; }
    if (draft !== initial) onCommit(draft);
  };

  const onEditKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') { (e.target as HTMLElement).blur(); return; }
    if (e.key === 'Escape') {
      abandoned.current = true;
      setDraft(initial);
      (e.target as HTMLElement).blur();
    }
  };
  useEffect(() => setDraft(initial), [initial]);

  // A LINK CELL IS NOT A VALUE, so it is not an input. It is a set of chips and
  // a '+', and every change to it is its own request rather than a commit on
  // blur — there is no draft state that could go stale.
  //
  // BELOW THE HOOKS, not above them. Every early return in this component is,
  // and has to be: a branch taken before useState would be a conditional hook.
  if (field.type === 'link') {
    const cells = linkCells(value);
    return (
      <div className="linkcell">
        <LinkChips
          cells={cells}
          editable={editable}
          sharedRemovable={sharedLinkable(field)}
          onOpen={(c) => onLinkOpen?.(c)}
          // The list sent back is the VISIBLE links minus this one. Restricted
          // chips are left out of it deliberately: the server preserves any link
          // whose target this caller cannot read, so leaving them out is how you
          // avoid asking to delete something you cannot see.
          onRemove={(c) => onLinkSet?.(keptLinkIds(field, cells.filter((x) => x.id !== c.id)))}
        />
        {editable && (
          <button className="chipadd" title="Link a record" onClick={() => onLinkAdd?.()}>+</button>
        )}
      </div>
    );
  }

  // AN ATTACHMENT CELL IS NOT A VALUE EITHER (C2b-5). Chips, a '+' in its
  // tinted box, and an expand control — and every change to it is its own
  // request, so there is no draft that could go stale.
  //
  // THE CELL ITSELF IS A DROP TARGET, which is Matt's "the attachments cell lets
  // us drag and drop straight into it". A dropped LINK is attached, because a
  // dropped link is a pasted link; a dropped FILE goes to the same route as the
  // popover's picker and comes back with the same refusal. onDragOver must
  // preventDefault or the browser navigates away from the console to whatever
  // was dropped, which is a spectacular way to lose an unsaved cell.
  if (field.type === 'attachment') {
    const cells = attachmentCells(value);
    return (
      <div
        className="attcell"
        onDragOver={editable && attach ? (e) => e.preventDefault() : undefined}
        onDrop={editable && attach ? (e) => { e.preventDefault(); attach.drop(e.dataTransfer); } : undefined}
      >
        {cells.map((a) => (
          <AttachmentChip
            key={a.id}
            a={a}
            editable={editable}
            onOpen={() => attach?.open(a)}
            onRemove={() => attach?.remove(a)}
          />
        ))}
        {editable && attach && <AttachPlus onOpen={attach.add} />}
        {/* THE EXPANSION ARROW. Present even when the cell is read-only and
            even when it is empty: it is how a person on a phone reads a long
            list that will not fit in a 32-pixel row, and an empty panel is a
            true answer. */}
        {attach && (
          <button className="attexpand" title="Show all attachments" aria-label="Show all attachments"
                  onClick={attach.expand}>⤢</button>
        )}
      </div>
    );
  }

  /*
    A RICH TEXT CELL IS NOT A VALUE AND IS NOT AN INPUT (C2b-9).
    IT IS THE FIRST FEW WORDS OF THE DOCUMENT AND A WAY IN.

    THE LISTING DOES NOT CARRY THE DOCUMENT AT ALL — the server sends only the
    first few hundred characters of the Markdown shadow, already turned into
    plain words, because a page is 100 rows and a document may be most of a
    megabyte. So there is nothing here to edit in place even if a 32-pixel row
    were a sensible place to write a checklist, and the expand panel is the
    editor. The `⤢` is on every rich text cell rather than only the selected one:
    it is the ONLY way in, which is not true of a long text cell.
  */
  if (field.type === 'rich_text') {
    const words = typeof value === 'string' ? value : '';
    return (
      <div className="rtcell">
        <span className={words ? 'rtcell-text' : 'rtcell-text muted'}>{words || 'Empty'}</span>
        {rich && (
          <button className="attexpand" title="Open this document"
                  aria-label={`Open ${field.name}`} onClick={rich.open}>⤢</button>
        )}
      </div>
    );
  }

  // EVERY SELECT-ISH TYPE GOES THROUGH THE BADGE PICKER, editable or not — a
  // read-only status column read as plain grey text is the thing Matt's
  // screenshot was complaining about. C1's native <select> is gone from here.
  const badges = field.type === 'single_select' || field.type === 'status' || field.type === 'multi_select';
  if (badges) {
    return <BadgePicker field={field} value={value} editable={editable} onCommit={onCommit} />;
  }

  // A read-only cell is where a FORMAT shows: a mailto, a link, a dollar sign.
  // An editable one stays an input, because turning the thing you are about to
  // type into a link is a way to make a grid unusable.
  if (!editable) return <div className="readonly">{formatted(field, value)}</div>;

  if (field.type === 'checkbox') {
    return (
      <select value={draft} onChange={(e) => { setDraft(e.target.value); onCommit(e.target.value); }}>
        <option value="false">no</option>
        <option value="true">yes</option>
      </select>
    );
  }

  // A DATETIME FIELD IS NOT A TEXT BOX (Matt, 2026-08-28). `date` already got a
  // native picker here; `datetime` fell through to `text`, so the column that
  // says "date and time" under its name offered a blank box and no way to pick
  // either.
  //
  // The conversion is the whole difficulty and it is why this is not one word.
  // These are stored UTC. <input type="datetime-local"> speaks LOCAL time with
  // no zone, so handing it an ISO string shows the wrong time, and handing back
  // what it returns stores the wrong instant. So: UTC -> local for display,
  // local -> UTC on the way out, and an unparseable value is passed through
  // untouched rather than silently becoming an epoch.
  if (field.type === 'datetime') {
    /*
      THE ZONE IS THE PERSON'S, NOT THE BROWSER'S — C2-16, and this is what UI-8
      actually found. The conversion here used `d.getHours()`, which is the
      BROWSER's zone, while the Created and Updated columns two cells over were
      rendered in a module constant reading America/New_York. On the mini those
      are the same zone and nothing looks wrong. From anywhere else, a value
      typed as 09:00 read back as 09:00 in this box and as something else in the
      column beside it, silently, with no error.

      Both now go through tz.ts and through one setting. The instant was always
      right and still is; what changes is that there is one zone on the screen
      instead of two. The DST cases — the hour that does not exist and the hour
      that happens twice — are in server/test/tz.test.ts, with a mutant.
    */
    const tz = zoneOf(displayPrefs);
    return (
      <input
        type="datetime-local"
        value={isoToZoned(draft, tz)}
        onChange={(e) => setDraft(zonedToIso(e.target.value, tz) || e.target.value)}
        onBlur={onEditBlur}
        onKeyDown={onEditKeyDown}
      />
    );
  }

  // A DATE INPUT WANTS yyyy-MM-dd AND NOTHING ELSE. The API returns a date
  // column as a full ISO timestamp, so the cell was handing
  // "2026-08-27T00:00:00.000Z" to <input type="date">, which rejects it and
  // renders BLANK — the value is there, the column looks empty. Chrome says so
  // on every render; it was in Matt's console five times over and nobody had
  // looked.
  //
  // SLICED, NOT PARSED, and that is the whole subtlety: new Date(...) would
  // read it as UTC midnight and getDate() would return the day BEFORE in every
  // timezone west of Greenwich, so a US user's dates would silently shift back
  // one day. A date is a calendar day, not an instant, and must never go
  // through a timezone.
  if (field.type === 'date') {
    return (
      <input
        type="date"
        value={draft ? String(draft).slice(0, 10) : ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={onEditBlur}
        onKeyDown={onEditKeyDown}
      />
    );
  }

  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={onEditBlur}
      onKeyDown={onEditKeyDown}
      type={field.type === 'number' ? 'number' : 'text'}
    />
  );
}

/**
 * THE RICH TEXT DOCUMENT MODAL — C2b-9. "The expand modal is the editor."
 *
 * IT FETCHES THE ROW. The grid listing deliberately does not carry rich text
 * documents (see selectColumnsFor() in the server: a page is 100 rows and a
 * document may be most of a megabyte), so the one place that needs one asks for
 * it by id — which is also where the record's attachments come from, and a
 * document may only show attachments of its own record.
 *
 * IT SENDS THE DOCUMENT AND NEVER THE SHADOW. The Markdown is the server's, made
 * from what this sends; there is no field in this component that could carry it
 * back, and the server would ignore it if there were.
 *
 * SAVE IS EXPLICIT, unlike the long-text panel next door, which saves on close.
 * A document is a thing a person works on for minutes and may want to abandon,
 * and the toolbar is full of ways to change it by accident.
 */
function RichTextModal({
  tableId, recordId, field, editable, onClose, onSaved, onError,
}: {
  tableId: string;
  recordId: string;
  field: Field;
  editable: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const [doc, setDoc] = useState<RTDoc | null>(null);
  const [attachments, setAttachments] = useState<EditorAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const readRef = useRef<(() => RTDoc) | null>(null);
  const register = useCallback((fn: () => RTDoc) => { readRef.current = fn; }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.record(tableId, recordId)
      .then((r) => {
        if (!live) return;
        const v = r.record[field.slug];
        setDoc(isRichTextDoc(v) ? v : EMPTY_DOC);
        // The attachment CELLS are on the record too — one batched query, the
        // same one the grid uses — so the picker costs no extra request.
        const found: EditorAttachment[] = [];
        for (const f of r.table.fields) {
          if (f.type !== 'attachment') continue;
          for (const a of attachmentCells(r.record[f.slug])) {
            found.push({ id: a.id, title: a.title || 'attachment' });
          }
        }
        setAttachments(found);
      })
      .catch(onError)
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tableId, recordId, field.slug, onError]);

  async function save() {
    if (!editable || busy || !readRef.current) return;
    setBusy(true);
    try {
      // THE DOCUMENT, under the field's slug. The server validates it, refuses
      // an unknown node or mark 422 BY NAME, refuses a javascript: or data: link
      // and refuses an attachment id belonging to another record — and then
      // writes the Markdown shadow itself.
      await api.patchRecord(tableId, recordId, { [field.slug]: { doc: readRef.current() } });
      setDirty(false);
      onSaved();
      onClose();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={field.name}
      onCancel={() => {
        // A CONFIRM RATHER THAN A SILENT DISCARD. Escape is one key away from
        // the editor and a document is minutes of work.
        if (dirty && !window.confirm('Close without saving this document?')) return;
        onClose();
      }}
      wide
    >
      {field.description && <p className="muted" style={{ marginTop: 0 }}>{field.description}</p>}
      {loading ? (
        <p className="muted">Opening…</p>
      ) : (
        <RichTextEditor
          value={doc}
          editable={editable}
          attachments={attachments}
          onDirty={() => setDirty(true)}
          register={register}
        />
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        {editable
          ? 'Tab and Shift+Tab indent a list. Enter starts a new line; Shift+Enter breaks one.'
          : 'Read-only — your session does not carry console.records:write.'}
      </p>
      <div className="modal-actions">
        <button className="action" onClick={() => {
          if (dirty && !window.confirm('Close without saving this document?')) return;
          onClose();
        }}>{editable ? 'Cancel' : 'Close'}</button>
        {editable && (
          <button className="action on" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </Modal>
  );
}

/**
 * The expand panel: long text, and the row's history.
 *
 * The history comes from console.audit, which is written by a SECURITY DEFINER
 * trigger and which console_app cannot insert into, update or delete. So what is
 * on screen is not the console's account of what it did — it is the database's.
 */
function RecordPanel({
  tableId, fields, record, editable, me, onClose, onSaved, onError, onCopyLink, copied, onOpenLink,
  onDuplicate, duplicating,
}: {
  tableId: string;
  fields: Field[];
  record: Record<string, unknown>;
  editable: boolean;
  me: Me;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
  onCopyLink: () => void;
  copied: boolean;
  /** C2-A: a chip in the panel opens the linked row, same as one in a cell. */
  onOpenLink?: (tableId: string, recordId: string) => void;
  /** C1c-14. Absent when the caller cannot write, which is why it is optional. */
  onDuplicate?: () => void;
  duplicating?: boolean;
}) {
  const id = String(record['id'] ?? '');
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id) return;
    api.history(tableId, id).then((r) => setHistory(r.history)).catch(onError);
  }, [tableId, id, onError]);

  return (
    <aside className="panel">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h2>Row</h2>
        <div style={{ flex: 1 }} />
        {/* C1c-13. The same link as the row menu's, offered where a person is
            already looking at the row they want to send. */}
        <button className="action" onClick={onCopyLink} title="Copy a link straight to this row">
          {copied ? 'Copied' : 'Copy link'}
        </button>
        {/*
          DUPLICATE — C1c-14, deferred out of C1c and landing here rather than in
          the grid's row menu, because this is the screen where you are actually
          looking at the row you want a second one of.

          The copy lands DIRECTLY BELOW this row in the manual order, and the
          grid opens it. A duplicate that appeared silently at the bottom of a
          hundred thousand rows would be indistinguishable from nothing having
          happened.
        */}
        {onDuplicate && (
          <button
            className="action"
            disabled={duplicating}
            onClick={onDuplicate}
            title="Make a copy of this row, directly below it"
          >
            {duplicating ? 'Copying…' : 'Duplicate'}
          </button>
        )}
        <button className="action" onClick={onClose}>Close</button>
      </div>
      <p className="muted" style={{ marginTop: 2, fontFamily: 'var(--mono)', fontSize: 12 }}>{id}</p>

      {fields.map((f) => (
        <div key={f.id}>
          <label htmlFor={`f-${f.id}`}>{f.name}</label>
          {/* C2-3: "Row expand panel shows linked rows as a list." Read-only
              here — the cell in the grid is where a link is changed, and two
              editors for one relation is two places for it to be half-saved. */}
          {f.type === 'link' ? (
            <div className="linkcell readonly">
              <LinkChips
                cells={linkCells(record[f.slug])}
                editable={false}
                onOpen={(c) => f.link_target_table_id && onOpenLink?.(f.link_target_table_id, c.id)}
              />
              {linkCells(record[f.slug]).length === 0 && <span className="muted">Not linked to anything.</span>}
            </div>
          ) : f.type === 'attachment' ? (
            /* C2b-5, and the same reasoning C2-3 gives about links: READ-ONLY
               here. The grid's cell is where an attachment is added or removed,
               and two editors for one cell is two places for it to be
               half-saved. The chips still open, because opening is a read. */
            <div className="attcell readonly">
              {attachmentCells(record[f.slug]).map((a) => (
                <AttachmentChip
                  key={a.id}
                  a={a}
                  editable={false}
                  onOpen={() => { if (a.url) window.open(a.url, '_blank', 'noopener'); }}
                />
              ))}
              {attachmentCells(record[f.slug]).length === 0 && (
                <span className="muted">Nothing attached.</span>
              )}
            </div>
          ) : f.type === 'rich_text' ? (
            /*
              READ-ONLY HERE, AND FOR THE REASON THE LINK AND ATTACHMENT CELLS
              ABOVE ARE: the grid's expand panel is where a document is edited,
              and two editors for one cell is two places for it to be half-saved.
              What this panel shows is the DOCUMENT as it reads, rendered from
              the model and sanitised — which is the thing a person on a phone
              opens a row to read.

              THE LISTING DOES NOT CARRY THE DOCUMENT, so this renders what it
              does have — the first words of the shadow — and says where the rest
              is. A panel that showed an empty document for a row that has one
              would be worse than a panel that says "open it".
            */
            <div className="rt-panel">
              {isRichTextDoc(record[f.slug])
                ? <RichTextView doc={record[f.slug] as RTDoc} />
                : (
                  <p className="muted" style={{ margin: 0 }}>
                    {typeof record[`${f.slug}_md`] === 'string' && record[`${f.slug}_md`]
                      ? String(record[`${f.slug}_md`])
                      : 'Empty.'}
                  </p>
                )}
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                Open the ⤢ on this column in the grid to read or edit the whole document.
              </p>
            </div>
          ) : f.type === 'long_text' ? (
            <textarea
              id={`f-${f.id}`}
              disabled={!editable}
              value={draft[f.slug] ?? fmt(record[f.slug])}
              onChange={(e) => setDraft({ ...draft, [f.slug]: e.target.value })}
              onBlur={async () => {
                const v = draft[f.slug];
                if (v === undefined || v === fmt(record[f.slug])) return;
                try { await api.patchRecord(tableId, id, { [f.slug]: v }); onSaved(); }
                catch (e) { onError(e); }
              }}
            />
          ) : (
            <input id={`f-${f.id}`} disabled value={fmt(record[f.slug])} readOnly />
          )}
        </div>
      ))}

      <div className="hist">
        <h2>History</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
          Written by the database, not by the console — {me.role === 'owner_admin' ? 'including yours' : 'yours included'}.
        </p>
        <ul>
          {history.map((h, i) => (
            <li key={i}>
              <div className="when">
                {fmtTime(h.at)} · {actorOf(h)} · {h.action}
                {h.field_slug ? ` · ${h.field_slug}` : ''}
              </div>
              {h.field_slug && (
                <div className="val">
                  {JSON.stringify(h.old_value)} → {JSON.stringify(h.new_value)}
                </div>
              )}
            </li>
          ))}
          {history.length === 0 && <li className="muted">Nothing recorded yet.</li>}
        </ul>
      </div>
    </aside>
  );
}

/**
 * THE FIELD EDITOR — Amendment 3 item 2, which is one panel and is treated as
 * one item because it is one panel.
 *
 * Matt's reason for cutting it this way, recorded because it explains the shape:
 * "these all live inside the same editor panel the picker and choice editor
 * already open, and field config is schema he already knows he needs." So this
 * component does the name and description, the FORMAT, the DEFAULT VALUE, and —
 * for a select, multi-select or status field — the whole choice editor from
 * Matt's ruling R2.
 *
 * WHAT IT WILL NOT DO, and says so rather than hiding it: change a field's TYPE.
 * That is explicitly out of C1c (ruling R3) because it means rewriting a column
 * of real data, and a control that looks like it will and then refuses is worse
 * than an absence with a sentence next to it.
 *
 * EVERY SAVE IS ITS OWN CALL, deliberately. A single "Save" over six independent
 * writes is a button that half works: rename the field, retire a choice, set a
 * default, and one of the three is refused — and now the panel and the database
 * disagree and the person cannot tell which part landed. Each control commits
 * on its own and re-reads the field, so what is on screen is what the database
 * says, always.
 */
function FieldEditor({
  tableId, field, onClose, onChanged, onError,
}: {
  tableId: string;
  field: Field;
  onClose: () => void;
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState(field.name);
  const [description, setDescription] = useState(field.description);
  const [format, setFormat] = useState(field.format);
  const [defaultOn, setDefaultOn] = useState(field.default_value !== null && field.default_value !== undefined);
  const [defaultDraft, setDefaultDraft] = useState(defaultToDraft(field));
  const [busy, setBusy] = useState(false);

  const formats = FORMATS[field.type] ?? [];
  const hasChoices = field.type === 'single_select' || field.type === 'multi_select' || field.type === 'status';
  const live = field.options.filter((o) => !o.retired);
  const retired = field.options.filter((o) => o.retired);

  async function run(what: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    try { await what(); onChanged(); } catch (e) { onError(e); } finally { setBusy(false); }
  }

  /** Name and description go through the PATCH route; the slug never moves. */
  async function saveNaming() {
    if (name === field.name && description === field.description) return;
    await run(() => api.describe('fields', field.id, { name, description }));
  }

  /**
   * Format and default are ONE call, because they are one function on the far
   * side and it sets both to exactly what it is sent.
   */
  async function saveConfig(nextFormat: string, nextDefault: unknown) {
    await run(() => api.configureField({
      fieldId: field.id, format: nextFormat, defaultValue: nextDefault,
    }));
  }

  return (
    <Modal title={`Edit ${field.name}`} onCancel={onClose} wide>
      <p className="muted" style={{ marginTop: 0 }}>
        <span className="ticon">{typeIcon(field.type)}</span> {typeLabel(field.type)} ·{' '}
        <code>{field.slug}</code>
      </p>

      <label htmlFor="fe-name">Name</label>
      <input
        id="fe-name" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => void saveNaming()}
      />

      <label htmlFor="fe-desc">Description</label>
      <textarea
        id="fe-desc" rows={2} value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={() => void saveNaming()}
      />

      {/*
        THE TYPE, SHOWN AND NOT EDITABLE. Ruling R3: changing the type of an
        existing field with data is out of C1c. Saying so beats leaving a gap
        where a control obviously belongs.
      */}
      <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
        A field's type is fixed once it holds data — changing it means rewriting
        the column, which is its own piece of work and is not in this release.
        Add a new field and move the values across.
      </p>

      {/* C2b-A. An attachment field's one setting, and the same shape the link
          field's has — including the same refusal to touch existing rows. */}
      {field.type === 'attachment' && (
        <>
          <label className="check" style={{ marginTop: 12 }}>
            <input
              type="checkbox"
              checked={field.att_allow_multiple === false}
              disabled={busy}
              onChange={(e) => {
                const one = e.target.checked;
                void run(() => api.configureAttachment({ fieldId: field.id, allowMultiple: !one }));
              }}
            />{' '}
            Limit to one attachment
          </label>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Turning this on does not touch cells that already hold more than one: nothing is
            removed, and the next attachment that would leave two is refused instead. Removing
            something to make room is a decision that stays yours.
          </p>
        </>
      )}

      {/* C2-A. What a link points at, and the one setting it carries. */}
      {field.type === 'link' && (
        <>
          <label className="check" style={{ marginTop: 12 }}>
            <input
              type="checkbox"
              checked={field.link_allow_multiple === false}
              disabled={busy}
              onChange={(e) => {
                const one = e.target.checked;
                void run(() => api.configureLink({ fieldId: field.id, allowMultiple: !one }));
              }}
            />{' '}
            Limit to one linked record
          </label>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            This side only — the matching column on the other table has its own setting, which
            is how "one client, many jobs" is written down. Turning it on does not touch rows
            that already hold more than one: nothing is deleted, and the next save that would
            leave two is refused instead.
          </p>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            The table this points at cannot be changed. Every existing link would have to point
            somewhere, and there is no honest answer to where — make a new field instead.
          </p>
        </>
      )}

      {formats.length > 0 && (
        <>
          <label htmlFor="fe-format">Format</label>
          <select
            id="fe-format" value={format}
            onChange={(e) => {
              setFormat(e.target.value);
              void saveConfig(e.target.value, defaultOn ? draftToDefault(field, defaultDraft) : null);
            }}
          >
            {formats.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          {formats.find((f) => f.value === format)?.hint && (
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {formats.find((f) => f.value === format)!.hint}
            </p>
          )}
        </>
      )}

      {canHaveDefault(field.type) ? (
        <>
          <label>Default value</label>
          <label className="check">
            <input
              type="checkbox" checked={defaultOn}
              onChange={(e) => {
                setDefaultOn(e.target.checked);
                if (!e.target.checked) void saveConfig(format, null);
              }}
            />{' '}
            Put something here when a row is created without a value
          </label>
          {defaultOn && (
            <>
              {hasChoices ? (
                <select
                  value={defaultDraft}
                  onChange={(e) => {
                    setDefaultDraft(e.target.value);
                    void saveConfig(format, e.target.value ? asDefaultForType(field, e.target.value) : null);
                  }}
                >
                  <option value="">choose a choice…</option>
                  {live.map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input
                  value={defaultDraft}
                  placeholder={field.type === 'number' ? '0' : ''}
                  onChange={(e) => setDefaultDraft(e.target.value)}
                  onBlur={() => void saveConfig(format, defaultDraft === '' ? null : asDefaultForType(field, defaultDraft))}
                />
              )}
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Applied by the server when a row arrives without this field — so it
                holds for the API and for anything writing through it, not just for
                this screen. Existing rows are not touched.
              </p>
            </>
          )}
          {!defaultOn && (
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              No default. Nothing is invented for you.
            </p>
          )}
        </>
      ) : (
        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          A checkbox is always yes or no and is never empty, so there is nothing
          for a default to fill in. It starts as <strong>no</strong>.
        </p>
      )}

      {hasChoices && (
        <ChoiceEditor
          field={field}
          live={live}
          retired={retired}
          busy={busy}
          onRun={run}
          onChanged={onChanged}
          onError={onError}
        />
      )}

      <div className="modal-actions">
        <button className="action on" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

/** The stored default as something an <input> or a <select> can hold. */
function defaultToDraft(field: Field): string {
  const v = field.default_value;
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

/** And back, as JSON of the shape the column actually wants. */
function asDefaultForType(field: Field, draft: string): unknown {
  switch (field.type) {
    case 'number': return Number(draft);
    case 'multi_select': return draft.split(',').map((s) => s.trim()).filter(Boolean);
    default: return draft;
  }
}

function draftToDefault(field: Field, draft: string): unknown {
  return draft === '' ? null : asDefaultForType(field, draft);
}

/**
 * THE CHOICE EDITOR — Matt's ruling R2, the Airtable "Edit field" panel.
 *
 * Rename, recolour, drag to reorder, Alphabetize, X to retire. And the one that
 * needed a ruling: RETIRING A CHOICE THAT IS IN USE keeps it on the rows that
 * have it, greyed, and refuses it to every row that does not — option (b) — with
 * "Reassign and retire" as the separate action that does the bulk replace first.
 *
 * A RETIRED CHOICE IS NEVER DELETED. It is the same rule as a trashed row and
 * for the same reason: the rows still carrying the value have to go on meaning
 * something. The database is what keeps that promise (migration 0017); this
 * screen is where it becomes visible.
 */
function ChoiceEditor({
  field, live, retired, busy, onRun, onChanged, onError,
}: {
  field: Field;
  live: FieldOption[];
  retired: FieldOption[];
  busy: boolean;
  onRun: (what: () => Promise<unknown>) => Promise<void>;
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const [adding, setAdding] = useState('');
  const [reassigning, setReassigning] = useState<FieldOption | null>(null);
  /*
    PORTED TO POINTER EVENTS — C2-8, 2026-09-03, and it was ported LAST on
    purpose: this was the ONE drag in the console that worked, so it is the one
    with something to lose. Rows and columns were built on the primitive and
    driven with a real mouse before this was touched.

    WHAT SURVIVED THE PORT, because it was right and was paid for: the geometry
    is still captured once at the start of the gesture; the target is still
    counted in rows travelled rather than read from "what is under the pointer";
    the list still moves immediately and rolls back on a refusal; and the
    one-paint transition suppression is still here. What went is the HTML5
    plumbing — dataTransfer, setDragImage, the drop target, the browser's own
    ghost — and with it the four failure modes in drag.ts's header.
  */
  const liEls = useRef<(HTMLLIElement | null)[]>([]);
  // Which row the pointer is currently over drives the gap that opens at the
  // insertion point, so the list SHOWS where the item would land instead of
  // making you guess and check afterwards (Matt, 2026-08-28).

  // The order shown, which is the server's until a drag says otherwise.
  const [optimistic, setOptimistic] = useState<string[] | null>(null);

  // True for exactly one paint after a drop, to stop the list animating its own
  // arrival. See the comment in reorderTo().
  const [settling, setSettling] = useState(false);
  const ordered = useMemo(() => {
    if (!optimistic) return live;
    const byId = new Map(live.map((o) => [o.id, o]));
    const out = optimistic.map((id) => byId.get(id)).filter((o): o is FieldOption => !!o);
    // Anything the optimistic list does not know about (added elsewhere, or
    // arrived with the refetch) is appended rather than dropped. Losing an
    // option off the screen because a drag did not know about it yet would be a
    // far worse bug than the one this is fixing.
    for (const o of live) if (!optimistic.includes(o.id)) out.push(o);
    return out;
  }, [live, optimistic]);

  // Once the refetch lands in the props, the server is the truth again.
  useEffect(() => {
    if (!optimistic) return;
    if (live.map((o) => o.id).join() === optimistic.join()) setOptimistic(null);
  }, [live, optimistic]);

  async function addOption() {
    const label = adding.trim();
    if (!label) return;
    const value = slugify(label) || label;
    if (field.options.some((o) => o.value === value)) {
      onError(new ApiError({
        kind: 'refused',
        message: `This field already has a choice stored as "${value}"${
          field.options.find((o) => o.value === value)!.retired ? ', and it is retired' : ''
        }.`,
      }));
      return;
    }
    await onRun(async () => {
      await api.addOption({ fieldId: field.id, value, label, color: nextColor(field.options.length) });
    });
    setAdding('');
  }

  /**
   * Drag to reorder. The whole list goes to the server — a partial one is refused.
   *
   * THE LIST MOVES IMMEDIATELY, and that is a fix rather than a flourish.
   *
   * Until now this fired the request and waited for the parent's reload to come
   * back and re-render the options. Matt dragged, saw the row settle back into
   * its old place, concluded nothing had happened, and dragged it back — which
   * is why the server log shows two successful reorders three seconds apart
   * whose net effect was zero. THE FEATURE WORKED AND LOOKED BROKEN, which is
   * worse than not working, because the second drag silently undid the first.
   *
   * So the local order is applied at once and the refetch confirms it. If the
   * call fails, `optimistic` is dropped and the props win again, so a refusal
   * still shows the truth rather than a lie that happens to be on screen.
   */
  /**
   * Where the pointer says the row should land — counted, not guessed.
   *
   * The index is HOW MANY MIDPOINTS THE POINTER HAS PASSED, which is a
   * different question from "which row is nearest" and the difference is a bug
   * I shipped: nearest-midpoint resolves a short drag to the dragged row
   * ITSELF, reorderTo then correctly refuses to move a row onto itself, and the
   * whole gesture dies silently with no request. The server log showed exactly
   * that — two reorders before this change, none after.
   *
   * Counting has no dead zone. Cross one midpoint and the answer changes.
   */
  function reorderTo(from: number, to: number) {
    const ids = reorder(ordered.map((o) => o.id), from, to);

    // LET IT LAND WITHOUT ANIMATING (Matt, 2026-08-28: the row that moved aside
    // "jumps" and then lands in its new spot).
    //
    // At the moment of the drop two things become true at once: the neighbour
    // is still sitting at translateY(-100%) from the shift, AND the list
    // re-renders with it genuinely in its new slot. So the browser puts it
    // where it belongs and then spends 120ms animating the leftover transform
    // away — you watch it arrive, jump, and settle. The animation is undoing
    // itself on top of a move that has already happened.
    //
    // Suppressing the transition for one paint makes the two agree: the DOM
    // reorder IS the movement, so there is nothing left to animate. Two nested
    // frames, not one — the first lets React commit the new order, the second
    // lets the browser paint it, and only then is animating safe again. A
    // single frame re-enables the transition too early and the jump comes back.
    setSettling(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setSettling(false)));

    setOptimistic(ids);
    // NOT through onRun(). That helper opens with `if (busy) return`, so a
    // second action while one is in flight is dropped with no request, no error
    // and no sign on screen — survivable for a button you can press again,
    // wrong for a drag you have already completed. This owns its own failure:
    // the optimistic order is rolled back and the error surfaced.
    api.reorderOptions(field.id, [...ids, ...retired.map((o) => o.id)])
      .then(() => onChanged())
      .catch((e) => { setOptimistic(null); onError(e); });

  }

  // The third thing that uses the one drag implementation, and it reads the same
  // per-person sensitivity as the other two (C2-13). Its own useSettings() call
  // rather than a prop: the field editor is opened from four places and none of
  // them has any other reason to know about settings.
  const choiceSettings = useSettings();
  const choiceDrag = usePointerReorder({
    axis: 'y',
    sensitivity: choiceSettings
      ? settingValue<number>(choiceSettings, 'drag_sensitivity', 35) / 100
      : undefined,
    onDrop: reorderTo,
  });

  return (
    <div className="choices">
      <label>Choices</label>

      {/*
        THE WHOLE LIST ACCEPTS THE DROP, not each row.
        
        Matt's console report ended a three-round hunt: dragstart, three
        dragovers, then dragend and NO DROP EVENT. A drag that ends without a
        drop is one the browser cancelled, which happens when you release
        somewhere that never called preventDefault. The handlers were on each
        <li>, so the droppable area was the three rows and nothing else — his
        dragend was at y=607 with the rows near y=645, i.e. released just above
        the first row, over the "Choices" label. It worked exactly twice all
        day, on the two occasions he happened to let go over a row.
        
        Rows are 46px tall in a modal you are dragging inside. Requiring the
        release to land on one is a target most people will miss, silently.
      */}
      <ul className={`choicelist${settling ? ' settling' : ''}`}>
        {ordered.map((o, oi) => {
          const shift = choiceDrag.shiftFor(oi);
          return (
          <li
            key={o.id}
            ref={(el) => { liEls.current[oi] = el; }}
            className={
              `c-${choiceColor(o.color)}` +
              (choiceDrag.state?.from === oi ? ' dragging' : '') +
              (shift === -1 ? ' shift-up' : shift === 1 ? ' shift-down' : '')
            }
            /*
              THE WHOLE ROW IS THE HANDLE — Matt, 2026-09-03, after dragging
              status options on his phone: "it was very difficult to get the
              field to detect I was trying to drag it... what would be nice is
              for the drag on mobile to detect everywhere within the status
              option except for the area where we click to name the status
              option."

              So: press anywhere on the row and drag, EXCEPT on a control that
              owns the gesture itself — the name box (where a press-and-move is
              how you select text), the colour picker, and the retire button.
              That exception is read off the event's target rather than from a
              list of coordinates, so it stays true when the row gains a control.

              The ⠿ stays. It is not the mechanism any more; it is the sign that
              says the row can be picked up, which is what UI-4 was about.
            */
            onPointerDown={(e) => {
              if ((e.target as HTMLElement).closest('input, select, textarea, button, a, [contenteditable]')) return;
              choiceDrag.begin(e, oi, liEls.current.slice(0, ordered.length));
            }}
          >
            {/*
              UI-4 (Matt, 2026-08-28): "I don't know how to drag a choice above
              another."

              THE ROW USED TO BE THE DRAG SOURCE — and the row contains the name
              input. Under HTML5 drag-and-drop, starting a drag inside a text
              input began a TEXT drag the browser handled itself, so grabbing the
              obvious thing did nothing at all.

              THE HANDLE SURVIVED THE PORT TO POINTER EVENTS, and the reason is
              now a different one. Pointer events have no native text drag to
              lose the gesture to, so the row COULD be the source — but the row
              holds a text input and a select, and a press-and-move inside a text
              input is how you select text. A handle keeps both gestures, and it
              is the thing that looks like a handle.
            */}
            {/*
              NO onPointerDown OF ITS OWN ANY MORE. The row above is the source,
              and a handler here as well would call begin() twice for one press
              — once on the span and once on the row it bubbles to.
            */}
            <span
              className="grip"
              role="button"
              tabIndex={-1}
              aria-label={`Drag to reorder ${o.label}`}
              title="Drag to reorder"
            >
              ⠿
            </span>
            <select
              className="swatch"
              value={choiceColor(o.color)}
              aria-label={`Colour of ${o.label}`}
              onChange={(e) => void onRun(() => api.updateOption({ optionId: o.id, color: e.target.value }))}
            >
              {CHOICE_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {/*
              THE INPUT IS THE BADGE. A separate badge next to an editable name
              showed every choice's text twice — which looked, on screen, like a
              bug rather than like a preview. Colouring the field being edited
              says the same thing once, and is what Matt's Airtable screenshot
              actually does.
            */}
            <input
              className="badgeinput"
              defaultValue={o.label}
              aria-label={`Name of ${o.label}`}
              onBlur={(e) => {
                if (e.target.value.trim() && e.target.value !== o.label) {
                  void onRun(() => api.updateOption({ optionId: o.id, label: e.target.value.trim() }));
                }
              }}
            />
            {/*
              The stored value, shown small and never editable. It is what every
              row holds, and a rename that moved it would mean rewriting them —
              which is what "Reassign and retire" is for.
            */}
            <code className="muted" title="What every row stores. A rename never changes this.">{o.value}</code>
            <button
              className="mini" disabled={busy} title={`Retire ${o.label}`}
              onClick={() => setReassigning(o)}
            >×</button>
          </li>
        );})}
        {live.length === 0 && <li className="muted">No live choices. Nothing can be written to this field.</li>}
      </ul>

      <div className="choiceadd">
        <input
          value={adding}
          placeholder="Add a choice…"
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void addOption(); }}
        />
        <button className="action" disabled={!adding.trim() || busy} onClick={() => void addOption()}>Add</button>
        <button
          className="action" disabled={live.length < 2 || busy}
          title="Sort the choices by name"
          onClick={() => {
            const sorted = [...live].sort((a, b) => a.label.localeCompare(b.label)).map((o) => o.id);
            void onRun(() => api.reorderOptions(field.id, [...sorted, ...retired.map((o) => o.id)]));
          }}
        >Alphabetize</button>
      </div>

      {retired.length > 0 && (
        <>
          <label style={{ marginTop: 12 }}>Retired</label>
          <p className="muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
            Still on the rows that carry them, and refused to every row that does not.
            Nothing was deleted.
          </p>
          <ul className="choicelist retired">
            {retired.map((o) => (
              <li key={o.id}>
                <span className="badge c-grey">{o.label}</span>
                <code className="muted">{o.value}</code>
                <button
                  className="mini" disabled={busy} title={`Bring ${o.label} back`}
                  onClick={() => void onRun(() => api.restoreOption(o.id))}
                >↩</button>
              </li>
            ))}
          </ul>
        </>
      )}

      {reassigning && (
        <RetireChoice
          field={field}
          option={reassigning}
          live={live}
          onCancel={() => setReassigning(null)}
          onDone={async (mode, to) => {
            const opt = reassigning;
            setReassigning(null);
            if (mode === 'retire') await onRun(() => api.retireOption(opt.id));
            else await onRun(() => api.reassignOption(field.id, opt.value, to!));
          }}
        />
      )}
    </div>
  );
}

/** A colour for a new choice, walking the palette rather than always grey. */
function nextColor(n: number): string {
  return CHOICE_COLORS[(n % (CHOICE_COLORS.length - 1)) + 1]!;
}

/**
 * The two ways to retire a choice, and the difference stated plainly.
 *
 * Matt's ruling R2 gives both: retire and leave the rows alone (they keep the
 * value, greyed), or reassign every row to another choice first and then retire.
 * The second rewrites data and the first does not, which is the only thing a
 * person needs to know here — so it is the sentence next to each button rather
 * than a paragraph above them.
 */
function RetireChoice({
  field, option, live, onCancel, onDone,
}: {
  field: Field;
  option: FieldOption;
  live: FieldOption[];
  onCancel: () => void;
  onDone: (mode: 'retire' | 'reassign', to?: string) => void;
}) {
  const others = live.filter((o) => o.id !== option.id);
  const [to, setTo] = useState(others[0]?.value ?? '');

  return (
    <Modal title={`Retire "${option.label}"`} onCancel={onCancel}>
      <p style={{ marginTop: 0 }}>
        In <strong>{field.name}</strong>. Nothing is deleted either way — a retired
        choice stays on the rows that already carry it and cannot be given to a row
        that does not.
      </p>

      <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
        <button className="action" onClick={() => onDone('retire')}>
          Retire and leave the rows alone
        </button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Rows keep <span className="badge c-grey">{option.label}</span> and show it greyed.
      </p>

      {others.length > 0 && (
        <>
          <label htmlFor="rc-to" style={{ marginTop: 16 }}>Or move every row to</label>
          <select id="rc-to" value={to} onChange={(e) => setTo(e.target.value)}>
            {others.map((o) => <option key={o.id} value={o.value}>{o.label}</option>)}
          </select>
          <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
            <button className="action" disabled={!to} onClick={() => onDone('reassign', to)}>
              Reassign and retire
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            This CHANGES ROWS. Every row holding <code>{option.value}</code> is rewritten,
            each one writes its own history line, and rows you may not edit are not
            touched — the count comes back and is reported rather than hidden.
          </p>
        </>
      )}

      <div className="modal-actions">
        <button className="action" onClick={onCancel}>Cancel</button>
      </div>
    </Modal>
  );
}

/**
 * THE COLUMN HEADER MENU (C1c-3, on Matt's cut).
 *
 * What is here is what Amendment 3 ships: edit field (which opens the whole
 * editor bundle), insert left, insert right, duplicate, sort, hide, and delete
 * to the field Trash. What is NOT here is what Amendment 3 defers — group by,
 * filter by this field — and it is absent rather than disabled, for the reason
 * the C1 add-field dialog gives about reserved types: an option a person can see
 * and not use is a question they have to ask somebody.
 *
 * ON THE FIRST COLUMN, TWO OF THEM ARE ABSENT for exactly that reason. Matt's
 * ruling R1 (Decision recppF5dHeIXkhZq3, 2026-08-29) pins the primary field:
 * nothing goes to its left and it does not go in the Trash while the table has
 * other fields. The SERVER is what enforces that (migration 0021, and it answers
 * with a sentence rather than a code); this only stops offering a person two
 * things that would be refused. A menu item that always fails is a worse
 * explanation of a rule than no menu item plus the line below.
 *
 * COLUMN DRAG IS NOT HERE TO HIDE. C1c-6's drag reorder is deferred to C2-C
 * (spec item C2-10), so there is no way to drag a column at all yet; when it
 * arrives it reads `field.position === 1` the same way this does.
 */
/** The two columns every generated table has that are not fields. */
export const BUILT_IN_SORT_COLUMNS = [
  { slug: 'created_at', label: 'Created' },
  { slug: 'updated_at', label: 'Updated' },
] as const;

/** How far a floating menu must stay from the edge of the screen. */
const MENU_MARGIN = 8;

/**
 * WHERE A FLOATING MENU GOES SO THAT ALL OF IT IS ON THE SCREEN.
 *
 * Pure arithmetic, separated from the DOM on purpose: it is the part that can
 * be wrong in a way nobody notices until somebody opens the app on a phone,
 * which is exactly how the defect this fixes was found.
 *
 * The menu is RIGHT-ALIGNED to its anchor by preference, which is what the old
 * `right: 0` did and what looks correct for a ▾ at the right of a column
 * header. Everything after that is clamping, in a deliberate order: push right
 * if it overflows the left edge, push left if it overflows the right edge, and
 * if the menu is wider than the viewport, pin it to the left so the FIRST words
 * of each item are readable rather than the last.
 */
export function clampMenu(
  anchor: { top: number; bottom: number; right: number },
  menu: { w: number; h: number },
  view: { w: number; h: number },
): { top: number; left: number } {
  let left = anchor.right - menu.w;
  if (left + menu.w > view.w - MENU_MARGIN) left = view.w - MENU_MARGIN - menu.w;
  if (left < MENU_MARGIN) left = MENU_MARGIN;

  // Below the anchor by preference; above it if that would run off the bottom;
  // pinned to the top if it fits in neither, where its own scrollbar takes over.
  let top = anchor.bottom;
  if (top + menu.h > view.h - MENU_MARGIN) {
    const above = anchor.top - menu.h;
    top = above >= MENU_MARGIN ? above : MENU_MARGIN;
  }
  return { top, left };
}

/**
 * A MENU THAT ESCAPES THE GRID'S SCROLL BOX.
 *
 * WHAT WAS WRONG, found by Matt on an iPhone on 2026-08-31: opening the ▾ on
 * the Title column produced a menu with its left third sliced off — "field…",
 * "A → Z", "rt right". He read it as the sidebar covering the menu, and that is
 * the natural reading, but it is not what was happening and the difference
 * decides the fix.
 *
 * `.hmenu` was `position: absolute` inside `.grid-wrap`, which is
 * `overflow: auto`. AN ABSOLUTELY-POSITIONED DESCENDANT IS CLIPPED BY ITS
 * SCROLLING ANCESTOR. Nothing was on top of the menu — the menu was being cut
 * off by the box it lived in. So raising `z-index`, or giving the sidebar a
 * lower one, would have changed NOTHING while looking exactly like a fix: the
 * same class of mistake as the corner icons losing a specificity fight.
 *
 * The escape is a portal to `document.body` plus `position: fixed`, so the menu
 * is no longer inside the clipping box at all, and then clampMenu() to keep it
 * on the screen. This also fixes it on a desktop for the leftmost column, where
 * it was equally broken and simply less likely to be met.
 *
 * IT MOVES WITH ITS ANCHOR OR IT IS A LIE. A fixed-position menu whose column
 * scrolls away underneath it would sit still, pointing at nothing, so it
 * re-places on scroll (capturing, so inner scrollers count) and on resize.
 */
function MenuLayer({
  onClose, children,
}: {
  onClose: () => void;
  children: (item: (label: string, fn: () => void, title?: string) => ReactElement) => ReactNode;
}) {
  const anchor = useRef<HTMLSpanElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    function place() {
      const a = anchor.current?.getBoundingClientRect();
      const m = menu.current?.getBoundingClientRect();
      if (!a || !m) return;
      const next = clampMenu(a, { w: m.width, h: m.height },
        { w: window.innerWidth, h: window.innerHeight });
      // ONLY WHEN IT ACTUALLY MOVED. This runs on every scroll event, capturing,
      // so it fires constantly while the grid is being scrolled — and
      // `setPos({…})` builds a new object every time, which React cannot see as
      // equal, so an unconditional set is a re-render per scroll event for a
      // menu that has not moved a pixel. Compare the numbers, not the object.
      setPos((cur) => (cur && cur.top === next.top && cur.left === next.left ? cur : next));
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, []);

  useEffect(() => {
    function away() { onClose(); }
    // A click anywhere else closes it. Registered on the next tick so the click
    // that OPENED the menu does not immediately close it again.
    const t = setTimeout(() => window.addEventListener('mousedown', away), 0);
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const item = (label: string, fn: () => void, title?: string) => (
    <button key={label} onMouseDown={(e) => { e.stopPropagation(); }}
            onClick={() => { onClose(); fn(); }} title={title}>
      {label}
    </button>
  );

  return (<>
    {/* Zero-size, in the flow, purely so there is something to measure from. */}
    <span ref={anchor} className="menuanchor" aria-hidden="true" />
    {createPortal(
      <div
        ref={menu}
        className="hmenu"
        onMouseDown={(e) => e.stopPropagation()}
        // Hidden until it has been measured and placed: one frame at the wrong
        // position is a visible jump, and on a phone it looks like a bug.
        style={pos
          ? { top: pos.top, left: pos.left, visibility: 'visible' }
          : { top: 0, left: 0, visibility: 'hidden' }}
      >
        {children(item)}
      </div>,
      document.body,
    )}
  </>);
}

function HeaderMenu({
  field, canBuild, onClose, onEdit, onInsert, onDuplicate, onSort, onHide, onTrash,
}: {
  field: Field;
  canBuild: boolean;
  onClose: () => void;
  onEdit: () => void;
  onInsert: (side: 'left' | 'right') => void;
  onDuplicate: () => void;
  onSort: (dir: 'asc' | 'desc') => void;
  onHide: () => void;
  onTrash: () => void;
}) {
  // POSITION 1 IS THE PRIMARY FIELD (ruling R1). The same definition the server
  // uses — console.primary_field_id() is "the live field at position 1" — asked
  // of the field list this menu was opened on.
  const isPrimary = field.position === 1;

  // The dismissal handling, the item styling and — since 2026-08-31 — escaping
  // the grid's scroll box all live in MenuLayer, so the two header menus cannot
  // drift apart in any of them.
  return (
    <MenuLayer onClose={onClose}>
      {(item) => (<>
        {canBuild && item('Edit field…', onEdit, 'Name, description, format, default and choices')}
        {item('Sort A → Z', () => onSort('asc'))}
        {item('Sort Z → A', () => onSort('desc'))}
        {item('Hide field', onHide, 'In this view only. The data is untouched.')}
        {canBuild && <div className="sep" />}
        {canBuild && !isPrimary && item('Insert left', () => onInsert('left'))}
        {canBuild && item('Insert right', () => onInsert('right'))}
        {canBuild && item('Duplicate field', onDuplicate, 'Same type and settings, no data')}
        {canBuild && !isPrimary && <div className="sep" />}
        {canBuild && !isPrimary && item('Delete field', onTrash, 'Moves it to this table’s field Trash. The data stays and it can be restored.')}
        {canBuild && isPrimary && (
          <p className="hmenu-note">
            This is the primary field. It stays first and cannot be deleted — it is
            what this table’s rows are called wherever they are linked from another
            table. Rename it, or change what it holds.
          </p>
        )}
      </>)}
    </MenuLayer>
  );
}

/**
 * THE FIELD TRASH (Amendment 3 item 2, "DELETE FIELD as restorable field Trash").
 *
 * A trashed field is HIDDEN, not dropped: its column and every value in it are
 * exactly where they were, which is what makes restoring it a matter of clearing
 * a timestamp. That is also why it goes on holding its name — the panel says so,
 * because otherwise the next person to want the name meets a refusal with no
 * explanation attached to it.
 */
function FieldTrash({
  tableId, onClose, onRestored, onError,
}: {
  tableId: string;
  onClose: () => void;
  onRestored: () => void;
  onError: (e: unknown) => void;
}) {
  const [fields, setFields] = useState<TrashedField[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.trashedFields(tableId).then((r) => setFields(r.fields)).catch(onError);
  }, [tableId, onError]);

  return (
    <Modal title="Field Trash" onCancel={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        Nothing here was deleted. Each of these still has its column and every value
        in it — which is why each still holds its name, and why bringing one back is
        instant.
      </p>
      {fields === null && <p className="muted">Reading…</p>}
      {fields?.length === 0 && <p className="muted">Nothing in the field Trash.</p>}
      <ul className="choicelist">
        {(fields ?? []).map((f) => (
          <li key={f.id}>
            <span className="ticon">{typeIcon(f.type)}</span>
            <strong style={{ flex: 1 }}>{f.name}</strong>
            <code className="muted">{f.slug}</code>
            <span className="muted" style={{ fontSize: 12 }}>{fmtTime(f.archived_at)}</span>
            <button
              className="mini" disabled={busy} title={`Restore ${f.name}`}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.restore({ kind: 'field', id: f.id });
                  setFields((cur) => (cur ?? []).filter((x) => x.id !== f.id));
                  onRestored();
                } catch (e) { onError(e); } finally { setBusy(false); }
              }}
            >↩</button>
          </li>
        ))}
      </ul>
      <div className="modal-actions">
        <button className="action on" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

/**
 * THE LONG-TEXT EXPAND MODAL (C1c-5, Matt's ruling R1).
 *
 * Airtable's behaviour exactly: select the cell, an expand icon appears in its
 * top-right corner (or press Shift+Space), and the full text opens in a modal
 * that scrolls and is editable in place. Plain text. No AI helper — R1 says so
 * and it is worth keeping, because the AI field type is C3 and a helper here
 * would be a second, unregistered one.
 *
 * IT SAVES ON CLOSE, not on every keystroke: one audit line per edit rather than
 * one per character, and console.audit is written by a trigger nobody can tidy
 * up afterwards.
 */
/**
 * HOW MUCH OF A LONG TEXT VALUE IS SEARCHABLE — migration 0017, proved in C3.
 *
 * A copy of a server number, and the comment is the guard: it is only ever used
 * to decide whether to show a sentence, never to truncate anything. If the two
 * drift, a note appears slightly early or slightly late; nothing is stored
 * wrongly and nothing is lost. That is why this is a constant here rather than
 * a field on every record's response.
 */
const SEARCHABLE_LIMIT = 200_000;

/**
 * MATT'S WORDING, RULED IN AMENDMENT 1 A1-1, AND NOT TO BE CHANGED.
 *
 * He was given three candidates and picked this one. It is a CONSTANT rather
 * than text inside the JSX for one reason: written inline, the apostrophe wants
 * to become `&rsquo;` and the dash wants to become `&mdash;`, and both of those
 * are re-casings of a string a person chose. A guard in the test suite compares
 * this constant to the ruled text character for character.
 */
const SEARCH_LIMIT_NOTE =
  "This text is longer than search can index — words near the end won't be found by Search.";

function ExpandText({
  field, value, editable, onClose, onCommit,
}: {
  field: Field;
  value: unknown;
  editable: boolean;
  onClose: () => void;
  onCommit: (next: string) => void;
}) {
  const initial = fmt(value);
  const [draft, setDraft] = useState(initial);

  function close() {
    if (editable && draft !== initial) onCommit(draft);
    onClose();
  }

  return (
    <Modal title={field.name} onCancel={close} wide>
      {field.description && <p className="muted" style={{ marginTop: 0 }}>{field.description}</p>}
      <textarea
        autoFocus
        className="expand"
        rows={18}
        readOnly={!editable}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      {/*
        C2-15 (UI-3) — THE SEARCH LIMIT, SAID OUT LOUD.

        A long text column is searchable only to its first 200,000 characters:
        a tsvector has a hard size limit and the console truncates the INDEX
        rather than the DATA. That is correct behaviour and it was silent, which
        is the whole finding — C3 proved it deliberately, with a token at
        character 230,001 that could not be found while the value itself came
        back whole.

        THE WORDING IS MATT'S PICK, ruled in Amendment 1 A1-1, and it is NOT to
        be reworded, "improved" or re-cased by a later session. It appears here
        and nowhere else, and only when this cell is actually over the limit —
        a warning on every long-text cell would be noise that teaches people to
        stop reading warnings.

        The threshold is the server's, restated here as the only place the
        browser can know it. It is deliberately the SAME number and not a
        margin: a note that appeared early would be telling somebody their text
        is unsearchable while it is still perfectly searchable.
      */}
      {draft.length > SEARCHABLE_LIMIT && (
        <p className="muted bad" style={{ fontSize: 12, marginTop: 4 }}>{SEARCH_LIMIT_NOTE}</p>
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {editable
          ? 'Saved when you close this. Escape closes it too.'
          : 'Read-only — your session does not carry console.records:write.'}
      </p>
      <div className="modal-actions">
        <button className="action on" onClick={close}>Close</button>
      </div>
    </Modal>
  );
}

/**
 * THE BADGE CELL PICKER (C1c-2(b)).
 *
 * Replaces the native <select> C1 shipped, and the reason is Matt's screenshot:
 * a status column is read at a glance by colour, and a native select renders
 * nine identical grey rows. It is searchable because a real status field has
 * more choices than a dropdown is pleasant to scroll.
 *
 * A RETIRED CHOICE THAT IS ON THE ROW IS SHOWN, GREYED, AND IS NOT OFFERED. That
 * is Matt's ruling R2 option (b) rendered: the cell tells the truth about what
 * the row holds, and the list only contains what the row may be given. The
 * database enforces both halves independently (migration 0017) — this is the
 * half a person can see.
 */
function BadgePicker({
  field, value, editable, onCommit,
}: {
  field: Field;
  value: unknown;
  editable: boolean;
  onCommit: (raw: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const multi = field.type === 'multi_select';
  const held: string[] = multi
    ? (Array.isArray(value) ? (value as string[]) : fmt(value).split(',').map((s) => s.trim()).filter(Boolean))
    : (fmt(value) ? [fmt(value)] : []);

  const byValue = new Map(field.options.map((o) => [o.value, o]));
  const choosable = field.options.filter(
    (o) => !o.retired && o.label.toLowerCase().includes(q.trim().toLowerCase()),
  );

  useEffect(() => {
    if (!open) return;
    function away() { setOpen(false); }
    // Escape closes it, like the modals and the header menu. Without this the
    // list stayed open after Escape while everything else on screen closed,
    // which reads as the page having got stuck.
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }
    const t = setTimeout(() => window.addEventListener('mousedown', away), 0);
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function badgeFor(v: string) {
    const o = byValue.get(v);
    // A value with no option at all is a value the option list has forgotten.
    // Showing the raw value beats showing an empty cell.
    if (!o) return <span key={v} className="badge c-grey" title="No such choice on this field">{v}</span>;
    return (
      <span key={v} className={`badge c-${o.retired ? 'grey' : choiceColor(o.color)}${o.retired ? ' off' : ''}`}
            title={o.retired ? 'This choice has been retired. The row keeps it; it cannot be given to another row.' : undefined}>
        {o.label}{o.is_trigger ? ' ⚡' : ''}
      </span>
    );
  }

  function toggle(v: string) {
    if (!multi) { onCommit(v); setOpen(false); return; }
    const next = held.includes(v) ? held.filter((x) => x !== v) : [...held, v];
    onCommit(next.join(', '));
  }

  if (!editable) {
    return <div className="readonly badges">{held.map(badgeFor)}</div>;
  }

  return (
    <div className="picker" onMouseDown={(e) => e.stopPropagation()}>
      <button className="pickerbtn" onClick={() => setOpen((o) => !o)} title="Choose">
        <span className="badges">{held.length ? held.map(badgeFor) : <span className="muted">—</span>}</span>
        <span className="caret">▾</span>
      </button>
      {open && (
        <div className="pickerlist">
          <input
            autoFocus value={q} placeholder="Find a choice…"
            onChange={(e) => setQ(e.target.value)}
          />
          {!multi && (
            <button className="opt" onClick={() => { onCommit(''); setOpen(false); }}>
              <span className="muted">— clear —</span>
            </button>
          )}
          {choosable.map((o) => (
            <button key={o.id} className={`opt${held.includes(o.value) ? ' on' : ''}`} onClick={() => toggle(o.value)}>
              <span className={`badge c-${choiceColor(o.color)}`}>{o.label}{o.is_trigger ? ' ⚡' : ''}</span>
              {held.includes(o.value) && <span className="tick">✓</span>}
            </button>
          ))}
          {choosable.length === 0 && (
            <p className="muted" style={{ padding: '6px 8px', margin: 0, fontSize: 12 }}>
              {field.options.length === 0
                ? 'This field has no choices yet. Add some in the field editor.'
                : 'No live choice matches.'}
            </p>
          )}
          {held.some((v) => byValue.get(v)?.retired) && (
            <p className="muted" style={{ padding: '6px 8px', margin: 0, fontSize: 12 }}>
              This row holds a retired choice. It keeps it until you pick another one.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * How a value is rendered once its FORMAT is taken into account.
 *
 * Read-only cells only. An editable cell stays an input, because turning the
 * thing you are about to type into a link is a way to make a grid unusable.
 */
function formatted(field: Field, value: unknown): React.ReactNode {
  const t = fmt(value);
  if (!t) return '';
  // A cell somebody cannot edit used to render the raw stored value — an ISO
  // timestamp with a Z on the end — while the identical value one row up, in a
  // cell they COULD edit, showed a picker. Same column, two languages (C2-16).
  if (field.type === 'datetime') return formatInstant(t, displayPrefs);
  if (field.type === 'date') return formatDateOnly(t, displayPrefs);
  switch (field.format) {
    case 'email': return <a href={`mailto:${t}`}>{t}</a>;
    case 'url':   return <a href={t} target="_blank" rel="noreferrer noopener">{t}</a>;
    case 'phone': return <a href={`tel:${t.replace(/[^\d+]/g, '')}`}>{t}</a>;
    case 'currency': {
      const n = Number(t);
      return Number.isFinite(n)
        ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        : t;
    }
    case 'percent': {
      const n = Number(t);
      // 12 means 12 percent. Dividing by 100 here would silently reinterpret
      // every number already in the column.
      return Number.isFinite(n) ? `${n}%` : t;
    }
    default: return t;
  }
}
